import { toast } from "react-toastify";
import {
  OBJECT_TYPES,
  SANITARY_ELEMENT_TYPES,
  ELEMENT_LIMITS_PER_WALL_SEGMENT,
  ELECTRICAL_ELEMENT_TYPES,
  CLIMATE_ELEMENT_TYPES,
  SHOWER_ALLOWED_ELEMENT_TYPES,
  TOILET_ALLOWED_ELEMENT_TYPES,
  ELEMENT_GROUP_LIMITS,
  WALL_THICKNESS_M_RENDER,
  CORRIDOR_WALL_THICKNESS_M,
  GRID_CELL_SIZE_M,
  defaultObjectSizes,
  DEFAULT_MODULE_CELLS_WIDE,
  DEFAULT_MODULE_CELLS_LONG,
  MODES,
  FRAMELESS_GRID_CELL_SIZE_M,
  FRAME_MODE_WIDTH,
  FRAME_MODE_INITIAL_LENGTH,
  FRAME_MODE_SECTION_LENGTH,
  MAX_FRAMELESS_WIDTH_M,
  MAX_FRAMELESS_HEIGHT_M,
  GEOMETRY_TOLERANCES,
} from "../configuratorConstants";
import {
  generateId,
  checkOverlapWithRotation,
  findDockingPair,
  getLocalCoordinates,
  getWallSegmentKeyFromLocal,
  distance,
  addVectors,
  scaleVector,
  subtractVectors,
  normalizeVector,
  perpendicularVector,
  checkInvalidAdjacency,
  getModuleVertices,
  isPointInModule,
  validateProjectState,
  findOppositeSegmentOnJoint,
  lineSegmentsIntersect,
  areModulesTouchingAtCorner,
  findOverlappingSegment,
  rotatePoint,
  findNearestPointOnLineSegment,
  getLineIntersectionPoint,
} from "../configuratorUtils";
import {
  getAllSanitaryModuleCorners,
  canPlaceSanitaryElementInSandwich,
  updateCornersElements,
  isSanitaryJoint,
  isEdgeSegmentInSanitaryJoint
} from "../utils/sanitaryCornerUtils";
import {
  handleFloorAction,
  handleJointAction,
  handleElementAction,
  handleWallAction,
  handleModuleAction,
} from "./reducers";

const getJointEndpoints = (joint) => {
  const center = { x: joint.x, y: joint.y };
  const halfWidthVec = { x: joint.width / 2, y: 0 };
  const rotatedHalfWidth = rotatePoint(
    halfWidthVec,
    { x: 0, y: 0 },
    joint.rotation || 0,
  );
  const p1 = subtractVectors(center, rotatedHalfWidth);
  const p2 = addVectors(center, rotatedHalfWidth);
  return { p1, p2 };
};

const DOCKING_TOLERANCE = GEOMETRY_TOLERANCES.DOCKING_TOLERANCE;

// --- НАЧАЛО: НОВАЯ ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ---
/**
 * Создает новую безкаркасную стену с корректно рассчитанными сегментами.
 * @param {object} startPoint - Начальная точка.
 * @param {object} endPoint - Конечная точка.
 * @param {array} elements - Массив элементов для переноса.
 * @returns {object} Новый объект стены.
 */
const createFramelessWallWithSegments = (startPoint, endPoint, elements = []) => {
  const wallLength = distance(startPoint, endPoint);
  // Не создаем стену, если ее длина слишком мала
  if (wallLength < 0.1) return null;

  const numSegments = Math.max(1, Math.round(wallLength / FRAMELESS_GRID_CELL_SIZE_M));
  const segmentLength = wallLength / numSegments;

  const newWall = {
    id: generateId("fw_"),
    type: OBJECT_TYPES.FRAMELESS_WALL,
    startPoint,
    endPoint,
    segments: [],
  };

  // Перераспределяем элементы по новым сегментам
  const segmentsElements = Array.from({ length: numSegments }, () => []);
  elements.forEach(element => {
    const position = element.positionOnSegment ?? 0.5;
    const index = Math.min(numSegments - 1, Math.floor(position * numSegments));
    segmentsElements[index].push(element);
  });

  for (let i = 0; i < numSegments; i++) {
    newWall.segments.push({
      id: generateId("fws_"),
      start: i * segmentLength,
      length: segmentLength,
      elements: segmentsElements[i],
    });
  }

  // Пересчитываем positionOnSegment для элементов внутри новых сегментов
  newWall.segments.forEach(segment => {
    if (segment.elements.length > 0) {
      const smallElements = segment.elements.filter(el => el.type !== OBJECT_TYPES.DOOR && el.type !== OBJECT_TYPES.WINDOW && el.type !== OBJECT_TYPES.PANORAMIC_WINDOW);
      if (smallElements.length > 1) {
        smallElements.forEach((el, index) => {
          el.positionOnSegment = (index + 1) / (smallElements.length + 1);
        });
      } else if (smallElements.length === 1) {
        smallElements[0].positionOnSegment = 0.5;
      }
    }
  });


  return newWall;
};

/**
 * Разделяет существующую стену на две в точке примыкания.
 * @param {object} wallToSplit - Стена, которую нужно разделить.
 * @param {object} splitPoint - Точка разделения.
 * @returns {array} Массив из двух новых стен.
 */
const splitFramelessWall = (wallToSplit, splitPoint) => {
  const wallLength = distance(wallToSplit.startPoint, wallToSplit.endPoint);
  if (wallLength < 0.1) return [wallToSplit];

  const splitRatio = distance(wallToSplit.startPoint, splitPoint) / wallLength;

  const allElements = wallToSplit.segments.flatMap(s => s.elements);
  const elementsForSegment1 = [];
  const elementsForSegment2 = [];

  allElements.forEach(el => {
    if (el.positionOnSegment < splitRatio) {
      // Пересчитываем позицию для первого сегмента
      el.positionOnSegment = el.positionOnSegment / splitRatio;
      elementsForSegment1.push(el);
    } else {
      // Пересчитываем позицию для второго сегмента
      el.positionOnSegment = (el.positionOnSegment - splitRatio) / (1 - splitRatio);
      elementsForSegment2.push(el);
    }
  });

  const wall1 = createFramelessWallWithSegments(wallToSplit.startPoint, splitPoint, elementsForSegment1);
  const wall2 = createFramelessWallWithSegments(splitPoint, wallToSplit.endPoint, elementsForSegment2);

  return [wall1, wall2].filter(Boolean); // Возвращаем только валидные стены
};
// --- КОНЕЦ: НОВАЯ ВСПОМОГАТЕЛЬНАЯ ФУНКЦИЯ ---


const findConnectedWalls = (startWall, allWalls) => {
  const component = new Set();
  const stack = [startWall];
  const visited = new Set();
  const epsilon = 0.001; // Уменьшено до 1мм для более точного определения связей


  while (stack.length > 0) {
    const currentWall = stack.pop();

    if (visited.has(currentWall.id)) {
      continue;
    }
    visited.add(currentWall.id);
    component.add(currentWall);

    let connectionsFound = 0;
    allWalls.forEach((otherWall) => {
      if (currentWall.id === otherWall.id || visited.has(otherWall.id)) {
        return;
      }

      const pointsA = [currentWall.startPoint, currentWall.endPoint];
      const pointsB = [otherWall.startPoint, otherWall.endPoint];

      // Проверяем, есть ли общая вершина (точное совпадение в пределах epsilon)
      let hasConnection = false;
      let minDist = Infinity;
      for (const pA of pointsA) {
        for (const pB of pointsB) {
          const dist = distance(pA, pB);
          minDist = Math.min(minDist, dist);
          if (dist < epsilon) {
            hasConnection = true;
            break;
          }
        }
        if (hasConnection) break;
      }

      if (hasConnection) {
        connectionsFound++;
        stack.push(otherWall);
      }
    });
  }

  const walls = Array.from(component);

  return walls;
};

// Находит все замкнутые прямоугольные циклы в наборе стен
const findAllClosedLoops = (walls) => {
  const loops = [];
  const epsilon = 0.001;


  // Вспомогательная функция для получения конечной точки в зависимости от направления
  const getNextPoint = (wall, fromPoint) => {
    const distToStart = distance(fromPoint, wall.startPoint);
    const distToEnd = distance(fromPoint, wall.endPoint);

    if (distToStart < epsilon) {
      return wall.endPoint; // Идём от start к end
    } else if (distToEnd < epsilon) {
      return wall.startPoint; // Идём от end к start
    }
    return null;
  };

  // Пробуем найти все возможные замкнутые ортогональные циклы (прямоугольники, L-образные и т.д.)
  for (let i = 0; i < walls.length; i++) {
    const startWall = walls[i];

    // Пробуем оба направления для начальной стены
    for (const startPoint of [startWall.startPoint, startWall.endPoint]) {
      const visited = new Set([startWall.id]);
      const path = [startWall];
      const currentPoint = getNextPoint(startWall, startPoint);
      if (!currentPoint) continue;

      // Сохраняем начальную точку для проверки замкнутости
      // Это должна быть точка, от которой мы начинаем путь (не та, к которой идем)
      const loopStartPoint = startPoint;

      const dfs = (currentWall, currentEnd) => {
        // Проверяем, можем ли замкнуть цикл
        // Проверяем не только когда path.length >= 4, но и в любой момент, если вернулись к началу
        if (path.length >= 3) {
          // Проверяем, совпадает ли конечная точка с начальной
          // Также проверяем обе точки первой стены, так как путь может вернуться к любой из них
          const distToStartPoint = distance(currentEnd, loopStartPoint);
          const distToStartWallStart = distance(currentEnd, startWall.startPoint);
          const distToStartWallEnd = distance(currentEnd, startWall.endPoint);
          const minDist = Math.min(distToStartPoint, distToStartWallStart, distToStartWallEnd);

          // Для замкнутого контура нужно минимум 3 стены (треугольник) или 4+ (прямоугольник и т.д.)
          // Но также проверяем, что мы прошли достаточно далеко от начальной точки
          const hasMinimumWalls = path.length >= 3;
          const isClosed = minDist < epsilon;

          if (hasMinimumWalls && isClosed) {
            // Нашли замкнутый цикл!
            const loop = [...path];
            // Проверка прямоугольности отключена в безкаркасном режиме - принимаем все замкнутые циклы
            // Проверяем, не добавили ли мы уже этот цикл
            const loopKey = loop.map(w => w.id).sort().join('|');
            const isDuplicate = loops.some(existingLoop => {
              const existingKey = existingLoop.map(w => w.id).sort().join('|');
              return existingKey === loopKey;
            });
            if (!isDuplicate) {
              loops.push(loop);
            }
            return;
          }
        }

        if (path.length >= 10) return; // Ограничение глубины поиска

        // Ищем следующую стену, которая соединяется с текущей точкой
        let foundNext = false;
        for (const nextWall of walls) {
          if (visited.has(nextWall.id)) continue;

          const nextPoint = getNextPoint(nextWall, currentEnd);
          if (nextPoint) {
            foundNext = true;
            visited.add(nextWall.id);
            path.push(nextWall);
            dfs(nextWall, nextPoint);
            path.pop();
            visited.delete(nextWall.id);
          }
        }
      };

      dfs(startWall, currentPoint);
    }
  }


  // Вычисляем границы и площадь каждого цикла
  const loopsWithBounds = loops.map(loop => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    loop.forEach(wall => {
      minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
      minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
      maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
      maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
    });
    const area = (maxX - minX) * (maxY - minY);
    return { loop, minX, minY, maxX, maxY, area };
  });

  // Фильтруем: оставляем только минимальные (не содержащие других циклов)
  // Но сохраняем смежные комнаты, которые используют общие стены
  const minimalLoops = loopsWithBounds.filter((loopData, index) => {
    const { loop, minX, minY, maxX, maxY, area } = loopData;

    // Проверяем, не содержит ли этот цикл другие циклы
    for (let j = 0; j < loopsWithBounds.length; j++) {
      if (j === index) continue;

      const other = loopsWithBounds[j];
      const otherLoop = other.loop;

      // Проверяем, используют ли циклы общие стены
      const loopWallIds = new Set(loop.map(w => w.id));
      const otherLoopWallIds = new Set(otherLoop.map(w => w.id));
      const sharedWalls = [...loopWallIds].filter(id => otherLoopWallIds.has(id));

      // Если циклы используют общие стены - это смежные комнаты, оба должны быть сохранены
      if (sharedWalls.length > 0) {
        continue; // Пропускаем проверку для смежных комнат
      }

      // Если другой цикл имеет меньшую площадь и находится полностью внутри текущего
      // И они не используют общие стены (т.е. одна комната действительно внутри другой)
      if (other.area < area) {
        const isInside =
          other.minX >= minX - 0.01 &&
          other.maxX <= maxX + 0.01 &&
          other.minY >= minY - 0.01 &&
          other.maxY <= maxY + 0.01;

        if (isInside) {
          return false;
        }
      }
    }

    return true;
  });

  const result = minimalLoops.map(data => data.loop);
  return result;
};

// Находит все компоненты связанных стен на этаже
const findAllWallComponents = (allWalls) => {
  const visited = new Set();
  const components = [];

  allWalls.forEach((wall) => {
    if (visited.has(wall.id)) {
      return;
    }

    const component = findConnectedWalls(wall, allWalls);
    component.forEach((w) => visited.add(w.id));
    components.push(component);
  });

  return components;
};

const pointsAreEqual = (a, b, epsilon = GEOMETRY_TOLERANCES.POINT_SNAP_EPSILON) =>
  Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon;

const ensureFramelessPartitionSegments = (partition) => {
  if (!partition || partition.type !== OBJECT_TYPES.FRAMELESS_PARTITION) {
    return partition;
  }

  const elementsArray = Array.isArray(partition.elements)
    ? partition.elements
    : [];
  const rawLength = distance(partition.startPoint, partition.endPoint);
  const hasLength = rawLength > 0;
  const segmentCount = hasLength
    ? Math.max(1, Math.round(rawLength / FRAMELESS_GRID_CELL_SIZE_M))
    : 1;
  const normalizedLength = hasLength
    ? rawLength / segmentCount
    : FRAMELESS_GRID_CELL_SIZE_M;

  const segmentsElements = Array.from({ length: segmentCount }, () => []);
  elementsArray.forEach((element) => {
    // Если у элемента есть segmentIndex, используем его
    if (element.segmentIndex !== undefined && element.segmentIndex < segmentCount) {
      segmentsElements[element.segmentIndex].push(element);
    } else {
      // Иначе вычисляем по позиции
      const position = element.globalPositionOnWall ?? element.positionOnSegment ?? 0.5;
      const clampedPosition = Math.max(0, Math.min(1, position));
      const index = Math.min(
        segmentCount - 1,
        Math.max(0, Math.floor(clampedPosition * segmentCount)),
      );
      segmentsElements[index].push(element);
    }
  });

  partition.elements = elementsArray;
  partition.segments = segmentsElements.map((segmentElements, idx) => ({
    id: `${partition.id}_segment_${idx}`,
    start: idx * normalizedLength,
    length: normalizedLength,
    elements: segmentElements,
  }));

  return partition;
};

const ensureFramelessPartitions = (objects, component, componentId) => {

  if (!component || component.length === 0) {
    return { objects, partitionsAdded: false };
  }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  component.forEach((wall) => {
    minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
    minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
    maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
    maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
  });

  const width = maxX - minX;
  const height = maxY - minY;
  let updatedObjects = [...objects];
  let partitionsAdded = false;
  const epsilon = 0.01;

  const needVerticalPartitions =
    width - MAX_FRAMELESS_WIDTH_M > epsilon
      ? Math.ceil(width / MAX_FRAMELESS_WIDTH_M) - 1
      : 0;
  const needHorizontalPartitions =
    height - MAX_FRAMELESS_HEIGHT_M > epsilon
      ? Math.ceil(height / MAX_FRAMELESS_HEIGHT_M) - 1
      : 0;


  // Collect all partition lines (before splitting at intersections)
  const partitionLines = [];

  // Add vertical partition lines
  for (let i = 1; i <= needVerticalPartitions; i++) {
    const partitionX = minX + i * MAX_FRAMELESS_WIDTH_M;
    partitionLines.push({
      startPoint: { x: partitionX, y: minY },
      endPoint: { x: partitionX, y: maxY },
      rotation: 90,
      type: 'vertical',
      index: i,
    });
  }

  // Add horizontal partition lines
  for (let i = 1; i <= needHorizontalPartitions; i++) {
    const partitionY = minY + i * MAX_FRAMELESS_HEIGHT_M;
    partitionLines.push({
      startPoint: { x: minX, y: partitionY },
      endPoint: { x: maxX, y: partitionY },
      rotation: 0,
      type: 'horizontal',
      index: i,
    });
  }

  // Find all intersection points for each partition line
  const partitionSegments = partitionLines.map((line) => {
    const intersectionPoints = [];

    // Find intersections with all other partition lines
    partitionLines.forEach((otherLine) => {
      if (line === otherLine) return;

      const intersectionPoint = getLineIntersectionPoint(
        line.startPoint,
        line.endPoint,
        otherLine.startPoint,
        otherLine.endPoint
      );

      if (intersectionPoint) {
        intersectionPoints.push(intersectionPoint);
      }
    });

    // Sort intersection points along the line
    intersectionPoints.sort((a, b) => {
      if (line.type === 'vertical') {
        return a.y - b.y;
      } else {
        return a.x - b.x;
      }
    });

    // Create segments between intersection points
    const segments = [];
    let currentStart = line.startPoint;

    intersectionPoints.forEach((intersection) => {
      // Add segment from current start to intersection
      segments.push({
        startPoint: { ...currentStart },
        endPoint: { ...intersection },
        rotation: line.rotation,
      });
      currentStart = { ...intersection };
    });

    // Add final segment from last intersection (or start if no intersections) to end
    segments.push({
      startPoint: { ...currentStart },
      endPoint: { ...line.endPoint },
      rotation: line.rotation,
    });

    return segments;
  }).flat();

  // Create partition objects for each segment

  let createdCount = 0;
  let skippedDuplicates = 0;

  partitionSegments.forEach((segment, idx) => {
    const effectiveWidth = Math.max(distance(segment.startPoint, segment.endPoint), 0);

    // Skip very short segments
    if (effectiveWidth < 0.1) {
      return;
    }

    const center = {
      x: (segment.startPoint.x + segment.endPoint.x) / 2,
      y: (segment.startPoint.y + segment.endPoint.y) / 2,
    };

    // Проверяем, нет ли уже перегородки в этом месте (от другой комнаты)
    const epsilon = 0.05; // Увеличиваем до 5см для более надежного определения
    const existingPartition = updatedObjects.find(obj => {
      if (obj.type !== OBJECT_TYPES.FRAMELESS_PARTITION) return false;
      if (obj.componentId === componentId) return false; // Пропускаем перегородки текущего компонента (они уже удалены)

      // Проверяем совпадение start/end точек
      const startMatch = (
        distance(obj.startPoint, segment.startPoint) < epsilon &&
        distance(obj.endPoint, segment.endPoint) < epsilon
      ) || (
          // Или в обратном направлении
          distance(obj.startPoint, segment.endPoint) < epsilon &&
          distance(obj.endPoint, segment.startPoint) < epsilon
        );

      if (startMatch) {
        return true;
      }

      // Дополнительно проверяем совпадение центра, ротации и ширины
      const centerDist = distance(
        { x: obj.x, y: obj.y },
        center
      );
      const rotationMatch = Math.abs(obj.rotation - segment.rotation) < 1;
      const widthMatch = Math.abs(obj.width - effectiveWidth) < epsilon;

      if (centerDist < epsilon && rotationMatch && widthMatch) {
        return true;
      }

      return false;
    });

    if (existingPartition) {
      skippedDuplicates++;
      return;
    }

    const partition = {
      id: generateId("fp_"),
      type: OBJECT_TYPES.FRAMELESS_PARTITION,
      componentId: componentId,
      startPoint: segment.startPoint,
      endPoint: segment.endPoint,
      x: center.x,
      y: center.y,
      rotation: segment.rotation,
      width: effectiveWidth,
      displayMode: "single",
      elements: [],
      activeWallSegments: [],
      passageSegments: [],
    };

    // Автоматическое создание дверей отключено в безкаркасном режиме
    ensureFramelessPartitionSegments(partition);
    updatedObjects.push(partition);
    partitionsAdded = true;
    createdCount++;
  });

  // Skipped duplicates are handled during iteration
  return { objects: updatedObjects, partitionsAdded };
};

const floorHasStairs = (floorObjects) => {
  if (!floorObjects || floorObjects.length === 0) return false;
  return floorObjects.some(
    (obj) =>
      obj.type === OBJECT_TYPES.MODULE &&
      obj.subtype === "stairs" &&
      obj.templateId &&
      obj.templateId.includes("floor1"),
  );
};

const createFramePerimeter = (width, length) => {
  const walls = {};
  for (let i = 0; i < width; i++) {
    walls[`${i},0_h`] = { id: generateId("wall_"), elements: [] };
    walls[`${i},${length}_h`] = { id: generateId("wall_"), elements: [] };
  }
  for (let i = 0; i < length; i++) {
    walls[`0,${i}_v`] = { id: generateId("wall_"), elements: [] };
    walls[`${width},${i}_v`] = { id: generateId("wall_"), elements: [] };
  }
  return walls;
};

export const getObjectById = (objectId, floorId, projectObjects) => {
  if (!objectId || !floorId || !projectObjects) return null;
  const floorObjects = projectObjects[floorId] || [];

  // Проверяем, является ли это сегментом стены в стыке
  if (objectId.includes('_wall_segment_')) {
    const [jointId, , segmentIndexStr] = objectId.split('_');
    const segmentIndex = parseInt(segmentIndexStr, 10);

    const joint = floorObjects.find(obj => obj.id === jointId && obj.type === OBJECT_TYPES.DOCKING_JOINT);
    if (joint && joint.activeWallSegments && joint.activeWallSegments.includes(segmentIndex)) {
      return {
        id: objectId,
        type: OBJECT_TYPES.JOINT_WALL_SEGMENT,
        jointId: jointId,
        segmentIndex: segmentIndex,
        parentJointId: jointId,
        label: "Сегмент стены в стыке",
        joint: joint
      };
    }
  }

  const topLevelObject = floorObjects.find((obj) => obj.id === objectId);
  if (topLevelObject) {
    if (
      topLevelObject.type === OBJECT_TYPES.FRAME_STRUCTURE ||
      topLevelObject.type === OBJECT_TYPES.FRAMELESS_BUILDING
    ) {
      return { ...topLevelObject };
    }
    if (
      topLevelObject.type === OBJECT_TYPES.WALL_SEGMENT &&
      topLevelObject.parentModuleId
    ) {
      const parentModule = floorObjects.find(
        (m) =>
          m.id === topLevelObject.parentModuleId &&
          m.type === OBJECT_TYPES.MODULE,
      );
      if (parentModule) {
        let segmentKeyInParent;
        if (parentModule.wallSegments) {
          for (const key in parentModule.wallSegments) {
            if (parentModule.wallSegments[key].id === objectId) {
              segmentKeyInParent = key;
              break;
            }
          }
        }
        if (segmentKeyInParent) {
          const [coords, orientation] = segmentKeyInParent.split("_");
          const [cxStr, cyStr] = coords.split(",");
          const cellX = parseInt(cxStr, 10);
          const cellY = parseInt(cyStr, 10);
          const isExternal =
            (orientation === "h" &&
              (cellY === 0 || cellY === parentModule.cellsLong)) ||
            (orientation === "v" &&
              (cellX === 0 || cellX === parentModule.cellsWide));
          return {
            ...topLevelObject,
            cellX,
            cellY,
            orientation,
            parentModuleCellsWide: parentModule.cellsWide,
            parentModuleCellsLong: parentModule.cellsLong,
            segmentKeyInParentModule: segmentKeyInParent,
            isExternal,
          };
        }
      }
    }
    return topLevelObject;
  }

  for (const module of floorObjects.filter(
    (o) => o.type === OBJECT_TYPES.MODULE,
  )) {
    const freeElement = module.freeElements?.find((el) => el.id === objectId);
    if (freeElement) {
      return {
        ...freeElement,
        parentModuleId: module.id,
        parentFloorId: floorId,
      };
    }

    if (module.wallSegments) {
      for (const key in module.wallSegments) {
        const segment = module.wallSegments[key];
        if (segment.id === objectId) {
          const [coords, orientation] = key.split("_");
          const [cxStr, cyStr] = coords.split(",");
          const cellX = parseInt(cxStr, 10);
          const cellY = parseInt(cyStr, 10);
          const isExternal =
            (orientation === "h" &&
              (cellY === 0 || cellY === module.cellsLong)) ||
            (orientation === "v" &&
              (cellX === 0 || cellX === module.cellsWide));
          return {
            ...segment,
            parentModuleId: module.id,
            parentFloorId: floorId,
            cellX,
            cellY,
            orientation,
            parentModuleCellsWide: module.cellsWide,
            parentModuleCellsLong: module.cellsLong,
            segmentKeyInParentModule: key,
            isExternal,
            jointId: segment.jointId,
          };
        }
        const wallElement = segment.elements?.find((el) => el.id === objectId);
        if (wallElement) {
          const [coords, orientation] = key.split("_");
          const [cxStr, cyStr] = coords.split(",");
          const cellX = parseInt(cxStr, 10);
          const cellY = parseInt(cyStr, 10);
          const isExternal =
            (orientation === "h" &&
              (cellY === 0 || cellY === module.cellsLong)) ||
            (orientation === "v" &&
              (cellX === 0 || cellX === module.cellsWide));
          return {
            ...wallElement,
            parentWallSegment: {
              ...segment,
              cellX,
              cellY,
              orientation,
              parentModuleCellsWide: module.cellsWide,
              parentModuleCellsLong: module.cellsLong,
              segmentKeyInParentModule: key,
              isExternal,
            },
            parentModuleId: module.id,
            parentFloorId: floorId,
          };
        }
      }
    }
  }

  for (const joint of floorObjects.filter(
    (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
  )) {
    const jointElement = joint.elements?.find((el) => el.id === objectId);
    if (jointElement) {
      return {
        ...jointElement,
        parentJointId: joint.id,
        parentFloorId: floorId,
      };
    }
  }

  for (const partition of floorObjects.filter(
    (o) => o.type === OBJECT_TYPES.FRAMELESS_PARTITION,
  )) {
    ensureFramelessPartitionSegments(partition);
    if (partition.id === objectId) {
      return { ...partition };
    }
    const partitionSegment = partition.segments?.find(
      (seg) => seg.id === objectId,
    );
    if (partitionSegment) {
      return {
        ...partitionSegment,
        type: "frameless_wall_segment",
        parentPartitionId: partition.id,
        parentWallId: partition.id,
        parentFloorId: floorId,
      };
    }
    const partitionElement = partition.segments
      ?.flatMap((seg) => seg.elements || [])
      .find((el) => el.id === objectId);
    if (partitionElement) {
      const parentSegment = partition.segments?.find((seg) =>
        (seg.elements || []).some((el) => el.id === objectId),
      );
      return {
        ...partitionElement,
        parentPartitionId: partition.id,
        parentWallId: partition.id,
        parentWallSegment: parentSegment,
        parentFloorId: floorId,
      };
    }
  }

  // Обрабатываем безкаркасные стены (как в зданиях, так и напрямую созданные)
  const building = floorObjects.find(
    (o) => o.type === OBJECT_TYPES.FRAMELESS_BUILDING,
  );
  if (building && building.id === objectId) return building;

  // Собираем все безкаркасные стены (из зданий и напрямую созданные)
  const allFramelessWalls = [];
  if (building) {
    allFramelessWalls.push(
      ...(building.perimeterWalls || []),
      ...(building.internalWalls || []),
    );
  }
  allFramelessWalls.push(
    ...floorObjects.filter((obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL)
  );

  // Обрабатываем все безкаркасные стены
  for (const wall of allFramelessWalls) {
    if (wall.id === objectId) return wall;
    const segment = wall.segments?.find((s) => s.id === objectId);
    if (segment) {
      return {
        ...segment,
        type: "frameless_wall_segment",
        parentWallId: wall.id,
        parentFloorId: floorId,
      };
    }
    const wallElement = wall.segments
      ?.flatMap((s) => s.elements)
      .find((el) => el.id === objectId);
    if (wallElement) {
      const parentSegment = wall.segments.find((s) =>
        s.elements.some((el) => el.id === objectId),
      );
      return {
        ...wallElement,
        parentWallSegment: parentSegment,
        parentWallId: wall.id,
        parentFloorId: floorId,
      };
    }
  }

  for (const frame of floorObjects.filter(
    (o) => o.type === OBJECT_TYPES.FRAME_STRUCTURE,
  )) {
    const allWalls = { ...frame.perimeterWalls, ...frame.internalPartitions };
    for (const key in allWalls) {
      const segment = allWalls[key];
      if (segment.id === objectId) {
        const [coords, orientation] = key.split("_");
        const [cxStr, cyStr] = coords.split(",");
        return {
          ...segment,
          type: OBJECT_TYPES.WALL_SEGMENT,
          parentFrameId: frame.id,
          parentFloorId: floorId,
          cellX: parseInt(cxStr, 10),
          cellY: parseInt(cyStr, 10),
          orientation,
          parentModuleCellsWide: frame.width,
          parentModuleCellsLong: frame.length,
        };
      }
      const wallElement = segment.elements?.find((el) => el.id === objectId);
      if (wallElement) {
        return {
          ...wallElement,
          parentWallSegment: segment,
          parentFrameId: frame.id,
          parentFloorId: floorId,
        };
      }
    }
  }

  return null;
};

const createInitialWallSegments = (moduleConfig) => {
  const segments = {};
  const cellsWide =
    parseInt(moduleConfig.cellsWide) || DEFAULT_MODULE_CELLS_WIDE;
  const cellsLong =
    parseInt(moduleConfig.cellsLong) || DEFAULT_MODULE_CELLS_LONG;
  const internalWallSegmentsConfig = moduleConfig.internalWallSegments || {};

  const isInternalSegment = (key) => {
    const [coords, orientation] = key.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr, 10);
    const cellY = parseInt(cyStr, 10);
    if (orientation === "h") return cellY > 0 && cellY < cellsLong;
    if (orientation === "v") return cellX > 0 && cellX < cellsWide;
    return false;
  };

  const createSegment = (key, props = {}) => ({
    id: generateId("wall_"),
    type: OBJECT_TYPES.WALL_SEGMENT,
    elements: [],
    thickness: props.thickness
      ? props.thickness
      : isInternalSegment(key)
        ? CORRIDOR_WALL_THICKNESS_M
        : WALL_THICKNESS_M_RENDER,
    ...props,
  });

  for (let i = 0; i < cellsWide; i++) {
    segments[`${i},0_h`] = createSegment(`${i},0_h`);
    segments[`${i},${cellsLong}_h`] = createSegment(`${i},${cellsLong}_h`);
  }
  for (let j = 0; j < cellsLong; j++) {
    segments[`0,${j}_v`] = createSegment(`0,${j}_v`);
    segments[`${cellsWide},${j}_v`] = createSegment(`${cellsWide},${j}_v`);
  }
  for (const [key, data] of Object.entries(internalWallSegmentsConfig)) {
    segments[key] = createSegment(key, data);
  }
  return segments;
};

/**
 * Пересчитывает сегменты стен модуля при повороте на 90 градусов
 * и переносит элементы из старых сегментов в новые
 * @param {object} module - Модуль с текущими сегментами
 * @param {number} oldRotation - Старый угол поворота
 * @param {number} newRotation - Новый угол поворота
 * @returns {object} Модуль с обновленными сегментами
 */
const recalculateWallSegmentsAfterRotation = (module, oldRotation, newRotation) => {
  const oldCellsWide = parseInt(module.cellsWide) || DEFAULT_MODULE_CELLS_WIDE;
  const oldCellsLong = parseInt(module.cellsLong) || DEFAULT_MODULE_CELLS_LONG;

  // Определяем ориентацию ТОЛЬКО по rotation (не по размерам!)
  // rotation = 0° или 180° = вертикальный
  // rotation = 90° или 270° = горизонтальный
  const oldIsHorizontal = Math.abs(oldRotation % 180) === 90;
  const newIsHorizontal = Math.abs(newRotation % 180) === 90;

  // Для нового модуля используем размеры после поворота (они уже поменяются местами)
  const newCellsWide = oldCellsLong; // После поворота размеры меняются местами
  const newCellsLong = oldCellsWide;

  // Если ориентация не изменилась, возвращаем модуль без изменений
  if (oldIsHorizontal === newIsHorizontal) {
    return module;
  }

  // Собираем внутренние сегменты из старого модуля для переноса
  const oldInternalSegments = {};
  const oldWallSegments = module.wallSegments || {};

  // Функция для проверки, является ли сегмент внутренним
  const isInternalSegment = (key, cellsWide, cellsLong) => {
    const [coords, orientation] = key.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr, 10);
    const cellY = parseInt(cyStr, 10);
    if (orientation === "h") return cellY > 0 && cellY < cellsLong;
    if (orientation === "v") return cellX > 0 && cellX < cellsWide;
    return false;
  };

  // Собираем все внутренние сегменты из старого модуля
  for (const [key, segment] of Object.entries(oldWallSegments)) {
    if (isInternalSegment(key, oldCellsWide, oldCellsLong)) {
      oldInternalSegments[key] = segment;
    }
  }

  // Создаем новые сегменты
  const newModuleConfig = {
    ...module,
    cellsWide: newCellsWide,
    cellsLong: newCellsLong,
    internalWallSegments: module.internalWallSegments || {},
  };
  const newSegments = createInitialWallSegments(newModuleConfig);

  // Маппинг сегментов при повороте на 90°
  // Направление поворота: если oldIsHorizontal=true (90°), то поворот на 0° (вертикальный)
  // Если oldIsHorizontal=false (0°), то поворот на 90° (горизонтальный)

  // При повороте с 0° на 90° (по часовой):
  // - Верхняя стена (i,0_h) -> Правая стена (newCellsWide,i_v)
  // - Нижняя стена (i,oldCellsLong_h) -> Левая стена (0,i_v)
  // - Левая стена (0,j_v) -> Верхняя стена (j,0_h)
  // - Правая стена (oldCellsWide,j_v) -> Нижняя стена (j,newCellsLong_h)

  // При повороте с 90° на 0° (против часовой):
  // - Верхняя стена (i,0_h) -> Левая стена (0,i_v)
  // - Нижняя стена (i,oldCellsLong_h) -> Правая стена (newCellsWide,i_v)
  // - Левая стена (0,j_v) -> Нижняя стена (j,newCellsLong_h)
  // - Правая стена (oldCellsWide,j_v) -> Верхняя стена (j,0_h)

  // Функция для маппинга ключа сегмента
  const mapSegmentKey = (oldKey) => {
    const [coords, orientation] = oldKey.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr, 10);
    const cellY = parseInt(cyStr, 10);

    if (orientation === "h") {
      // Горизонтальные сегменты (верх/низ)
      if (cellY === 0) {
        // Верхняя стена
        if (oldIsHorizontal) {
          // Поворот с 90° (горизонтальный) на 0° (вертикальный): верхняя -> левая
          return `0,${cellX}_v`;
        } else {
          // Поворот с 0° (вертикальный) на 90° (горизонтальный): верхняя -> правая
          return `${newCellsWide},${cellX}_v`;
        }
      } else if (cellY === oldCellsLong) {
        // Нижняя стена
        if (oldIsHorizontal) {
          // Поворот с 90° (горизонтальный) на 0° (вертикальный): нижняя -> правая
          return `${newCellsWide},${cellX}_v`;
        } else {
          // Поворот с 0° (вертикальный) на 90° (горизонтальный): нижняя -> левая
          return `0,${cellX}_v`;
        }
      }
    } else if (orientation === "v") {
      // Вертикальные сегменты (лево/право)
      if (cellX === 0) {
        // Левая стена
        if (oldIsHorizontal) {
          // Поворот с 90° (горизонтальный) на 0° (вертикальный): левая -> нижняя
          return `${cellY},${newCellsLong}_h`;
        } else {
          // Поворот с 0° (вертикальный) на 90° (горизонтальный): левая -> верхняя
          return `${cellY},0_h`;
        }
      } else if (cellX === oldCellsWide) {
        // Правая стена
        if (oldIsHorizontal) {
          // Поворот с 90° (горизонтальный) на 0° (вертикальный): правая -> верхняя
          return `${cellY},0_h`;
        } else {
          // Поворот с 0° (вертикальный) на 90° (горизонтальный): правая -> нижняя
          return `${cellY},${newCellsLong}_h`;
        }
      }
    }

    // Обработка внутренних сегментов
    // Внутренние сегменты: горизонтальные (cellY > 0 && cellY < oldCellsLong) или вертикальные (cellX > 0 && cellX < oldCellsWide)
    const isInternalHorizontal = orientation === "h" && cellY > 0 && cellY < oldCellsLong;
    const isInternalVertical = orientation === "v" && cellX > 0 && cellX < oldCellsWide;

    if (isInternalHorizontal) {
      // Горизонтальный внутренний сегмент (cellX, cellY)_h
      // При повороте на 90° становится вертикальным внутренним сегментом (cellY, cellX)_v
      // Но нужно проверить, что новые координаты находятся в допустимых пределах
      const newCellX = cellY;
      const newCellY = cellX;
      if (newCellX > 0 && newCellX < newCellsWide && newCellY >= 0 && newCellY <= newCellsLong) {
        return `${newCellX},${newCellY}_v`;
      }
    } else if (isInternalVertical) {
      // Вертикальный внутренний сегмент (cellX, cellY)_v
      // При повороте на 90° становится горизонтальным внутренним сегментом (cellY, cellX)_h
      // Но нужно проверить, что новые координаты находятся в допустимых пределах
      const newCellX = cellY;
      const newCellY = cellX;
      if (newCellX >= 0 && newCellX <= newCellsWide && newCellY > 0 && newCellY < newCellsLong) {
        return `${newCellX},${newCellY}_h`;
      }
    }

    // Если сегмент не удалось замапить, возвращаем null
    return null;
  };

  // Функция для пересчета positionOnSegment при повороте
  const recalculatePositionOnSegment = (oldKey, newKey, oldPosition) => {
    if (oldPosition === undefined || oldPosition === null) return oldPosition;

    const [oldCoords, oldOrientation] = oldKey.split("_");
    const [newCoords, newOrientation] = newKey.split("_");
    const [oldCellX, oldCellY] = oldCoords.split(",").map(Number);
    const [newCellX, newCellY] = newCoords.split(",").map(Number);

    // Если ориентация сегмента не изменилась (оба горизонтальные или оба вертикальные),
    // позиция сохраняется
    if (oldOrientation === newOrientation) {
      return oldPosition;
    }

    // При повороте на 90°:
    // - Горизонтальный сегмент становится вертикальным (или наоборот)
    // - Позиция сохраняется, так как длина сегмента одинакова (GRID_CELL_SIZE_M)
    // Но нужно учесть направление отсчета:
    //   - На верхней стене (h): 0 = левый край, 1 = правый край
    //   - На правой стене (v): 0 = верхний край, 1 = нижний край
    //   - На левой стене (v): 0 = верхний край, 1 = нижний край
    //   - На нижней стене (h): 0 = левый край, 1 = правый край

    // При повороте с 0° на 90° (по часовой):
    // - Верхняя стена (i,0_h) -> Правая стена (newCellsWide,i_v)
    //   Левый край верхней -> Верхний край правой (позиция сохраняется)
    // - Нижняя стена (i,oldCellsLong_h) -> Левая стена (0,i_v)
    //   Левый край нижней -> Верхний край левой (позиция сохраняется)
    // - Левая стена (0,j_v) -> Верхняя стена (j,0_h)
    //   Верхний край левой -> Левый край верхней (позиция сохраняется)
    // - Правая стена (oldCellsWide,j_v) -> Нижняя стена (j,newCellsLong_h)
    //   Верхний край правой -> Левый край нижней (позиция сохраняется)

    // При повороте с 90° на 0° (против часовой):
    // - Верхняя стена (i,0_h) -> Левая стена (0,i_v)
    //   Левый край верхней -> Верхний край левой (позиция сохраняется)
    // - Нижняя стена (i,oldCellsLong_h) -> Правая стена (newCellsWide,i_v)
    //   Левый край нижней -> Верхний край правой (позиция сохраняется)
    // - Левая стена (0,j_v) -> Нижняя стена (j,newCellsLong_h)
    //   Верхний край левой -> Левый край нижней (позиция сохраняется)
    // - Правая стена (oldCellsWide,j_v) -> Верхняя стена (j,0_h)
    //   Верхний край правой -> Левый край верхней (позиция сохраняется)

    // В обоих случаях позиция сохраняется, так как:
    // - Длина сегмента одинакова (GRID_CELL_SIZE_M)
    // - Направление отсчета соответствует (начало -> начало, конец -> конец)
    return oldPosition;
  };

  // Переносим все сегменты (включая внутренние стенки) из старых в новые
  for (const [oldKey, oldSegment] of Object.entries(oldWallSegments)) {
    const newKey = mapSegmentKey(oldKey);

    if (newKey) {
      // Если сегмент еще не существует в новых сегментах, создаем его
      // Это важно для внутренних сегментов, которые могут не быть в internalWallSegmentsConfig
      if (!newSegments[newKey]) {
        // Проверяем, является ли это внутренним сегментом
        const isNewInternal = (() => {
          const [coords, orientation] = newKey.split("_");
          const [cxStr, cyStr] = coords.split(",");
          const cellX = parseInt(cxStr, 10);
          const cellY = parseInt(cyStr, 10);
          if (orientation === "h") return cellY > 0 && cellY < newCellsLong;
          if (orientation === "v") return cellX > 0 && cellX < newCellsWide;
          return false;
        })();

        // Создаем новый сегмент
        newSegments[newKey] = {
          id: generateId("wall_"),
          type: OBJECT_TYPES.WALL_SEGMENT,
          elements: [],
          thickness: isNewInternal
            ? CORRIDOR_WALL_THICKNESS_M
            : (oldSegment.thickness || WALL_THICKNESS_M_RENDER),
        };

      }

      // Переносим элементы в новый сегмент (если они есть)
      if (oldSegment.elements && oldSegment.elements.length > 0) {
        if (!newSegments[newKey].elements) {
          newSegments[newKey].elements = [];
        }
        // Копируем элементы с пересчетом positionOnSegment
        const mappedElements = oldSegment.elements.map(el => {
          const oldPosition = el.positionOnSegment !== undefined && el.positionOnSegment !== null
            ? el.positionOnSegment
            : 0.5; // Значение по умолчанию
          const newPosition = recalculatePositionOnSegment(
            oldKey,
            newKey,
            oldPosition
          );
          return {
            ...el,
            positionOnSegment: newPosition !== undefined && newPosition !== null
              ? newPosition
              : oldPosition
          };
        });
        newSegments[newKey].elements = [
          ...(newSegments[newKey].elements || []),
          ...mappedElements
        ];

      }

      // Сохраняем толщину стены, если она была задана
      if (oldSegment.thickness) {
        newSegments[newKey].thickness = oldSegment.thickness;
      }
    } else {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`[Rotation] Could not map segment ${oldKey} -> null, segment will be lost`);
      }
    }
  }

  // При повороте на 90° width и height меняются местами
  const oldWidth = module.width || (oldCellsWide * GRID_CELL_SIZE_M);
  const oldHeight = module.height || (oldCellsLong * GRID_CELL_SIZE_M);
  const newWidth = oldHeight;
  const newHeight = oldWidth;

  return {
    ...module,
    cellsWide: newCellsWide,
    cellsLong: newCellsLong,
    width: newWidth,
    height: newHeight,
    wallSegments: newSegments,
  };
};

const recalculateStateAfterGeometryChange = (
  currentObjects,
  originalObjects,
  t,
) => {
  const workingObjects = JSON.parse(JSON.stringify(currentObjects));

  const modules = workingObjects.filter((o) => o.type === OBJECT_TYPES.MODULE);

  const dockingPairs = new Map();
  modules.forEach((m) => dockingPairs.set(m.id, new Set()));

  for (let i = 0; i < modules.length; i++) {
    for (let j = i + 1; j < modules.length; j++) {
      if (findOverlappingSegment(modules[i], modules[j], DOCKING_TOLERANCE)) {
        dockingPairs.get(modules[i].id).add(modules[j].id);
        dockingPairs.get(modules[j].id).add(modules[i].id);
      }
    }
  }

  for (let i = 0; i < modules.length; i++) {
    for (let j = i + 1; j < modules.length; j++) {
      const moduleA = modules[i];
      const moduleB = modules[j];

      const areDocked = dockingPairs.get(moduleA.id).has(moduleB.id);

      if (areDocked) continue;

      const isOverlapping = checkOverlapWithRotation(moduleA, moduleB);
      if (isOverlapping) {
        setTimeout(() => toast.error(t("toasts.moduleOverlapError")), 0);
        return originalObjects;
      }

      const isTouchingAtCorner = areModulesTouchingAtCorner(moduleA, moduleB);
      if (isTouchingAtCorner) {
        const isA_connected = dockingPairs.get(moduleA.id).size > 0;
        const isB_connected = dockingPairs.get(moduleB.id).size > 0;
        if (!isA_connected && !isB_connected) {
          setTimeout(() => toast.error(t("toasts.moduleOverlapError")), 0);
          return originalObjects;
        }
      }

      if (checkInvalidAdjacency(moduleA, moduleB, modules, DOCKING_TOLERANCE)) {
        setTimeout(() => toast.error(t("toasts.invalidAdjacencyError")), 0);
        return originalObjects;
      }
    }
  }

  const oldJoints = (originalObjects || workingObjects).filter(
    (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
  );
  const oldJointsMap = new Map();
  oldJoints.forEach((joint) => {
    const key = [joint.moduleA_id, joint.moduleB_id].sort().join("-");
    oldJointsMap.set(key, {
      id: joint.id, // Сохраняем ID стыка для стабильности
      displayMode: joint.displayMode || "double",
      elements: joint.elements || [],
      activeWallSegments: joint.activeWallSegments || [],
      width: joint.width, // Сохраняем ширину для проверки изменений
      passageSegments: joint.passageSegments || [], // Сохраняем массив проходов
    });
  });

  let finalObjects = workingObjects.filter(
    (o) => o.type !== OBJECT_TYPES.DOCKING_JOINT,
  );

  finalObjects.forEach((m) => {
    if (m.type === OBJECT_TYPES.MODULE && m.wallSegments) {
      Object.values(m.wallSegments).forEach((w) => {
        w.isDocked = false;
        delete w.jointId;
      });
    }
  });

  const newJoints = [];
  for (let i = 0; i < modules.length; i++) {
    for (let j = i + 1; j < modules.length; j++) {
      const moduleA = modules[i];
      const moduleB = modules[j];
      const dockingInfo = findOverlappingSegment(
        moduleA,
        moduleB,
        DOCKING_TOLERANCE,
      );

      if (dockingInfo) {
        const { overlapSegment, moduleA_id, moduleB_id } = dockingInfo;
        const [start, end] = overlapSegment;
        const isVerticalDocking = moduleA.isDerived || moduleB.isDerived;
        let jointId = null;

        if (!isVerticalDocking) {
          const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
          const vec = subtractVectors(end, start);
          const width = distance(start, end);
          const rotation = Math.atan2(vec.y, vec.x) * (180 / Math.PI);
          const key = [moduleA_id, moduleB_id].sort().join("-");
          const oldJointData = oldJointsMap.get(key);

          // Используем существующий ID стыка, если он есть, чтобы избежать "прыжков"
          const jointIdToUse = oldJointData?.id || generateId("joint_");

          const joint = {
            id: jointIdToUse,
            type: OBJECT_TYPES.DOCKING_JOINT,
            moduleA_id,
            moduleB_id,
            x: center.x,
            y: center.y,
            width,
            rotation,
            displayMode: "double",
            elements: [],
            activeWallSegments: [],
          };

          if (oldJointData) {
            joint.displayMode = oldJointData.displayMode;
            joint.activeWallSegments = oldJointData.activeWallSegments;
            // Восстанавливаем массив проходов
            if (oldJointData.passageSegments && oldJointData.passageSegments.length > 0) {
              joint.passageSegments = oldJointData.passageSegments;
            }
            if (joint.displayMode !== "passage") {
              joint.elements = oldJointData.elements;

              // Если ширина стыка изменилась, пересчитываем позиции элементов
              const oldWidth = oldJointData.width;
              if (oldWidth && Math.abs(oldWidth - width) > 0.001) {

                // Пересчитываем позиции элементов, сохраняя их в тех же сегментах
                const oldNumSlots = Math.max(1, Math.round(oldWidth / GRID_CELL_SIZE_M));
                const newNumSlots = Math.max(1, Math.round(width / GRID_CELL_SIZE_M));

                joint.elements.forEach(el => {
                  // Определяем, в каком сегменте был элемент
                  const oldSlotIndex = Math.max(0, Math.min(oldNumSlots - 1, Math.floor(el.positionOnSegment * oldNumSlots)));

                  // Вычисляем относительную позицию внутри сегмента
                  const oldSegmentStart = oldSlotIndex / oldNumSlots;
                  const oldSegmentEnd = (oldSlotIndex + 1) / oldNumSlots;
                  const relativePositionInSegment = (el.positionOnSegment - oldSegmentStart) / (oldSegmentEnd - oldSegmentStart);

                  // Размещаем элемент в соответствующем новом сегменте
                  const newSlotIndex = Math.min(oldSlotIndex, newNumSlots - 1);
                  const newSegmentStart = newSlotIndex / newNumSlots;
                  const newSegmentEnd = (newSlotIndex + 1) / newNumSlots;
                  el.positionOnSegment = newSegmentStart + relativePositionInSegment * (newSegmentEnd - newSegmentStart);
                  el.segmentIndex = newSlotIndex;

                });
              } else if (joint.elements.length > 0) {
                const currentNumSlots = Math.max(
                  1,
                  Math.round(width / GRID_CELL_SIZE_M),
                );
                joint.elements.forEach((el) => {
                  el.segmentIndex = Math.max(
                    0,
                    Math.min(
                      currentNumSlots - 1,
                      Math.floor((el.positionOnSegment ?? 0.5) * currentNumSlots),
                    ),
                  );
                });
              }
            } else {
              joint.elements = [];
            }
          }
          newJoints.push(joint);
          jointId = joint.id;
        }

        const modA = finalObjects.find((m) => m.id === moduleA_id);
        const modB = finalObjects.find((m) => m.id === moduleB_id);
        const segmentLen = distance(start, end);
        const steps = Math.max(1, Math.round(segmentLen / GRID_CELL_SIZE_M));

        for (let k = 0; k < steps; k++) {
          const ratio = (k + 0.5) / steps;
          const worldPoint = addVectors(
            start,
            scaleVector(subtractVectors(end, start), ratio),
          );
          const localA = getLocalCoordinates(worldPoint.x, worldPoint.y, modA);
          const localB = getLocalCoordinates(worldPoint.x, worldPoint.y, modB);
          const keyA = getWallSegmentKeyFromLocal(localA.x, localA.y, modA);
          const keyB = getWallSegmentKeyFromLocal(localB.x, localB.y, modB);
          if (keyA && modA.wallSegments[keyA]) {
            modA.wallSegments[keyA].isDocked = true;
            if (jointId) modA.wallSegments[keyA].jointId = jointId;
          }
          if (keyB && modB.wallSegments[keyB]) {
            modB.wallSegments[keyB].isDocked = true;
            if (jointId) modB.wallSegments[keyB].jointId = jointId;
          }
        }
      }
    }
  }

  return [...finalObjects, ...newJoints];
};

const validateProjectInteractions = (
  floorObjects,
  floorId,
  projectData,
  floorsArray,
  t,
) => {
  let warningsGiven = new Set();
  const modules = floorObjects.filter((m) => m.type === OBJECT_TYPES.MODULE);
  const joints = floorObjects.filter(
    (j) => j.type === OBJECT_TYPES.DOCKING_JOINT,
  );

  // ПРИМЕЧАНИЕ: Проверка отключена, так как она мешает процессу проектирования
  // Пользователь может добавлять внутренние стены и двери в любом порядке

  // modules.forEach((module) => {
  //   const internalWallSegments = Object.keys(module.wallSegments || {})
  //     .map((key) => {
  //       const segment = module.wallSegments[key];
  //       const [coords, orientation] = key.split("_");
  //       const [cxStr, cyStr] = coords.split(",");
  //       return {
  //         ...segment,
  //         cellX: parseInt(cxStr, 10),
  //         cellY: parseInt(cyStr, 10),
  //         orientation,
  //       };
  //     })
  //     .filter((ws) => {
  //       return (
  //         (ws.orientation === "h" &&
  //           ws.cellY > 0 &&
  //           ws.cellY < module.cellsLong) ||
  //         (ws.orientation === "v" &&
  //           ws.cellX > 0 &&
  //           ws.cellX < module.cellsWide)
  //       );
  //     });

  //   if (internalWallSegments.length > 0) {
  //     let hasAnyDoor = false;
  //     Object.values(module.wallSegments || {}).forEach((seg) => {
  //       if (seg.elements?.some((el) => el.type === OBJECT_TYPES.DOOR))
  //         hasAnyDoor = true;
  //     });
  //     joints.forEach((joint) => {
  //       if (joint.moduleA_id === module.id || joint.moduleB_id === module.id) {
  //         if (joint.elements?.some((el) => el.type === OBJECT_TYPES.DOOR))
  //           hasAnyDoor = true;
  //       }
  //     });

  //     if (!hasAnyDoor) {
  //       const warnKey = `module_no_exit_${module.id}`;
  //       if (!warningsGiven.has(warnKey)) {
  //         toast.warn(
  //           t("toasts.moduleHasNoExitWarning", {
  //             moduleLabel: module.label || module.id.slice(-4),
  //           }),
  //           { autoClose: 7000 },
  //         );
  //         warningsGiven.add(warnKey);
  //       }
  //     }
  //   }
  // });

  return floorObjects;
};

export const projectReducer = (state, action) => {
  const currentFloorId = state.currentFloorId;
  const currentFloorObjects = state.projectObjects[currentFloorId] || [];
  const currentFloorIndex = state.floors.findIndex(
    (f) => f.id === currentFloorId,
  );

  const t = action.t;

  // 1. Floor domain reducer
  const floorResult = handleFloorAction(state, action, t);
  if (floorResult !== null) return floorResult;

  let nextStateObjects = currentFloorObjects;
  let needsRecalculation = false;
  let needsValidation = false;
  let newSelectedObjectId = state.selectedObjectId;
  let framelessElementsUnlocked = state.framelessElementsUnlocked;

  const domainContext = {
    currentFloorObjects,
    currentFloorId,
    currentFloorIndex,
    t,
    getJointEndpoints,
    recalculateStateAfterGeometryChange,
    validateProjectInteractions,
    recalculateWallSegmentsAfterRotation,
    createFramelessWallWithSegments,
  };

  // 2. Domain slice reducers (Joint, Element, Wall, Module)
  const domainResult =
    handleJointAction(state, action, domainContext) ||
    handleElementAction(state, action, domainContext) ||
    handleWallAction(state, action, domainContext) ||
    handleModuleAction(state, action, domainContext);

  if (domainResult && domainResult.handled) {
    if (domainResult.directState) {
      return domainResult.directState;
    }
    if (domainResult.nextStateObjects) {
      nextStateObjects = domainResult.nextStateObjects;
    }
    if (domainResult.needsRecalculation !== undefined) {
      needsRecalculation = domainResult.needsRecalculation;
    }
    if (domainResult.needsValidation !== undefined) {
      needsValidation = domainResult.needsValidation;
    }
    if (domainResult.newSelectedObjectId !== undefined) {
      newSelectedObjectId = domainResult.newSelectedObjectId;
    }
  } else {
    switch (action.type) {
    case "SET_STATE":
      return { ...state, ...action.payload };

    case "SET_ACTIVE_MODE": {
      const initialFloor = state.floors[0];
      let initialObjects = [];
      let newSelectedId = null;

      // Для каркасного режима сетка изначально пустая
      // Каркасы добавляются пользователем через drag and drop

      return {
        ...state,
        projectObjects: { [initialFloor.id]: initialObjects },
        currentFloorId: initialFloor.id,
        floors: [initialFloor],
        activeMode: action.payload,
        selectedObjectId: newSelectedId,
      };
    }

    case "ADD_FRAME_SECTION": {
      nextStateObjects = currentFloorObjects.map((obj) => {
        if (obj.type === OBJECT_TYPES.FRAME_STRUCTURE) {
          const newGridLength = obj.gridLength + FRAME_MODE_SECTION_LENGTH;
          return {
            ...obj,
            length: newGridLength * FRAMELESS_GRID_CELL_SIZE_M,
            gridLength: newGridLength,
            perimeterWalls: createFramePerimeter(obj.gridWidth, newGridLength),
            canRemoveSection: true,
          };
        }
        return obj;
      });
      break;
    }

    case "REMOVE_FRAME_SECTION": {
      nextStateObjects = currentFloorObjects.map((obj) => {
        if (obj.type === OBJECT_TYPES.FRAME_STRUCTURE && obj.canRemoveSection) {
          const newGridLength = obj.gridLength - FRAME_MODE_SECTION_LENGTH;
          return {
            ...obj,
            length: newGridLength * FRAMELESS_GRID_CELL_SIZE_M,
            gridLength: newGridLength,
            perimeterWalls: createFramePerimeter(obj.gridWidth, newGridLength),
            canRemoveSection: newGridLength > FRAME_MODE_INITIAL_LENGTH,
          };
        }
        return obj;
      });
      break;
    }

    case "CREATE_FRAMELESS_BUILDING": {
      const { dimensions, position, suggestions } = action.payload;
      const { width, length, height } = dimensions;

      const existingObjects = currentFloorObjects.filter(
        (obj) => obj.type !== OBJECT_TYPES.FRAMELESS_BUILDING,
      );

      if (width === 0 && length === 0) {
        nextStateObjects = existingObjects;
        break;
      }

      const newBuilding = {
        id: generateId("fb_"),
        type: OBJECT_TYPES.FRAMELESS_BUILDING,
        width,
        length,
        height,
        x: position ? position.x : 0,
        y: position ? position.y : 0,
        perimeterWalls: [],
        internalWalls: [],
      };

      const w = width;
      const l = length;
      const pos = newBuilding;

      const wallsData = [
        {
          start: { x: pos.x - w / 2, y: pos.y - l / 2 },
          end: { x: pos.x + w / 2, y: pos.y - l / 2 },
        },
        {
          start: { x: pos.x + w / 2, y: pos.y - l / 2 },
          end: { x: pos.x + w / 2, y: pos.y + l / 2 },
        },
        {
          start: { x: pos.x + w / 2, y: pos.y + l / 2 },
          end: { x: pos.x - w / 2, y: pos.y + l / 2 },
        },
        {
          start: { x: pos.x - w / 2, y: pos.y + l / 2 },
          end: { x: pos.x - w / 2, y: pos.y - l / 2 },
        },
      ];

      wallsData.forEach((data) => {
        const wall = createFramelessWallWithSegments(data.start, data.end);
        if (wall) {
          wall.isExternal = true;
          newBuilding.perimeterWalls.push(wall);
        }
      });


      if (suggestions && suggestions.length > 0) {
        suggestions.forEach((sug) => {
          const absoluteStart = {
            x: sug.start.x + pos.x,
            y: sug.start.y + pos.y,
          };
          const absoluteEnd = { x: sug.end.x + pos.x, y: sug.end.y + pos.y };

          const wall = createFramelessWallWithSegments(absoluteStart, absoluteEnd);
          if (wall) {
            wall.isExternal = false;
            newBuilding.internalWalls.push(wall);
          }
        });
      }

      nextStateObjects = [...currentFloorObjects, newBuilding];
      newSelectedObjectId = newBuilding.id;
      framelessElementsUnlocked = true;
      break;
    }

    case "START_DRAWING_WALL": {
      // Проверяем незамкнутые компоненты только в безкаркасном режиме
      if (state.activeMode === MODES.FRAMELESS) {
        const currentFloorId = state.currentFloorId;
        const objects = state.projectObjects[currentFloorId] || [];
        const existingWalls = objects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);

        if (existingWalls.length > 0) {
          // Находим все компоненты связанных стен
          const allComponents = findAllWallComponents(existingWalls);

          // Находим незамкнутые компоненты (компоненты, которые не образуют замкнутые контуры)
          const unclosedComponents = allComponents.filter(component => {
            // Проверяем, есть ли для этого компонента замкнутый контур
            const componentLoops = findAllClosedLoops(component);
            // Если компонент имеет стены, но не образует замкнутый контур - он незамкнут
            return component.length >= 1 && componentLoops.length === 0;
          });

          if (unclosedComponents.length > 0) {
            // Есть незамкнутые компоненты - новая стена должна начинаться от последней поставленной стены
            const { startPoint } = action.payload;
            const epsilon = 0.1; // Допуск для сравнения точек (10 см)

            // Проверяем, есть ли последняя стена
            if (state.framelessLastWallId) {
              const lastWall = existingWalls.find(w => w.id === state.framelessLastWallId);

              if (lastWall) {
                // Проверяем, начинается ли новая стена от последней стены
                const distToLastStart = distance(startPoint, lastWall.startPoint);
                const distToLastEnd = distance(startPoint, lastWall.endPoint);

                if (distToLastStart >= epsilon && distToLastEnd >= epsilon) {
                  // Также проверяем примыкание к средней части последней стены
                  const nearest = findNearestPointOnLineSegment(startPoint, lastWall.startPoint, lastWall.endPoint);
                  if (nearest.distance >= epsilon || nearest.t < 0 || nearest.t > 1) {
                    // Новая стена не начинается от последней стены
                    setTimeout(() => toast.error('Продовжуйте малювати від останньої поставленої стіни. Спочатку завершіть поточну кімнату або видаліть її.'), 0);
                    return state; // Не начинаем рисование новой стены
                  }
                }
              }
            } else {
              // Нет последней стены, но есть незамкнутые компоненты
              // Проверяем, начинается ли новая стена от любого незамкнутого компонента
              let continuesUnclosedComponent = false;

              for (const unclosedComponent of unclosedComponents) {
                for (const wall of unclosedComponent) {
                  const distToStart = distance(startPoint, wall.startPoint);
                  const distToEnd = distance(startPoint, wall.endPoint);

                  if (distToStart < epsilon || distToEnd < epsilon) {
                    continuesUnclosedComponent = true;
                    break;
                  }

                  const nearest = findNearestPointOnLineSegment(startPoint, wall.startPoint, wall.endPoint);
                  if (nearest.distance < epsilon && nearest.t >= 0 && nearest.t <= 1) {
                    continuesUnclosedComponent = true;
                    break;
                  }
                }

                if (continuesUnclosedComponent) break;
              }

              if (!continuesUnclosedComponent) {
                setTimeout(() => toast.error('Спочатку завершіть поточну кімнату або видаліть її, перш ніж починати нову.'), 0);
                return state;
              }
            }
          }
        }
      }

      return { ...state, drawingWall: action.payload };
    }

    case "UPDATE_DRAWING_WALL":
      if (!state.drawingWall) return state;
      return {
        ...state,
        drawingWall: {
          ...state.drawingWall,
          endPoint: action.payload.endPoint,
        },
      };

    case "CANCEL_DRAWING_WALL":
      return { ...state, drawingWall: null };

    case "FINISH_DRAWING_WALL": {
      if (!state.drawingWall) return state;
      const { startPoint, endPoint } = state.drawingWall;
      const { snapTarget } = action.payload || {};
      const wallLength = distance(startPoint, endPoint);

      if (wallLength < 0.1) {
        return { ...state, drawingWall: null };
      }

      const newWallCandidate = {
        id: generateId("temp_"),
        type: OBJECT_TYPES.FRAMELESS_WALL,
        startPoint,
        endPoint,
      };

      const allWallsForCheck = [
        ...currentFloorObjects.filter(
          (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL,
        ),
        newWallCandidate,
      ];
      const connectedComponent = findConnectedWalls(
        newWallCandidate,
        allWallsForCheck,
      );

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      connectedComponent.forEach((wall) => {
        minX = Math.min(minX, wall.startPoint.x, wall.endPoint.x);
        minY = Math.min(minY, wall.startPoint.y, wall.endPoint.y);
        maxX = Math.max(maxX, wall.startPoint.x, wall.endPoint.x);
        maxY = Math.max(maxY, wall.startPoint.y, wall.endPoint.y);
      });

      for (const wall of currentFloorObjects.filter(
        (obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL,
      )) {
        if (
          lineSegmentsIntersect(
            startPoint,
            endPoint,
            wall.startPoint,
            wall.endPoint,
          )
        ) {
          setTimeout(() => toast.error("Стіни не можуть перетинатися"), 0);
          return { ...state, drawingWall: null };
        }
      }

      let objects = [...currentFloorObjects];

      const newWall = createFramelessWallWithSegments(startPoint, endPoint);
      if (!newWall) {
        return { ...state, drawingWall: null };
      }

      // 🧱 ЛОГ: Создание прямой стены в безкаркасном режиме
      const newWallLength = distance(startPoint, endPoint);
      const isHorizontal = Math.abs(endPoint.y - startPoint.y) < 0.01;
      const isVertical = Math.abs(endPoint.x - startPoint.x) < 0.01;
      const direction = isHorizontal ? 'горизонтальная' : isVertical ? 'вертикальная' : 'диагональная';
      console.log('🧱 [FRAMELESS] Создание прямой стены:', {
        wallId: newWall.id,
        startPoint: { x: startPoint.x.toFixed(3), y: startPoint.y.toFixed(3) },
        endPoint: { x: endPoint.x.toFixed(3), y: endPoint.y.toFixed(3) },
        length: newWallLength.toFixed(3) + 'м',
        direction,
        segmentsCount: newWall.segments?.length || 0,
        hasSnapTarget: !!snapTarget,
        snapPoint: snapTarget ? { x: snapTarget.snapPoint?.x?.toFixed(3), y: snapTarget.snapPoint?.y?.toFixed(3) } : null,
        willSplitWall: !!(snapTarget && snapTarget.wallId)
      });

      if (snapTarget && snapTarget.wallId) {
        const wallToSplit = objects.find((w) => w.id === snapTarget.wallId);
        if (wallToSplit) {
          console.log('✂️ [FRAMELESS] Разделение существующей стены:', {
            wallToSplitId: wallToSplit.id,
            splitPoint: { x: snapTarget.snapPoint?.x?.toFixed(3), y: snapTarget.snapPoint?.y?.toFixed(3) }
          });
          objects = objects.filter((w) => w.id !== snapTarget.wallId);
          const [segment1, segment2] = splitFramelessWall(wallToSplit, snapTarget.snapPoint);
          if (segment1) {
            console.log('  → Создан сегмент 1:', { id: segment1.id, length: distance(segment1.startPoint, segment1.endPoint).toFixed(3) + 'м' });
            objects.push(segment1);
          }
          if (segment2) {
            console.log('  → Создан сегмент 2:', { id: segment2.id, length: distance(segment2.startPoint, segment2.endPoint).toFixed(3) + 'м' });
            objects.push(segment2);
          }
        }
      }

      objects.push(newWall);

      return {
        ...state,
        projectObjects: { ...state.projectObjects, [currentFloorId]: objects },
        drawingWall: null,
        selectedObjectId: newWall.id,
        framelessElementsUnlocked:
          state.framelessElementsUnlocked || findConnectedWalls(newWall, objects.filter((obj) => obj.type === OBJECT_TYPES.FRAMELESS_WALL)).length > 0,
      };
    }

    // --- НАЧАЛО: ИЗМЕНЕННАЯ ЛОГИКА ---
    case "CREATE_FRAMELESS_INTERNAL_WALL": {
      const { startPoint, endPoint, parentRoomId } = action.payload;
      const currentFloorId = state.currentFloorId;
      const currentFloorObjects = state.projectObjects[currentFloorId] || [];

      // Создаем один сэндвич (перегородку) для одного сегмента сетки
      const wallLength = distance(startPoint, endPoint);
      if (wallLength < 0.1) {
        return state;
      }

      // Создаем один сегмент для этого сэндвича
      const segments = [{
        id: generateId("fws_"),
        start: 0,
        length: wallLength,
        elements: [],
      }];

      const newInternalWall = {
        id: generateId("fip_"),
        type: OBJECT_TYPES.FRAMELESS_PARTITION,
        startPoint,
        endPoint,
        segments,
        isInternal: true, // Флаг для внутренней стены
        parentRoomId, // ID комнаты, внутри которой находится стена
      };

      const updatedObjects = [...currentFloorObjects, newInternalWall];

      return {
        ...state,
        projectObjects: {
          ...state.projectObjects,
          [currentFloorId]: updatedObjects,
        },
      };
    }

    case "ADD_WALL_SEGMENT": {
      let { startPoint, endPoint } = action.payload;
      let objects = [...currentFloorObjects];
      const SNAP_DISTANCE_FRAMELESS = 0.5; // Расстояние примагничивания в метрах

      // 1. Проверка на пересечения (кроме конечных точек)
      // И проверка на дублирование - если новая стена лежит на той же линии, что и существующая
      const epsilon = 0.01;
      for (const wall of objects.filter(o => o.type === OBJECT_TYPES.FRAMELESS_WALL)) {
        // Проверка на пересечение в середине (не в конечных точках)
        // Используем getLineIntersectionPoint, которая уже исключает пересечения в конечных точках
        const intersectionPoint = getLineIntersectionPoint(startPoint, endPoint, wall.startPoint, wall.endPoint);
        if (intersectionPoint) {
          // Если getLineIntersectionPoint вернула точку, значит есть пересечение в середине
          // (она уже исключает пересечения в конечных точках)
          setTimeout(() => toast.error("Стіни не можуть перетинатися посередині."), 0);
          return state;
        }

        // Дополнительная проверка: если стены пересекаются по lineSegmentsIntersect,
        // но это может быть соединение в углу - проверяем расстояния до конечных точек
        if (lineSegmentsIntersect(startPoint, endPoint, wall.startPoint, wall.endPoint)) {
          // Проверяем, не соединяются ли стены в конечных точках
          const distStartToWallStart = distance(startPoint, wall.startPoint);
          const distStartToWallEnd = distance(startPoint, wall.endPoint);
          const distEndToWallStart = distance(endPoint, wall.startPoint);
          const distEndToWallEnd = distance(endPoint, wall.endPoint);

          // Если хотя бы одна конечная точка новой стены совпадает с конечной точкой существующей - это соединение в углу, разрешено
          const isConnectionAtStart = distStartToWallStart < epsilon || distStartToWallEnd < epsilon;
          const isConnectionAtEnd = distEndToWallStart < epsilon || distEndToWallEnd < epsilon;

          if (!(isConnectionAtStart || isConnectionAtEnd)) {
            // Пересечение в середине - запрещено
            setTimeout(() => toast.error("Стіни не можуть перетинатися посередині."), 0);
            return state;
          }
        }

        // Проверка на дублирование - стены лежат на одной линии
        const newDx = endPoint.x - startPoint.x;
        const newDy = endPoint.y - startPoint.y;
        const existingDx = wall.endPoint.x - wall.startPoint.x;
        const existingDy = wall.endPoint.y - wall.startPoint.y;

        // Проверяем, параллельны ли стены
        const newIsHorizontal = Math.abs(newDy) < epsilon;
        const newIsVertical = Math.abs(newDx) < epsilon;
        const existingIsHorizontal = Math.abs(existingDy) < epsilon;
        const existingIsVertical = Math.abs(existingDx) < epsilon;

        if ((newIsHorizontal && existingIsHorizontal) || (newIsVertical && existingIsVertical)) {
          // Стены параллельны - проверяем, лежат ли на одной линии
          let onSameLine = false;

          if (newIsHorizontal && existingIsHorizontal) {
            // Обе горизонтальные - проверяем, совпадает ли Y координата
            const newY = startPoint.y;
            const existingY = wall.startPoint.y;
            if (Math.abs(newY - existingY) < epsilon) {
              onSameLine = true;
            }
          } else if (newIsVertical && existingIsVertical) {
            // Обе вертикальные - проверяем, совпадает ли X координата
            const newX = startPoint.x;
            const existingX = wall.startPoint.x;
            if (Math.abs(newX - existingX) < epsilon) {
              onSameLine = true;
            }
          }

          if (onSameLine) {
            // Стены на одной линии - проверяем перекрытие
            let newMin, newMax, existingMin, existingMax;

            if (newIsHorizontal) {
              newMin = Math.min(startPoint.x, endPoint.x);
              newMax = Math.max(startPoint.x, endPoint.x);
              existingMin = Math.min(wall.startPoint.x, wall.endPoint.x);
              existingMax = Math.max(wall.startPoint.x, wall.endPoint.x);
            } else {
              newMin = Math.min(startPoint.y, endPoint.y);
              newMax = Math.max(startPoint.y, endPoint.y);
              existingMin = Math.min(wall.startPoint.y, wall.endPoint.y);
              existingMax = Math.max(wall.startPoint.y, wall.endPoint.y);
            }

            // Проверяем, перекрываются ли стены
            const overlapMin = Math.max(newMin, existingMin);
            const overlapMax = Math.min(newMax, existingMax);
            const overlapLength = overlapMax - overlapMin;

            if (overlapLength > epsilon) {
              // Есть перекрытие - проверяем, полностью ли стены совпадают или перекрывают друг друга
              const newLength = newMax - newMin;
              const existingLength = existingMax - existingMin;

              // Если стены полностью совпадают (или почти полностью)
              if (Math.abs(newLength - existingLength) < epsilon &&
                Math.abs(newMin - existingMin) < epsilon &&
                Math.abs(newMax - existingMax) < epsilon) {
                // Не создаем новую стену - она полностью дублирует существующую
                setTimeout(() => toast.error('Стіна не може бути створена: вона повністю дублює існуючу стіну.'), 0);
                return { ...state, drawingWall: null };
              }

              // Если перекрытие составляет более 50% от длины новой стены или существующей - блокируем
              const overlapRatioNew = overlapLength / newLength;
              const overlapRatioExisting = overlapLength / existingLength;

              if (overlapRatioNew > 0.5 || overlapRatioExisting > 0.5) {
                // Значительное перекрытие - не создаем новую стену
                setTimeout(() => toast.error('Стіна не може бути створена: вона значно перекривається з існуючою стіною.'), 0);
                return { ...state, drawingWall: null };
              }

              // Если новая стена полностью перекрывает существующую (или почти полностью)
              if (overlapLength >= existingLength - epsilon && newLength >= existingLength - epsilon) {
                // Не создаем новую стену - она дублирует существующую
                setTimeout(() => toast.error('Стіна не може бути створена: вона дублює існуючу стіну.'), 0);
                return { ...state, drawingWall: null };
              }

              // Если существующая стена полностью перекрывает новую
              if (overlapLength >= newLength - epsilon && existingLength >= newLength - epsilon) {
                // Не создаем новую стену - она полностью внутри существующей
                setTimeout(() => toast.error('Стіна не може бути створена: вона повністю всередині існуючої стіни.'), 0);
                return { ...state, drawingWall: null };
              }
            } else {
              // Нет перекрытия, но стены на одной линии - проверяем, не совпадают ли они точно
              const newLength = newMax - newMin;
              const existingLength = existingMax - existingMin;

              // Если стены точно совпадают по координатам
              if (Math.abs(newMin - existingMin) < epsilon &&
                Math.abs(newMax - existingMax) < epsilon &&
                Math.abs(newLength - existingLength) < epsilon) {
                setTimeout(() => toast.error('Стіна не може бути створена: вона точно співпадає з існуючою стіною.'), 0);
                return { ...state, drawingWall: null };
              }
            }
          }
        }
      }

      // 2. Поиск точки примагничивания для конечной точки
      let snapTarget = null;
      for (const wall of objects.filter(o => o.type === OBJECT_TYPES.FRAMELESS_WALL)) {
        const res = findNearestPointOnLineSegment(endPoint, wall.startPoint, wall.endPoint);
        if (res.distance < SNAP_DISTANCE_FRAMELESS) {
          snapTarget = { wallToSplit: wall, snapPoint: res.point };
          break;
        }
      }

      // 3. Если найдена точка примагничивания, разделяем стену
      if (snapTarget) {
        const { wallToSplit, snapPoint } = snapTarget;

        // Проверяем, что точка привязки не является конечной точкой существующей стены
        const isAtEndpoint = pointsAreEqual(snapPoint, wallToSplit.startPoint) || pointsAreEqual(snapPoint, wallToSplit.endPoint);

        if (!isAtEndpoint) {
          objects = objects.filter(o => o.id !== wallToSplit.id);
          const newSegments = splitFramelessWall(wallToSplit, snapPoint);
          objects.push(...newSegments);
        }

        // Примагничиваем конечную точку новой стены
        endPoint = snapPoint;
      }

      // 4. Проверяем длину стены
      const wallLength = distance(startPoint, endPoint);
      if (wallLength < 0.1) {
        return { ...state, drawingWall: null }; // Не создаем слишком короткие стены
      }

      // 4.1. Проверяем проекции стены на оси X и Y - не должны превышать 6x4
      const dx = Math.abs(endPoint.x - startPoint.x);
      const dy = Math.abs(endPoint.y - startPoint.y);

      if (dx > MAX_FRAMELESS_WIDTH_M || dy > MAX_FRAMELESS_HEIGHT_M) {
        setTimeout(() => toast.error(`Максимально можна ставити ${MAX_FRAMELESS_WIDTH_M} на ${MAX_FRAMELESS_HEIGHT_M} сендвіча. Це максимум.`), 0);
        return { ...state, drawingWall: null };
      }

      // 4.2. Проверяем, что если есть последняя стена (незамкнутая комната), новая стена должна соединяться с ней
      const existingWalls = objects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);
      const existingClosedLoops = existingWalls.length > 0 ? findAllClosedLoops(existingWalls) : [];

      // Если есть последняя стена (незамкнутая комната), проверяем соединение
      if (state.framelessLastWallId) {
        const lastWall = existingWalls.find(w => w.id === state.framelessLastWallId);

        if (lastWall) {
          const epsilon = 0.1;
          // Проверяем, соединяется ли новая стена с последней стеной
          const distStartToLastStart = distance(startPoint, lastWall.startPoint);
          const distStartToLastEnd = distance(startPoint, lastWall.endPoint);
          const distEndToLastStart = distance(endPoint, lastWall.startPoint);
          const distEndToLastEnd = distance(endPoint, lastWall.endPoint);

          const connectsToLastWall =
            distStartToLastStart < epsilon || distStartToLastEnd < epsilon ||
            distEndToLastStart < epsilon || distEndToLastEnd < epsilon ||
            (() => {
              const nearestToStart = findNearestPointOnLineSegment(startPoint, lastWall.startPoint, lastWall.endPoint);
              const nearestToEnd = findNearestPointOnLineSegment(endPoint, lastWall.startPoint, lastWall.endPoint);
              return (nearestToStart.distance < epsilon && nearestToStart.t >= 0 && nearestToStart.t <= 1) ||
                (nearestToEnd.distance < epsilon && nearestToEnd.t >= 0 && nearestToEnd.t <= 1);
            })();

          if (!connectsToLastWall) {
            setTimeout(() => toast.error('Продовжуйте малювати від останньої поставленої стіни. Спочатку завершіть поточну кімнату або видаліть її.'), 0);
            return { ...state, drawingWall: null };
          }
        }
      }

      // 4.3. Проверяем, что если уже есть замкнутые комнаты, новая стена примыкает к существующим стенам
      if (existingWalls.length > 0) {
        if (existingClosedLoops.length > 0) {
          // Есть существующие комнаты - проверяем, что новая стена примыкает к существующим стенам
          const epsilon = 0.1; // Допуск для сравнения точек (10 см для более гибкой проверки)
          let touchesExistingWall = false;

          // Проверяем, примыкает ли начало или конец новой стены к существующим стенам
          for (const wall of existingWalls) {
            // Проверяем начало новой стены
            const distToStart = distance(startPoint, wall.startPoint);
            const distToEnd = distance(startPoint, wall.endPoint);
            if (distToStart < epsilon || distToEnd < epsilon) {
              touchesExistingWall = true;
              break;
            }

            // Проверяем конец новой стены
            const distEndToStart = distance(endPoint, wall.startPoint);
            const distEndToEnd = distance(endPoint, wall.endPoint);
            if (distEndToStart < epsilon || distEndToEnd < epsilon) {
              touchesExistingWall = true;
              break;
            }

            // Также проверяем примыкание к средней части существующей стены
            // Проверяем, находится ли начальная точка новой стены близко к линии существующей стены
            const nearestToStart = findNearestPointOnLineSegment(startPoint, wall.startPoint, wall.endPoint);
            if (nearestToStart.distance < epsilon && nearestToStart.t >= 0 && nearestToStart.t <= 1) {
              // Точка находится на сегменте (t в диапазоне [0, 1])
              touchesExistingWall = true;
              break;
            }

            // Проверяем, находится ли конечная точка новой стены близко к линии существующей стены
            const nearestToEnd = findNearestPointOnLineSegment(endPoint, wall.startPoint, wall.endPoint);
            if (nearestToEnd.distance < epsilon && nearestToEnd.t >= 0 && nearestToEnd.t <= 1) {
              // Точка находится на сегменте (t в диапазоне [0, 1])
              touchesExistingWall = true;
              break;
            }
          }

          if (!touchesExistingWall) {
            setTimeout(() => toast.error('Кожна наступна кімната повинна примикати до вже існуючої кімнати.'), 0);
            return { ...state, drawingWall: null };
          }
        }
      }

      // 5. Создаем временную стену для проверки (не добавляем в objects)
      const tempWall = createFramelessWallWithSegments(startPoint, endPoint);
      if (!tempWall) {
        return { ...state, drawingWall: null };
      }

      // 6. Проверяем размеры каждой отдельной комнаты (замкнутого контура), а не всего компонента
      const tempObjects = [...objects, tempWall];
      const tempAllWalls = tempObjects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);
      const tempComponentWithNewWall = findConnectedWalls(tempWall, tempAllWalls);

      // Находим все замкнутые контуры в компоненте с новой стеной
      // findAllClosedLoops уже возвращает только минимальные контуры (не содержащие другие внутри)
      const tempClosedLoops = findAllClosedLoops(tempComponentWithNewWall);

      // Поиск замкнутых контуров (комнат)

      // Находим только НОВЫЕ комнаты
      // Новая комната - это контур, который:
      // 1. Содержит новую стену (tempWall.id)
      // 2. Имеет стены, которых нет в существующих комнатах (не является просто расширением или объединением)
      const newClosedLoops = tempClosedLoops.filter((loop, loopIdx) => {
        const loopWallIds = new Set(loop.map(w => w.id));
        const filterReason = [];

        // Детальное логирование для отладки
        const debugInfo = {
          loopIndex: loopIdx + 1,
          wallsCount: loop.length,
          wallIds: loop.map(w => w.id)
        };

        // Проверяем, содержит ли контур новую стену
        const containsNewWall = loopWallIds.has(tempWall.id);
        if (!containsNewWall) {
          filterReason.push('не содержит новую стену');
          return false; // Контур не содержит новую стену - это не новая комната
        }

        // Собираем все стены из существующих комнат
        const allExistingWallIds = new Set();
        existingClosedLoops.forEach(existingLoop => {
          existingLoop.forEach(wall => allExistingWallIds.add(wall.id));
        });

        // Проверяем, является ли этот контур полностью идентичным существующему
        const isIdenticalToExisting = existingClosedLoops.some(existingLoop => {
          if (existingLoop.length !== loop.length) return false;
          const existingWallIds = new Set(existingLoop.map(w => w.id));
          // Проверяем, что все стены совпадают
          return loop.length === existingWallIds.size &&
            [...loopWallIds].every(id => existingWallIds.has(id));
        });

        if (isIdenticalToExisting) {
          filterReason.push('идентичен существующей комнате');
          return false; // Это точно такая же комната - не новая
        }

        // Проверяем, не является ли это просто объединением существующих комнат
        // Если все стены контура (кроме новой стены) уже были в существующих комнатах - это не новая комната
        const wallsWithoutNew = [...loopWallIds].filter(id => id !== tempWall.id);
        const allWallsWereExisting = wallsWithoutNew.every(id => allExistingWallIds.has(id));

        if (allWallsWereExisting && wallsWithoutNew.length > 0) {
          // Все стены (кроме новой) уже были в существующих комнатах
          // Проверяем, сколько существующих комнат содержит стены из этого контура
          let existingRoomsCount = 0;
          const roomsWithCommonWalls = [];
          for (const existingLoop of existingClosedLoops) {
            const existingWallIds = new Set(existingLoop.map(w => w.id));
            const hasCommonWalls = wallsWithoutNew.some(id => existingWallIds.has(id));
            if (hasCommonWalls) {
              existingRoomsCount++;
              roomsWithCommonWalls.push({ loop: existingLoop, wallIds: existingWallIds });
            }
          }

          // Если контур содержит стены из нескольких существующих комнат - это объединение, не новая комната
          if (existingRoomsCount >= 2) {
            filterReason.push(`объединение ${existingRoomsCount} существующих комнат`);
            return false;
          }

          // Если контур содержит стены только из одной существующей комнаты
          if (existingRoomsCount === 1) {
            const { loop: existingRoom, wallIds: existingWallIds } = roomsWithCommonWalls[0];

            // КЛЮЧЕВАЯ ПРОВЕРКА: Если новый контур содержит ВСЕ стены существующей комнаты - это расширение
            // Если новый контур содержит только ЧАСТЬ стен существующей комнаты (общая стенка) - это новая комната
            const allExistingWallsInNew = [...existingWallIds].every(id => loopWallIds.has(id));

            if (allExistingWallsInNew) {
              // Новый контур содержит все стены существующей комнаты
              // Проверяем, является ли это расширением или новой комнатой с общей стенкой
              let existingMinX = Infinity, existingMinY = Infinity, existingMaxX = -Infinity, existingMaxY = -Infinity;
              existingRoom.forEach(wall => {
                existingMinX = Math.min(existingMinX, wall.startPoint.x, wall.endPoint.x);
                existingMinY = Math.min(existingMinY, wall.startPoint.y, wall.endPoint.y);
                existingMaxX = Math.max(existingMaxX, wall.startPoint.x, wall.endPoint.x);
                existingMaxY = Math.max(existingMaxY, wall.startPoint.y, wall.endPoint.y);
              });

              let newMinX = Infinity, newMinY = Infinity, newMaxX = -Infinity, newMaxY = -Infinity;
              loop.forEach(wall => {
                newMinX = Math.min(newMinX, wall.startPoint.x, wall.endPoint.x);
                newMinY = Math.min(newMinY, wall.startPoint.y, wall.endPoint.y);
                newMaxX = Math.max(newMaxX, wall.startPoint.x, wall.endPoint.x);
                newMaxY = Math.max(newMaxY, wall.startPoint.y, wall.endPoint.y);
              });

              const existingWidth = existingMaxX - existingMinX;
              const existingHeight = existingMaxY - existingMinY;
              const newWidth = newMaxX - newMinX;
              const newHeight = newMaxY - newMinY;

              const epsilon = 0.01;

              // Проверяем, является ли существующая комната полностью внутри нового по границам
              const existingFullyInside = existingMinX >= newMinX - epsilon &&
                existingMaxX <= newMaxX + epsilon &&
                existingMinY >= newMinY - epsilon &&
                existingMaxY <= newMaxY + epsilon;

              // Проверяем, является ли новый контур МЕНЬШЕ существующего - это разделение комнаты, не расширение
              const isSmaller = newWidth < existingWidth - epsilon || newHeight < existingHeight - epsilon;

              // Если новый контур меньше существующего - это разделение комнаты (новая комната)
              if (isSmaller) {
                console.log(`✅ [FRAMELESS] Контур ${loopIdx + 1} меньше существующей - разделение комнаты (новая комната):`, {
                  ...debugInfo,
                  existingSize: `${existingWidth.toFixed(2)}x${existingHeight.toFixed(2)}`,
                  newSize: `${newWidth.toFixed(2)}x${newHeight.toFixed(2)}`,
                  note: 'Новая комната, созданная разделением существующей'
                });
                // Это новая комната, созданная разделением существующей - продолжаем проверку дальше
                // (не возвращаем false здесь)
              }

              // Если новый контур больше существующего И существующая комната полностью внутри - это расширение
              const isLarger = newWidth > existingWidth + epsilon || newHeight > existingHeight + epsilon;

              if (isLarger && existingFullyInside) {
                filterReason.push(`расширение существующей комнаты (${newWidth.toFixed(2)}x${newHeight.toFixed(2)} > ${existingWidth.toFixed(2)}x${existingHeight.toFixed(2)})`);
                return false; // Это расширение существующей комнаты, не новая комната
              }

              // Если новый контур содержит все стены существующей, но НЕ больше и НЕ содержит существующую полностью внутри
              // - это может быть новая комната с общей стенкой (комнаты рядом друг с другом)
              // Продолжаем проверку дальше (не возвращаем false здесь)
              console.log(`🔍 [FRAMELESS] Контур ${loopIdx + 1} содержит все стены существующей, но не расширение:`, {
                ...debugInfo,
                existingSize: `${existingWidth.toFixed(2)}x${existingHeight.toFixed(2)}`,
                newSize: `${newWidth.toFixed(2)}x${newHeight.toFixed(2)}`,
                isLarger,
                existingFullyInside,
                note: 'Возможно, новая комната с общей стенкой'
              });
            }
            // Если новый контур НЕ содержит все стены существующей комнаты - это новая комната с общей стенкой
            // Продолжаем проверку дальше (не возвращаем false здесь)
          }
        }

        // Проверяем, является ли это расширением существующей комнаты
        // (т.е. содержит все стены существующей комнаты плюс новые)
        let isExtensionOfExisting = false;
        for (const existingLoop of existingClosedLoops) {
          if (existingLoop.length === 0) continue;

          const existingWallIds = new Set(existingLoop.map(w => w.id));

          // Если все стены существующей комнаты входят в новый контур - это расширение
          const allExistingWallsInNew = [...existingWallIds].every(id => loopWallIds.has(id));

          // Но если новый контур имеет стены, которых нет в существующей - это новая комната
          const hasNewWalls = [...loopWallIds].some(id => !existingWallIds.has(id));

          if (allExistingWallsInNew && !hasNewWalls) {
            // Все стены существующей комнаты в новом контуре, и нет новых стен - это расширение
            filterReason.push('расширение: все стены существующей комнаты в новом контуре, нет новых стен');
            isExtensionOfExisting = true;
            break;
          }

          // Если новый контур содержит все стены существующей И имеет дополнительные стены
          if (allExistingWallsInNew && hasNewWalls) {
            // Находим границы существующего контура
            let existingMinX = Infinity, existingMinY = Infinity, existingMaxX = -Infinity, existingMaxY = -Infinity;
            existingLoop.forEach(wall => {
              existingMinX = Math.min(existingMinX, wall.startPoint.x, wall.endPoint.x);
              existingMinY = Math.min(existingMinY, wall.startPoint.y, wall.endPoint.y);
              existingMaxX = Math.max(existingMaxX, wall.startPoint.x, wall.endPoint.x);
              existingMaxY = Math.max(existingMaxY, wall.startPoint.y, wall.endPoint.y);
            });

            // Находим границы нового контура
            let newMinX = Infinity, newMinY = Infinity, newMaxX = -Infinity, newMaxY = -Infinity;
            loop.forEach(wall => {
              newMinX = Math.min(newMinX, wall.startPoint.x, wall.endPoint.x);
              newMinY = Math.min(newMinY, wall.startPoint.y, wall.endPoint.y);
              newMaxX = Math.max(newMaxX, wall.startPoint.x, wall.endPoint.x);
              newMaxY = Math.max(newMaxY, wall.startPoint.y, wall.endPoint.y);
            });

            const epsilon = 0.01;

            // Вычисляем размеры для проверки
            const existingWidth = existingMaxX - existingMinX;
            const existingHeight = existingMaxY - existingMinY;
            const newWidth = newMaxX - newMinX;
            const newHeight = newMaxY - newMinY;

            // Проверяем, является ли новый контур МЕНЬШЕ существующего - это разделение комнаты, не расширение
            const isSmaller = newWidth < existingWidth - epsilon || newHeight < existingHeight - epsilon;

            // Проверяем, является ли это расширением существующей комнаты
            // Расширение: существующая комната полностью внутри нового по границам
            const existingFullyInside = existingMinX >= newMinX - epsilon &&
              existingMaxX <= newMaxX + epsilon &&
              existingMinY >= newMinY - epsilon &&
              existingMaxY <= newMaxY + epsilon;

            // Проверяем, является ли это новой комнатой с общей стенкой
            // Новая комната: новый контур имеет стены, которых нет в существующей, 
            // и новый контур НЕ полностью содержит существующую комнату внутри
            // (т.е. они находятся рядом друг с другом, а не один внутри другого)
            const newWallsNotInExisting = [...loopWallIds].filter(id => !existingWallIds.has(id));
            const hasSignificantNewWalls = newWallsNotInExisting.length > 1; // Не только новая стена, но и другие

            // Если новый контур меньше существующей комнаты - это разделение комнаты (новая комната)
            if (isSmaller) {
              console.log(`✅ [FRAMELESS] Контур ${loopIdx + 1} меньше существующей - разделение комнаты (новая комната):`, {
                ...debugInfo,
                existingSize: `${existingWidth.toFixed(2)}x${existingHeight.toFixed(2)}`,
                newSize: `${newWidth.toFixed(2)}x${newHeight.toFixed(2)}`,
                note: 'Новая комната, созданная разделением существующей'
              });
              // Это новая комната, созданная разделением существующей - не помечаем как расширение
              // (не устанавливаем isExtensionOfExisting = true, продолжаем проверку)
            }
            // Если существующая комната полностью внутри нового И новый контур НЕ меньше - это расширение
            else if (existingFullyInside) {
              filterReason.push('расширение: существующая комната полностью внутри нового контура');
              isExtensionOfExisting = true;
              break;
            }

            // Если новый контур имеет значительное количество новых стен (не только новая стена),
            // и существующая комната НЕ полностью внутри нового - это может быть новая комната
            // Но нужно проверить, что это не просто расширение в одном направлении
            if (hasSignificantNewWalls && !existingFullyInside) {
              // Это новая комната с общей стенкой, не расширение
              // Продолжаем проверку дальше (не помечаем как расширение)
            } else {
              // Проверяем, является ли это расширением в одном направлении
              // (размеры уже вычислены выше)

              // Если новый контур больше только в одном направлении и содержит существующую - это расширение
              const widthExpanded = newWidth > existingWidth + epsilon;
              const heightExpanded = newHeight > existingHeight + epsilon;

              if ((widthExpanded && !heightExpanded) || (!widthExpanded && heightExpanded)) {
                // Расширение в одном направлении
                filterReason.push('расширение: расширение в одном направлении');
                isExtensionOfExisting = true;
                break;
              }
            }
          }
        }

        // Новая комната, если не является расширением существующей
        const isNewRoom = !isExtensionOfExisting;
        if (!isNewRoom && filterReason.length === 0) {
          filterReason.push('расширение существующей комнаты (общая проверка)');
        }

        // Логируем результат фильтрации для каждого контура
        if (!isNewRoom) {
          // Контур отфильтрован
        } else {
          console.log(`✅ [FRAMELESS] Контур ${loopIdx + 1} принят как новая комната:`, {
            ...debugInfo
          });
        }

        return isNewRoom;
      });

      if (newClosedLoops.length > 0) {
        console.log('🏠 [FRAMELESS] Найдена новая комната:', {
          roomsCount: newClosedLoops.length,
          rooms: newClosedLoops.map((loop, idx) => ({
            roomIndex: idx + 1,
            wallsCount: loop.length,
            wallIds: loop.map(w => w.id),
            containsNewWall: loop.some(w => w.id === tempWall.id)
          }))
        });
      } else {
        // Новые комнаты не найдены, все контуры отфильтрованы
      }

      // Проверяем, замкнут ли контур после добавления новой стены
      // Используем результаты findAllClosedLoops для определения замкнутости
      // Если найдены новые замкнутые контуры, значит комната замкнулась
      const hasNewClosedLoops = newClosedLoops.length > 0;

      // Также проверяем общую замкнутость компонента (для обратной совместимости)
      let isClosedLoop = tempComponentWithNewWall.length >= 4 && (() => {
        const epsilon = 0.001;
        const pointMap = new Map();
        tempComponentWithNewWall.forEach(wall => {
          const startKey = `${wall.startPoint.x.toFixed(3)},${wall.startPoint.y.toFixed(3)}`;
          const endKey = `${wall.endPoint.x.toFixed(3)},${wall.endPoint.y.toFixed(3)}`;
          pointMap.set(startKey, (pointMap.get(startKey) || 0) + 1);
          pointMap.set(endKey, (pointMap.get(endKey) || 0) + 1);
        });
        // Замкнутый контур: каждая точка встречается ровно 2 раза
        return Array.from(pointMap.values()).every(count => count === 2);
      })();

      // Комната считается замкнутой, если найдены новые замкнутые контуры
      // или если общий компонент замкнут
      const isComponentClosed = hasNewClosedLoops || isClosedLoop;

      // Проверяем размеры каждой отдельной комнаты
      // Находим размеры первой комнаты (если есть) для сравнения
      let firstRoomWidth = null;
      let firstRoomHeight = null;

      if (existingClosedLoops.length > 0) {
        const firstRoom = existingClosedLoops[0];
        let firstMinX = Infinity, firstMinY = Infinity, firstMaxX = -Infinity, firstMaxY = -Infinity;
        firstRoom.forEach((wall) => {
          firstMinX = Math.min(firstMinX, wall.startPoint.x, wall.endPoint.x);
          firstMinY = Math.min(firstMinY, wall.startPoint.y, wall.endPoint.y);
          firstMaxX = Math.max(firstMaxX, wall.startPoint.x, wall.endPoint.x);
          firstMaxY = Math.max(firstMaxY, wall.startPoint.y, wall.endPoint.y);
        });
        firstRoomWidth = firstMaxX - firstMinX;
        firstRoomHeight = firstMaxY - firstMinY;
      }

      // Проверяем размеры ТОЛЬКО для комнаты, которая создается новой стеной
      // Если комната уже закрыта (существует в existingClosedLoops) - НЕ проверяем ее размеры
      // Каждая закрытая комната имеет свой лимит 6x4, и проверяется только при ее создании

      // Собираем все стены из существующих закрытых комнат
      const allExistingWallIds = new Set();
      existingClosedLoops.forEach(existingLoop => {
        existingLoop.forEach(wall => allExistingWallIds.add(wall.id));
      });

      // Проверяем только комнаты из newClosedLoops, которые действительно создаются новой стеной
      for (const loop of newClosedLoops) {
        const loopWallIds = new Set(loop.map(w => w.id));

        // Комната должна содержать новую стену
        if (!loopWallIds.has(tempWall.id)) {
          // Комната не содержит новую стену - это существующая закрытая комната, пропускаем
          continue;
        }

        // Проверяем, содержит ли комната стены из существующих закрытых комнат
        const wallsWithoutNew = [...loopWallIds].filter(id => id !== tempWall.id);
        const hasWallsFromClosedRooms = wallsWithoutNew.some(id => allExistingWallIds.has(id));

        // Если комната содержит хотя бы одну стену из существующих закрытых комнат - это расширение/объединение
        // НЕ проверяем размеры, так как это не новая комната, а модификация существующих закрытых комнат
        if (hasWallsFromClosedRooms) {
          // Комната содержит стены из существующих закрытых комнат - пропускаем проверку
          console.log('📏 [FRAMELESS] Пропущена проверка - комната содержит стены из существующих закрытых комнат (расширение/объединение):', {
            wallsCount: loop.length,
            newWallId: tempWall.id,
            wallsFromClosedRooms: wallsWithoutNew.filter(id => allExistingWallIds.has(id)).length,
            totalWallsWithoutNew: wallsWithoutNew.length
          });
          continue;
        }

        // Это действительно новая комната, создаваемая новой стеной - проверяем размеры
        let roomMinX = Infinity, roomMinY = Infinity, roomMaxX = -Infinity, roomMaxY = -Infinity;
        loop.forEach((wall) => {
          roomMinX = Math.min(roomMinX, wall.startPoint.x, wall.endPoint.x);
          roomMinY = Math.min(roomMinY, wall.startPoint.y, wall.endPoint.y);
          roomMaxX = Math.max(roomMaxX, wall.startPoint.x, wall.endPoint.x);
          roomMaxY = Math.max(roomMaxY, wall.startPoint.y, wall.endPoint.y);
        });

        const roomWidth = roomMaxX - roomMinX;
        const roomHeight = roomMaxY - roomMinY;

        console.log('📏 [FRAMELESS] Проверка размеров создающейся новой комнаты:', {
          roomWidth: roomWidth.toFixed(2) + 'м',
          roomHeight: roomHeight.toFixed(2) + 'м',
          maxWidth: MAX_FRAMELESS_WIDTH_M + 'м',
          maxHeight: MAX_FRAMELESS_HEIGHT_M + 'м',
          exceedsLimit: roomWidth > MAX_FRAMELESS_WIDTH_M || roomHeight > MAX_FRAMELESS_HEIGHT_M,
          wallsCount: loop.length,
          wallIds: loop.map(w => w.id),
          newWallId: tempWall.id
        });

        // Если размеры новой создающейся комнаты превышают 6x4 - НЕ создаем стену
        if (roomWidth > MAX_FRAMELESS_WIDTH_M || roomHeight > MAX_FRAMELESS_HEIGHT_M) {
          setTimeout(() => toast.error(`Максимально можна ставити ${MAX_FRAMELESS_WIDTH_M} на ${MAX_FRAMELESS_HEIGHT_M} сендвіча. Розміри кімнати ${roomWidth.toFixed(2)}м x ${roomHeight.toFixed(2)}м перевищують ліміт.`), 0);
          return { ...state, drawingWall: null };
        }
      }

      // Проверяем размеры незамкнутого компонента (для незамкнутых стен)
      // Это нужно делать всегда, даже если есть замкнутые комнаты, чтобы не было бесконечного продления
      if (tempClosedLoops.length === 0 || !isClosedLoop) {
        // Находим незамкнутые стены в компоненте (стены, которые не входят в замкнутые контуры)
        const closedWallIds = new Set();
        tempClosedLoops.forEach(loop => {
          loop.forEach(wall => closedWallIds.add(wall.id));
        });

        const unclosedWalls = tempComponentWithNewWall.filter(wall => !closedWallIds.has(wall.id));

        if (unclosedWalls.length > 0) {
          // Проверяем каждую стену на диагональность
          const epsilon = 0.01;
          for (const wall of unclosedWalls) {
            const dx = Math.abs(wall.endPoint.x - wall.startPoint.x);
            const dy = Math.abs(wall.endPoint.y - wall.startPoint.y);

            if (dx > epsilon && dy > epsilon) {
              // Диагональная стена - запрещена
              setTimeout(() => toast.error('Стіни повинні бути тільки прямі (горизонтальні або вертикальні).'), 0);
              return { ...state, drawingWall: null };
            }
          }

          // Проверяем, что незамкнутые стены могут образовать ортогональный контур
          // Разрешаем создание комнат по частям (прямоугольники, L-образные и т.д.)
          // Запрещаем только "ступеньки" - когда стены не могут образовать валидный контур

          // Если есть замкнутые комнаты (новые или существующие), и новая стена примыкает к ним - это расширение, разрешено
          // Проверяем на "ступеньки" только если нет замкнутых комнат или если незамкнутые стены не примыкают к замкнутым
          const hasClosedRooms = tempClosedLoops.length > 0 || existingClosedLoops.length > 0;

          // Проверяем, примыкает ли новая стена к стенам из существующих закрытых комнат
          const existingClosedWallIds = new Set();
          existingClosedLoops.forEach(loop => {
            loop.forEach(wall => existingClosedWallIds.add(wall.id));
          });

          // Проверяем, есть ли среди незамкнутых стен стены из существующих закрытых комнат
          // Если есть - это расширение существующей закрытой комнаты, не нужно проверять размеры незамкнутого компонента
          const unclosedWallsFromClosedRooms = unclosedWalls.filter(wall => existingClosedWallIds.has(wall.id));
          const isExtensionOfClosedRoom = unclosedWallsFromClosedRooms.length > 0;

          if (!hasClosedRooms) {
            // Если нет замкнутых комнат, проверяем на "ступеньки" как раньше
            // Собираем все точки соединения стен
            const connectionPoints = new Map();
            unclosedWalls.forEach(wall => {
              const startKey = `${wall.startPoint.x.toFixed(3)},${wall.startPoint.y.toFixed(3)}`;
              const endKey = `${wall.endPoint.x.toFixed(3)},${wall.endPoint.y.toFixed(3)}`;

              connectionPoints.set(startKey, (connectionPoints.get(startKey) || 0) + 1);
              connectionPoints.set(endKey, (connectionPoints.get(endKey) || 0) + 1);
            });

            // В правильном ортогональном контуре должно быть:
            // - 2 точки с 1 соединением (концы незамкнутого контура)
            // - Остальные точки с 2 соединениями (углы)
            // Если есть точка с 3+ соединениями в незамкнутом контуре - это "ступенька"
            let hasInvalidConnection = false;
            connectionPoints.forEach((count, pointKey) => {
              if (count > 2) {
                hasInvalidConnection = true;
              }
            });

            if (hasInvalidConnection) {
              setTimeout(() => toast.error('Не можна створювати стіни "сходинками". Створюйте ортогональні контури (прямокутники, L-подібні форми тощо).'), 0);
              return { ...state, drawingWall: null };
            }
          }
          // Если есть замкнутые комнаты, разрешаем расширение вбок (даже если есть точки с 3+ соединениями)
          // Это нормально, когда новая стена примыкает к существующим замкнутым комнатам

          let componentMinX = Infinity, componentMinY = Infinity, componentMaxX = -Infinity, componentMaxY = -Infinity;
          unclosedWalls.forEach((wall) => {
            componentMinX = Math.min(componentMinX, wall.startPoint.x, wall.endPoint.x);
            componentMinY = Math.min(componentMinY, wall.startPoint.y, wall.endPoint.y);
            componentMaxX = Math.max(componentMaxX, wall.startPoint.x, wall.endPoint.x);
            componentMaxY = Math.max(componentMaxY, wall.startPoint.y, wall.endPoint.y);
          });

          const componentWidth = componentMaxX - componentMinX;
          const componentHeight = componentMaxY - componentMinY;

          // Проверяем общую длину всех стен в компоненте (даже если они перекрываются)
          // Это предотвращает создание слишком длинных стен при быстром кликании в одном месте
          let totalWallLength = 0;
          const processedWalls = new Set();
          unclosedWalls.forEach((wall) => {
            // Избегаем двойного подсчета перекрывающихся стен
            const wallKey = `${Math.min(wall.startPoint.x, wall.endPoint.x).toFixed(2)},${Math.min(wall.startPoint.y, wall.endPoint.y).toFixed(2)}-${Math.max(wall.startPoint.x, wall.endPoint.x).toFixed(2)},${Math.max(wall.startPoint.y, wall.endPoint.y).toFixed(2)}`;
            if (!processedWalls.has(wallKey)) {
              const wallLength = distance(wall.startPoint, wall.endPoint);
              totalWallLength += wallLength;
              processedWalls.add(wallKey);
            }
          });

          // Если общая длина стен превышает разумный лимит
          // Но если есть замкнутые комнаты, увеличиваем лимит, так как незамкнутые стены могут примыкать к ним
          const baseMaxTotalLength = Math.max(MAX_FRAMELESS_WIDTH_M, MAX_FRAMELESS_HEIGHT_M) * 2;
          const maxTotalLength = hasClosedRooms ? baseMaxTotalLength * 2 : baseMaxTotalLength;

          if (totalWallLength > maxTotalLength) {
            setTimeout(() => toast.error(`Загальна довжина стін компонента (${totalWallLength.toFixed(2)}м) перевищує ліміт. Можливо, створено дубльовані стіни.`), 0);
            return { ...state, drawingWall: null };
          }

          // Убрана проверка на совпадение ширины - теперь разрешено создавать "сходинки" (модули с разной шириной)
          // Пользователь может создавать модули с разной шириной, если это необходимо

          // Если размеры незамкнутого компонента превышают 6x4 - НЕ создаем стену
          // НО: если это расширение существующей закрытой комнаты - пропускаем проверку
          if (!isExtensionOfClosedRoom && (componentWidth > MAX_FRAMELESS_WIDTH_M || componentHeight > MAX_FRAMELESS_HEIGHT_M)) {
            setTimeout(() => toast.error(`Максимально можна ставити ${MAX_FRAMELESS_WIDTH_M} на ${MAX_FRAMELESS_HEIGHT_M} сендвіча. Розміри незамкнутого компонента ${componentWidth.toFixed(2)}м x ${componentHeight.toFixed(2)}м перевищують ліміт.`), 0);
            return { ...state, drawingWall: null };
          }

          // Если это расширение существующей закрытой комнаты - логируем для отладки
          if (isExtensionOfClosedRoom) {
            console.log('📏 [FRAMELESS] Пропущена проверка размеров незамкнутого компонента - расширение существующей закрытой комнаты:', {
              componentWidth: componentWidth.toFixed(2) + 'м',
              componentHeight: componentHeight.toFixed(2) + 'м',
              unclosedWallsFromClosedRooms: unclosedWallsFromClosedRooms.length,
              totalUnclosedWalls: unclosedWalls.length
            });
          }
        }
      }

      // Если уже есть замкнутые комнаты, проверяем что новая стена не просто продлевает существующую комнату
      // Разрешаем создавать незамкнутые стены, если они примыкают к существующим и могут образовать новую комнату
      if (existingClosedLoops.length > 0 && !isClosedLoop) {
        // Проверяем, что новая стена примыкает к существующим стенам (уже проверено выше)
        // Разрешаем создавать незамкнутые стены, если они примыкают к существующим
        // Ограничиваем только размер незамкнутого компонента до 6x4
        // Это позволит создавать комнаты по частям, но не бесконечно продлевать стены
      }

      // Проверка прямоугольности отключена в безкаркасном режиме
      // Принимаем любые замкнутые контуры

      // 6.5. Проверка ориентации: если последняя стена горизонтальная, следующая должна быть вертикальной, и наоборот
      const newDx = Math.abs(endPoint.x - startPoint.x);
      const newDy = Math.abs(endPoint.y - startPoint.y);
      const newIsHorizontal = Math.abs(newDy) < epsilon;
      const newIsVertical = Math.abs(newDx) < epsilon;

      // Проверяем ориентацию только если стена действительно горизонтальная или вертикальная
      if (newIsHorizontal || newIsVertical) {
        // Если есть последняя стена, проверяем ее ориентацию
        if (state.framelessLastWallId) {
          const lastWall = existingWalls.find(w => w.id === state.framelessLastWallId);

          if (lastWall) {
            const lastDx = Math.abs(lastWall.endPoint.x - lastWall.startPoint.x);
            const lastDy = Math.abs(lastWall.endPoint.y - lastWall.startPoint.y);
            const lastIsHorizontal = Math.abs(lastDy) < epsilon;
            const lastIsVertical = Math.abs(lastDx) < epsilon;

            // Если последняя стена горизонтальная, новая должна быть вертикальной
            if (lastIsHorizontal && newIsHorizontal) {
              setTimeout(() => toast.error('Після горизонтальної стіни наступна повинна бути вертикальною.'), 0);
              return { ...state, drawingWall: null };
            }

            // Если последняя стена вертикальная, новая должна быть горизонтальной
            if (lastIsVertical && newIsVertical) {
              setTimeout(() => toast.error('Після вертикальної стіни наступна повинна бути горизонтальною.'), 0);
              return { ...state, drawingWall: null };
            }
          }
        }
      }

      // 7. Если проверки пройдены - создаем и добавляем новую стену
      const newWall = tempWall;
      objects.push(newWall);

      // Переменные для автоматически добавленных стен (для отслеживания последней стены)
      let newHorizontalWall = null;
      let newVerticalWall = null;

      // 7.1. Автоматическое дорисовывание недостающих стен для прямоугольника
      // Если есть 2 незамкнутые стены разного направления, соединенные в углу, дорисовываем остальные 2
      // Работает как для первой комнаты, так и для последующих
      // Проверяем автозакрытие всегда, если есть незамкнутые стены (даже если есть уже замкнутые комнаты)
      {
        const allWalls = objects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);
        const componentWalls = findConnectedWalls(newWall, allWalls);

        // Находим незамкнутые стены (не входят в замкнутые контуры)
        const closedWallIds = new Set();
        const allClosedLoops = findAllClosedLoops(componentWalls);
        allClosedLoops.forEach(loop => {
          loop.forEach(wall => closedWallIds.add(wall.id));
        });

        const unclosedWalls = componentWalls.filter(wall => !closedWallIds.has(wall.id));

        // Если есть существующие закрытые комнаты, также учитываем их стены для автозакрытия
        // Это нужно для случая, когда новая комната использует общую стену с существующей закрытой комнатой
        const wallsForAutoClose = [...unclosedWalls];
        if (existingClosedLoops.length > 0 && unclosedWalls.length > 0) {
          // Добавляем стены из существующих закрытых комнат, которые соединены с незамкнутыми стенами
          const unclosedWallIds = new Set(unclosedWalls.map(w => w.id));
          const epsilon = 0.01;

          existingClosedLoops.forEach(existingLoop => {
            existingLoop.forEach(existingWall => {
              // Проверяем, соединена ли стена из существующей комнаты с незамкнутыми стенами
              const existingPoints = [existingWall.startPoint, existingWall.endPoint];
              const isConnectedToUnclosed = unclosedWalls.some(unclosedWall => {
                const unclosedPoints = [unclosedWall.startPoint, unclosedWall.endPoint];
                return existingPoints.some(ep =>
                  unclosedPoints.some(up => distance(ep, up) < epsilon)
                );
              });

              // Если стена соединена с незамкнутыми стенами, добавляем ее для проверки автозакрытия
              if (isConnectedToUnclosed && !unclosedWallIds.has(existingWall.id)) {
                wallsForAutoClose.push(existingWall);
              }
            });
          });
        }

        // Если есть 2 или больше стен для автозакрытия, проверяем, могут ли они образовать прямоугольник
        // Используем wallsForAutoClose, который включает незамкнутые стены + общие стены с существующими комнатами
        if (wallsForAutoClose.length >= 2) {
          const epsilon = 0.01;

          // Пробуем найти пару стен, которые могут образовать прямоугольник
          // Проверяем все возможные пары стен для автозакрытия
          let foundRectangle = false;

          for (let i = 0; i < wallsForAutoClose.length && !foundRectangle; i++) {
            for (let j = i + 1; j < wallsForAutoClose.length && !foundRectangle; j++) {
              const wall1 = wallsForAutoClose[i];
              const wall2 = wallsForAutoClose[j];

              // Пропускаем, если обе стены из существующих закрытых комнат (не нужны для автозакрытия)
              const wall1IsFromClosed = closedWallIds.has(wall1.id);
              const wall2IsFromClosed = closedWallIds.has(wall2.id);
              if (wall1IsFromClosed && wall2IsFromClosed) {
                continue;
              }

              // Определяем направления
              const dx1 = Math.abs(wall1.endPoint.x - wall1.startPoint.x);
              const dy1 = Math.abs(wall1.endPoint.y - wall1.startPoint.y);
              const dx2 = Math.abs(wall2.endPoint.x - wall2.startPoint.x);
              const dy2 = Math.abs(wall2.endPoint.y - wall2.startPoint.y);

              const isWall1Horizontal = dy1 < epsilon;
              const isWall2Horizontal = dy2 < epsilon;

              // Стены должны быть разного направления
              if (isWall1Horizontal !== isWall2Horizontal) {
                // Находим точку соединения (угол)
                let cornerPoint = null;
                const points1 = [wall1.startPoint, wall1.endPoint];
                const points2 = [wall2.startPoint, wall2.endPoint];

                for (const p1 of points1) {
                  for (const p2 of points2) {
                    if (distance(p1, p2) < epsilon) {
                      cornerPoint = p1;
                      break;
                    }
                  }
                  if (cornerPoint) break;
                }

                if (cornerPoint) {
                  // Определяем свободные концы стен
                  const freeEnd1 = points1.find(p => distance(p, cornerPoint) >= epsilon);
                  const freeEnd2 = points2.find(p => distance(p, cornerPoint) >= epsilon);

                  if (freeEnd1 && freeEnd2) {
                    // Определяем, какая стена горизонтальная, какая вертикальная
                    let horizontalWall, verticalWall, horizontalFreeEnd, verticalFreeEnd;

                    if (isWall1Horizontal) {
                      horizontalWall = wall1;
                      verticalWall = wall2;
                      horizontalFreeEnd = freeEnd1;
                      verticalFreeEnd = freeEnd2;
                    } else {
                      horizontalWall = wall2;
                      verticalWall = wall1;
                      horizontalFreeEnd = freeEnd2;
                      verticalFreeEnd = freeEnd1;
                    }

                    // Если есть 3 стены для автозакрытия, проверяем, может ли третья стена быть частью прямоугольника
                    if (wallsForAutoClose.length === 3) {
                      const wall3 = wallsForAutoClose.find(w => w.id !== wall1.id && w.id !== wall2.id);
                      if (wall3) {
                        const dx3 = Math.abs(wall3.endPoint.x - wall3.startPoint.x);
                        const dy3 = Math.abs(wall3.endPoint.y - wall3.startPoint.y);
                        const isWall3Horizontal = dy3 < epsilon;

                        // Проверяем все возможные соединения третьей стены
                        const points3 = [wall3.startPoint, wall3.endPoint];
                        const connectsToCorner = points3.some(p => distance(p, cornerPoint) < epsilon);
                        const connectsToFreeEnd1 = points3.some(p => distance(p, freeEnd1) < epsilon);
                        const connectsToFreeEnd2 = points3.some(p => distance(p, freeEnd2) < epsilon);

                        // Находим свободный конец третьей стены
                        const freeEnd3 = points3.find(p => {
                          const distToCorner = distance(p, cornerPoint);
                          const distToFree1 = distance(p, freeEnd1);
                          const distToFree2 = distance(p, freeEnd2);
                          return distToCorner >= epsilon && distToFree1 >= epsilon && distToFree2 >= epsilon;
                        });

                        // Если третья стена соединяется с одним из свободных концов и параллельна соответствующей стене
                        if ((connectsToFreeEnd1 && isWall3Horizontal === isWall1Horizontal) ||
                          (connectsToFreeEnd2 && isWall3Horizontal === isWall2Horizontal)) {
                          if (freeEnd3) {
                            // Определяем координаты четвертого угла прямоугольника
                            let fourthCorner;
                            if (connectsToFreeEnd1) {
                              // Третья стена соединяется с freeEnd1, нужно найти четвертый угол от freeEnd2 и freeEnd3
                              if (isWall1Horizontal) {
                                fourthCorner = { x: freeEnd2.x, y: freeEnd3.y };
                              } else {
                                fourthCorner = { x: freeEnd3.x, y: freeEnd2.y };
                              }
                            } else {
                              // Третья стена соединяется с freeEnd2
                              if (isWall2Horizontal) {
                                fourthCorner = { x: freeEnd1.x, y: freeEnd3.y };
                              } else {
                                fourthCorner = { x: freeEnd3.x, y: freeEnd1.y };
                              }
                            }

                            // Дорисовываем только одну недостающую стену (4-ю сторону)
                            if (isWall3Horizontal) {
                              newVerticalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            } else {
                              newHorizontalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            }
                          }
                        } else if (connectsToCorner && freeEnd3) {
                          // Третья стена соединяется с углом (cornerPoint), образуя L-образную форму
                          // В этом случае нужно найти, какая стена отсутствует
                          // Проверяем, параллельна ли третья стена одной из первых двух
                          if (isWall3Horizontal === isWall1Horizontal) {
                            // Третья стена параллельна wall1, нужно дорисовать стену от freeEnd3 до freeEnd2
                            const fourthCorner = { x: freeEnd2.x, y: freeEnd3.y };
                            if (isWall3Horizontal) {
                              newVerticalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            } else {
                              newHorizontalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            }
                          } else if (isWall3Horizontal === isWall2Horizontal) {
                            // Третья стена параллельна wall2, нужно дорисовать стену от freeEnd3 до freeEnd1
                            const fourthCorner = { x: freeEnd1.x, y: freeEnd3.y };
                            if (isWall3Horizontal) {
                              newVerticalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            } else {
                              newHorizontalWall = createFramelessWallWithSegments(freeEnd3, fourthCorner);
                            }
                          }
                        }
                      }
                    } else {
                      // Случай с 2 стенами - дорисовываем 2 недостающие стены
                      // Определяем координаты четвертого угла прямоугольника
                      // Четвертый угол: (horizontalFreeEnd.x, verticalFreeEnd.y)
                      const fourthCorner = { x: horizontalFreeEnd.x, y: verticalFreeEnd.y };

                      // Дорисовываем недостающие 2 стены:
                      // 1. Горизонтальную стену от вертикального свободного конца до четвертого угла
                      // 2. Вертикальную стену от горизонтального свободного конца до четвертого угла

                      newHorizontalWall = createFramelessWallWithSegments(
                        verticalFreeEnd,
                        fourthCorner
                      );

                      newVerticalWall = createFramelessWallWithSegments(
                        horizontalFreeEnd,
                        fourthCorner
                      );
                    }

                    // Если нашли прямоугольник, выходим из циклов
                    if (newHorizontalWall || newVerticalWall) {
                      foundRectangle = true;

                      // Проверяем, не дублируют ли новые стены существующие
                      const checkWallDuplicate = (wallToCheck) => {
                        if (!wallToCheck) return true;

                        for (const existingWall of objects.filter(o => o.type === OBJECT_TYPES.FRAMELESS_WALL)) {
                          const checkDx = Math.abs(wallToCheck.endPoint.x - wallToCheck.startPoint.x);
                          const checkDy = Math.abs(wallToCheck.endPoint.y - wallToCheck.startPoint.y);
                          const existingDx = Math.abs(existingWall.endPoint.x - existingWall.startPoint.x);
                          const existingDy = Math.abs(existingWall.endPoint.y - existingWall.startPoint.y);

                          const checkIsHorizontal = Math.abs(checkDy) < epsilon;
                          const checkIsVertical = Math.abs(checkDx) < epsilon;
                          const existingIsHorizontal = Math.abs(existingDy) < epsilon;
                          const existingIsVertical = Math.abs(existingDx) < epsilon;

                          if ((checkIsHorizontal && existingIsHorizontal) || (checkIsVertical && existingIsVertical)) {
                            let onSameLine = false;

                            if (checkIsHorizontal && existingIsHorizontal) {
                              if (Math.abs(wallToCheck.startPoint.y - existingWall.startPoint.y) < epsilon) {
                                onSameLine = true;
                              }
                            } else if (checkIsVertical && existingIsVertical) {
                              if (Math.abs(wallToCheck.startPoint.x - existingWall.startPoint.x) < epsilon) {
                                onSameLine = true;
                              }
                            }

                            if (onSameLine) {
                              let checkMin, checkMax, existingMin, existingMax;

                              if (checkIsHorizontal) {
                                checkMin = Math.min(wallToCheck.startPoint.x, wallToCheck.endPoint.x);
                                checkMax = Math.max(wallToCheck.startPoint.x, wallToCheck.endPoint.x);
                                existingMin = Math.min(existingWall.startPoint.x, existingWall.endPoint.x);
                                existingMax = Math.max(existingWall.startPoint.x, existingWall.endPoint.x);
                              } else {
                                checkMin = Math.min(wallToCheck.startPoint.y, wallToCheck.endPoint.y);
                                checkMax = Math.max(wallToCheck.startPoint.y, wallToCheck.endPoint.y);
                                existingMin = Math.min(existingWall.startPoint.y, existingWall.endPoint.y);
                                existingMax = Math.max(existingWall.startPoint.y, existingWall.endPoint.y);
                              }

                              const overlapMin = Math.max(checkMin, existingMin);
                              const overlapMax = Math.min(checkMax, existingMax);

                              if (overlapMax - overlapMin > epsilon) {
                                const checkLength = checkMax - checkMin;
                                const existingLength = existingMax - existingMin;
                                const overlapLength = overlapMax - overlapMin;

                                // Если стены полностью совпадают или одна полностью перекрывает другую
                                if ((Math.abs(checkLength - existingLength) < epsilon &&
                                  Math.abs(checkMin - existingMin) < epsilon) ||
                                  (overlapLength >= existingLength - epsilon && checkLength >= existingLength - epsilon) ||
                                  (overlapLength >= checkLength - epsilon && existingLength >= checkLength - epsilon)) {
                                  return true; // Дубликат
                                }
                              }
                            }
                          }
                        }
                        return false;
                      };

                      // Добавляем найденные стены
                      if (newHorizontalWall) {
                        if (!checkWallDuplicate(newHorizontalWall)) {
                          objects.push(newHorizontalWall);
                        }
                      }

                      if (newVerticalWall) {
                        if (!checkWallDuplicate(newVerticalWall)) {
                          objects.push(newVerticalWall);
                        }
                      }

                      // Проверяем, были ли добавлены стены
                      const addedWalls = objects.filter(o => o.type === OBJECT_TYPES.FRAMELESS_WALL &&
                        (o.id === newHorizontalWall?.id || o.id === newVerticalWall?.id));

                      if (addedWalls.length > 0) {
                        // Обновляем isClosedLoop после добавления стен
                        const updatedWalls = objects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);
                        const updatedComponent = findConnectedWalls(newWall, updatedWalls);
                        const updatedClosedLoops = findAllClosedLoops(updatedComponent);
                        if (updatedClosedLoops.length > 0) {
                          isClosedLoop = true;
                          // Автозакрытие комнаты после дорисовки стен
                        }
                      }

                      // Выходим из циклов после обработки найденного прямоугольника
                      break;
                    }
                  }
                }
              }
            }
            if (foundRectangle) break;
          }
        }
      }

      // Комната считается замкнутой, если найдены новые замкнутые контуры
      const isClosed = hasNewClosedLoops || isClosedLoop;

      // Логика перегородок отключена в безкаркасном режиме

      // Определяем последнюю стену для отслеживания
      // Если были добавлены автоматические стены, последней считается вертикальная (добавляется последней)
      let lastWallId = null;
      if (!isClosed && newWall) {
        // Проверяем, были ли добавлены автоматические стены
        const addedAutoWalls = objects.filter(o => o.type === OBJECT_TYPES.FRAMELESS_WALL &&
          (o.id === newHorizontalWall?.id || o.id === newVerticalWall?.id));

        if (addedAutoWalls.length > 0) {
          // Если были добавлены автоматические стены, последней считается вертикальная
          if (newVerticalWall && objects.some(o => o.id === newVerticalWall.id)) {
            lastWallId = newVerticalWall.id;
          } else if (newHorizontalWall && objects.some(o => o.id === newHorizontalWall.id)) {
            lastWallId = newHorizontalWall.id;
          } else {
            lastWallId = newWall.id;
          }
        } else {
          // Если автоматических стен не было, последней считается новая стена
          lastWallId = newWall.id;
        }
      }

      if (isClosed) {
        // 🎉 ЛОГ: Комната создана/замкнута
        const finalWalls = objects.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);
        const finalComponent = findConnectedWalls(newWall, finalWalls);
        const finalClosedLoops = findAllClosedLoops(finalComponent);

        setTimeout(() => toast.success("Контур замкнено! Модуль створено."), 0);
      } else {
        // Стена добавлена, но комната не замкнута
      }

      return {
        ...state,
        projectObjects: { ...state.projectObjects, [currentFloorId]: objects },
        drawingWall: null,
        selectedObjectId: newWall ? newWall.id : state.selectedObjectId,
        framelessElementsUnlocked: state.framelessElementsUnlocked || isClosed,
        // Если комната замкнута - сбрасываем последнюю стену, иначе сохраняем
        framelessLastWallId: isClosed ? null : lastWallId,
      };
    }
    // --- КОНЕЦ: ИЗМЕНЕННАЯ ЛОГИКА ---


    case "ADD_FRAME_STRUCTURE": {
      const { frameConfig } = action.payload;
      const newFrameId = generateId("frame_");

      const gridWidth = frameConfig.sandwichCountWidth || 1;
      const gridLength = frameConfig.sandwichCountLength || 1;
      const width = gridWidth * FRAMELESS_GRID_CELL_SIZE_M;
      const length = gridLength * FRAMELESS_GRID_CELL_SIZE_M;

      const perimeterWalls = createFramePerimeter(gridWidth, gridLength);

      const newFrame = {
        ...frameConfig,
        id: newFrameId,
        type: OBJECT_TYPES.FRAME_STRUCTURE,
        x: frameConfig.posX || 0,
        y: frameConfig.posY || 0,
        width,
        length,
        gridWidth,
        gridLength,
        rotation: frameConfig.rotation || 0,
        perimeterWalls,
        internalPartitions: {},
        canRemoveSection: false,
        svgUrl: frameConfig.svgUrl || '',
        kpUrl: frameConfig.kpUrl || '',
        price: frameConfig.price || 0,
      };

      nextStateObjects = [...currentFloorObjects, newFrame];
      newSelectedObjectId = newFrameId;
      break;
    }

    case "ADD_MODULE": {
      needsRecalculation = true;
      needsValidation = true;
      const { moduleConfig } = action.payload;
      const existingModules = currentFloorObjects.filter(
        (obj) => obj.type === OBJECT_TYPES.MODULE,
      );
      const firstModule =
        existingModules.length > 0 ? existingModules[0] : null;
      const newModuleRotation = moduleConfig.rotation || 0;
      const currentCellsWide =
        parseInt(moduleConfig.cellsWide) || DEFAULT_MODULE_CELLS_WIDE;
      const currentCellsLong =
        parseInt(moduleConfig.cellsLong) || DEFAULT_MODULE_CELLS_LONG;
      const newModuleId = generateId("mod_");

      // Проверяем, нужно ли пересчитывать wallSegments из-за изменения ориентации
      let finalWallSegments;
      if (moduleConfig.oldModule && moduleConfig.oldModule.wallSegments) {
        const oldRotation = moduleConfig.oldModule.rotation || 0;
        const oldCellsWide = parseInt(moduleConfig.oldModule.cellsWide) || DEFAULT_MODULE_CELLS_WIDE;
        const oldCellsLong = parseInt(moduleConfig.oldModule.cellsLong) || DEFAULT_MODULE_CELLS_LONG;
        // Определяем вертикальность по rotation ИЛИ по соотношению размеров
        // Модуль вертикальный, если rotation=90/270 ИЛИ если cellsLong > cellsWide
        const oldIsVerticalByRotation = Math.abs(oldRotation % 180) === 90;
        const oldIsVerticalBySize = oldCellsLong > oldCellsWide;
        const oldIsVertical = oldIsVerticalByRotation || oldIsVerticalBySize;

        const newIsVerticalByRotation = Math.abs(newModuleRotation % 180) === 90;
        const newIsVerticalBySize = currentCellsLong > currentCellsWide;
        const newIsVertical = newIsVerticalByRotation || newIsVerticalBySize;

        // Подсчитываем количество элементов в старых сегментах
        let totalOldElements = 0;
        for (const segment of Object.values(moduleConfig.oldModule.wallSegments)) {
          if (segment.elements && Array.isArray(segment.elements)) {
            totalOldElements += segment.elements.length;
          }
        }

        // Debug logging removed for production

        // Если ориентация изменилась, пересчитываем сегменты с сохранением элементов
        if (oldIsVertical !== newIsVertical) {
          const oldModule = {
            ...moduleConfig.oldModule,
            rotation: oldRotation,
            cellsWide: oldCellsWide,
            cellsLong: oldCellsLong,
            wallSegments: moduleConfig.oldModule.wallSegments,
          };
          const recalculatedModule = recalculateWallSegmentsAfterRotation(
            oldModule,
            oldRotation,
            newModuleRotation
          );
          finalWallSegments = recalculatedModule.wallSegments;

          // Подсчитываем количество элементов в новых сегментах
          let totalNewElements = 0;
          for (const segment of Object.values(finalWallSegments)) {
            if (segment.elements && Array.isArray(segment.elements)) {
              totalNewElements += segment.elements.length;
            }
          }

          // Debug logging removed for production
        } else if (oldCellsWide !== currentCellsWide || oldCellsLong !== currentCellsLong) {
          // Ориентация не изменилась, но размеры изменились - создаем новые сегменты
          // (элементы будут потеряны, но это редкий случай)
          if (process.env.NODE_ENV !== "production") {
            console.warn(`[ADD_MODULE] Size changed but orientation didn't, elements will be lost`);
          }
          finalWallSegments = createInitialWallSegments({
            ...moduleConfig,
            cellsWide: currentCellsWide,
            cellsLong: currentCellsLong,
          });
        } else {
          // Ориентация и размеры не изменились, используем старые сегменты (глубокая копия)
          finalWallSegments = JSON.parse(JSON.stringify(moduleConfig.oldModule.wallSegments));
          // Debug logging removed for production
        }
      } else {
        // Нет старого модуля - используем wallSegments из конфигурации шаблона, если они есть
        if (moduleConfig.wallSegments && Object.keys(moduleConfig.wallSegments).length > 0) {
          // Используем wallSegments из шаблона (глубокая копия)
          finalWallSegments = JSON.parse(JSON.stringify(moduleConfig.wallSegments));
          // Генерируем новые ID для сегментов
          for (const key in finalWallSegments) {
            if (finalWallSegments[key]) {
              finalWallSegments[key].id = generateId("wall_");
              // Генерируем новые ID для элементов
              if (finalWallSegments[key].elements && Array.isArray(finalWallSegments[key].elements)) {
                finalWallSegments[key].elements = finalWallSegments[key].elements.map(el => ({
                  ...el,
                  id: generateId("el_")
                }));
              }
            }
          }
        } else {
          // Нет wallSegments в шаблоне, создаем новые пустые сегменты
          finalWallSegments = createInitialWallSegments({
            ...moduleConfig,
            cellsWide: currentCellsWide,
            cellsLong: currentCellsLong,
          });
        }
      }

      const newModule = {
        ...moduleConfig,
        id: newModuleId,
        templateId: moduleConfig.id,
        type: OBJECT_TYPES.MODULE,
        subtype: moduleConfig.subtype,
        label: moduleConfig.label,
        x: moduleConfig.posX,
        y: moduleConfig.posY,
        cellsWide: currentCellsWide,
        cellsLong: currentCellsLong,
        width: currentCellsWide * GRID_CELL_SIZE_M,
        height: currentCellsLong * GRID_CELL_SIZE_M,
        rotation: newModuleRotation,
        wallSegments: finalWallSegments,
        freeElements: moduleConfig.freeElements || [],
      };

      if (currentFloorIndex > 0) {
        const prevFloorId = state.floors[currentFloorIndex - 1].id;
        const ghostModules = (state.projectObjects[prevFloorId] || []).filter(
          (o) => o.type === OBJECT_TYPES.MODULE,
        );
        if (ghostModules.length === 0) {
          setTimeout(() => toast.error(t("toasts.noModulesBelow")), 0);
          return state;
        }
        const vertices = getModuleVertices(newModule);
        const isSupported = vertices.every((vertex) =>
          ghostModules.some((ghostModule) =>
            isPointInModule(vertex, ghostModule),
          ),
        );
        if (!isSupported) {
          setTimeout(() => toast.error(t("toasts.modulePlacementError")), 0);
          return state;
        }
      }

      (moduleConfig.predefinedElements || []).forEach((el) => {
        const wall = newModule.wallSegments[el.segmentKey];
        if (wall) {
          const newElement = {
            id: generateId("el_"),
            type: el.type,
            ...el.properties,
            ...defaultObjectSizes[el.type],
          };
          wall.elements.push(newElement);
        }
      });

      nextStateObjects = [...currentFloorObjects, newModule];
      newSelectedObjectId = newModuleId;
      break;
    }

    case "ADD_PRE_DERIVED_STAIRS": {
      const { ghostModule, template } = action.payload;
      const config = template.config;

      if (
        currentFloorObjects.some(
          (obj) => obj.id === `derived_${ghostModule.id}`,
        )
      ) {
        return state;
      }

      const newStairsModule = {
        id: `derived_${ghostModule.id}`,
        type: OBJECT_TYPES.MODULE,
        templateId: config.id,
        x: ghostModule.x,
        y: ghostModule.y,
        cellsWide: config.cellsWide,
        cellsLong: config.cellsLong,
        width: config.cellsWide * GRID_CELL_SIZE_M,
        height: config.cellsLong * GRID_CELL_SIZE_M,
        rotation: ghostModule.rotation || 0,
        wallSegments: createInitialWallSegments(config),
        freeElements: config.freeElements || [],
        subtype: config.subtype,
        label: config.label,
      };

      nextStateObjects = [...currentFloorObjects, newStairsModule];
      needsRecalculation = true;
      break;
    }

    case "ADD_ELEMENT_TO_WALL": {
      needsValidation = true;
      const {
        elementType,
        wallSegmentId,
        positionOnSegment,
        clickSide,
        segmentIndex,
      } = action.payload;


      const tempObjects = JSON.parse(JSON.stringify(currentFloorObjects));
      const newElementId = generateId("el_");
      const newElement = {
        id: newElementId,
        type: elementType,
        positionOnSegment: positionOnSegment ?? 0.5,
        ...defaultObjectSizes[elementType],
        placementSide: 1,
        segmentIndex: segmentIndex ?? null,
      };

      if (clickSide !== null && clickSide !== undefined) {
        newElement.placementSide = clickSide;
      } else {
        if (elementType === OBJECT_TYPES.DOOR) {
          const openingDirection = newElement.openingDirection || "outward";
          newElement.placementSide = openingDirection === "outward" ? -1 : 1;
        } else {
          newElement.placementSide = 1;
        }
      }

      if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
        if (clickSide === null || clickSide === undefined) {
          newElement.placementSide = 1;
        }
      }
      let elementAdded = false;
      let restrictionFailed = false;

      const isLargeOpeningElement = (elType) =>
        elType === OBJECT_TYPES.DOOR ||
        elType === OBJECT_TYPES.GATE ||
        elType === OBJECT_TYPES.WINDOW ||
        elType === OBJECT_TYPES.PANORAMIC_WINDOW;

      // Функция для получения абсолютных координат сегмента стены
      const getSegmentAbsoluteCoords = (wall, segment) => {
        const wallLength = distance(wall.startPoint, wall.endPoint);
        if (wallLength === 0) return null;

        const wallVector = {
          x: wall.endPoint.x - wall.startPoint.x,
          y: wall.endPoint.y - wall.startPoint.y
        };
        const normalizedVector = {
          x: wallVector.x / wallLength,
          y: wallVector.y / wallLength
        };

        const segmentStart = segment.start || 0;
        const segmentLength = segment.length || 0;
        const segmentEnd = segmentStart + segmentLength;

        const absoluteStart = {
          x: wall.startPoint.x + normalizedVector.x * segmentStart,
          y: wall.startPoint.y + normalizedVector.y * segmentStart
        };
        const absoluteEnd = {
          x: wall.startPoint.x + normalizedVector.x * segmentEnd,
          y: wall.startPoint.y + normalizedVector.y * segmentEnd
        };

        return { start: absoluteStart, end: absoluteEnd };
      };

      // Функция для проверки реального пересечения отрезков (не просто касание в одной точке)
      const segmentsReallyIntersect = (seg1Start, seg1End, seg2Start, seg2End, tolerance = 0.01) => {
        // Проверяем пересечение отрезков (исключая конечные точки)
        const intersects = lineSegmentsIntersect(seg1Start, seg1End, seg2Start, seg2End);
        if (intersects) {
          return true;
        }

        // Проверяем касание в конечных точках - для панорамных окон это запрещено
        // Проверяем, лежит ли точка на отрезке (включая конечные точки)
        const isPointOnSegment = (point, segStart, segEnd) => {
          const segLength = distance(segStart, segEnd);
          if (segLength < tolerance) {
            // Если отрезок очень короткий, проверяем просто расстояние до точки
            return distance(point, segStart) < tolerance;
          }

          const distToStart = distance(point, segStart);
          const distToEnd = distance(point, segEnd);
          // Проверяем, лежит ли точка на отрезке (включая конечные точки)
          const isOnSegment = Math.abs(distToStart + distToEnd - segLength) < tolerance;
          return isOnSegment;
        };

        // Проверяем, касается ли сегмент стены перегородки в любой точке
        // Это включает случай, когда конечная точка сегмента стены лежит на перегородке
        const seg1StartOnSeg2 = isPointOnSegment(seg1Start, seg2Start, seg2End);
        const seg1EndOnSeg2 = isPointOnSegment(seg1End, seg2Start, seg2End);
        const seg2StartOnSeg1 = isPointOnSegment(seg2Start, seg1Start, seg1End);
        const seg2EndOnSeg1 = isPointOnSegment(seg2End, seg1Start, seg1End);

        // Если какая-либо точка сегмента стены лежит на перегородке или наоборот, это считается пересечением
        if (seg1StartOnSeg2 || seg1EndOnSeg2 || seg2StartOnSeg1 || seg2EndOnSeg1) {
          return true;
        }

        // Проверяем, что сегмент стены проходит через перегородку (не просто касается в одной точке)
        // Это означает, что внутренние точки сегмента стены должны лежать на перегородке
        const seg1Mid = {
          x: (seg1Start.x + seg1End.x) / 2,
          y: (seg1Start.y + seg1End.y) / 2
        };

        // Проверяем, лежит ли средняя точка сегмента стены на перегородке
        const seg1Length = distance(seg1Start, seg1End);
        const seg2Length = distance(seg2Start, seg2End);
        if (seg1Length < tolerance || seg2Length < tolerance) {
          return false;
        }

        // Проверяем, лежит ли средняя точка сегмента стены на перегородке
        const distToSeg2Start = distance(seg1Mid, seg2Start);
        const distToSeg2End = distance(seg1Mid, seg2End);
        const seg1MidOnSeg2 = Math.abs(distToSeg2Start + distToSeg2End - seg2Length) < tolerance;

        // Также проверяем среднюю точку перегородки на сегменте стены
        const seg2Mid = {
          x: (seg2Start.x + seg2End.x) / 2,
          y: (seg2Start.y + seg2End.y) / 2
        };
        const distToSeg1Start = distance(seg2Mid, seg1Start);
        const distToSeg1End = distance(seg2Mid, seg1End);
        const seg2MidOnSeg1 = Math.abs(distToSeg1Start + distToSeg1End - seg1Length) < tolerance;

        return seg1MidOnSeg2 || seg2MidOnSeg1;
      };

      // Функция для проверки пересечения перегородки с сегментом стены
      const checkPartitionIntersection = (wall, segment) => {
        const segmentCoords = getSegmentAbsoluteCoords(wall, segment);
        if (!segmentCoords) {
          return false;
        }

        const allPartitions = tempObjects.filter(obj =>
          obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && obj.id !== wall.id
        );


        for (const partition of allPartitions) {

          if (partition.startPoint && partition.endPoint) {
            // Проверяем реальное пересечение (не просто касание в одной точке)
            const hasRealIntersection = segmentsReallyIntersect(
              segmentCoords.start,
              segmentCoords.end,
              partition.startPoint,
              partition.endPoint
            );


            if (hasRealIntersection) {
              return true;
            }
          }
          // No intersection found
        }

        return false;
      };

      const tryAddElementToFramelessWall = (wall) => {

        if (
          wall.type !== OBJECT_TYPES.FRAMELESS_WALL &&
          wall.type !== OBJECT_TYPES.FRAMELESS_PARTITION
        ) {
          return false;
        }

        // Нельзя размещать панорамные окна и ворота на перегородках
        if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
            const errorMessage = "Не можна розміщувати панорамне вікно на перегородці";
            setTimeout(() => toast.warn(errorMessage), 0);
            restrictionFailed = true; // Устанавливаем внешнюю переменную
            return false;
          }
          if (elementType === OBJECT_TYPES.GATE) {
            const errorMessage = "Не можна розміщувати ворота на перегородці";
            setTimeout(() => toast.warn(errorMessage), 0);
            restrictionFailed = true; // Устанавливаем внешнюю переменную
            return false;
          }
        }

        // Автоматически определяем placementSide на основе геометрии стены
        // Элемент должен всегда смотреть внутрь комнаты
        if (elementType !== OBJECT_TYPES.DOOR) {
          const allWallsOnFloor = tempObjects.filter(obj =>
            obj.type === OBJECT_TYPES.FRAMELESS_WALL ||
            obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
          );

          let component = [];
          if (allWallsOnFloor.length > 0) {
            component = findConnectedWalls(wall, allWallsOnFloor);
          }

          if (component.length > 0) {
            // Находим все замкнутые контуры (комнаты), которые содержат эту стену
            const closedLoops = findAllClosedLoops(component);
            const roomsWithThisWall = closedLoops.filter(loop =>
              loop.some(w => w.id === wall.id)
            );

            let centroid;
            if (roomsWithThisWall.length > 0) {
              // Если стена входит в комнату, используем centroid самой маленькой комнаты
              // (самая маленькая комната - это та, к которой непосредственно принадлежит стена)
              const smallestRoom = roomsWithThisWall.reduce((smallest, current) => {
                const smallestBounds = smallest.reduce((bounds, w) => {
                  bounds.minX = Math.min(bounds.minX, w.startPoint.x, w.endPoint.x);
                  bounds.minY = Math.min(bounds.minY, w.startPoint.y, w.endPoint.y);
                  bounds.maxX = Math.max(bounds.maxX, w.startPoint.x, w.endPoint.x);
                  bounds.maxY = Math.max(bounds.maxY, w.startPoint.y, w.endPoint.y);
                  return bounds;
                }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

                const currentBounds = current.reduce((bounds, w) => {
                  bounds.minX = Math.min(bounds.minX, w.startPoint.x, w.endPoint.x);
                  bounds.minY = Math.min(bounds.minY, w.startPoint.y, w.endPoint.y);
                  bounds.maxX = Math.max(bounds.maxX, w.startPoint.x, w.endPoint.x);
                  bounds.maxY = Math.max(bounds.maxY, w.startPoint.y, w.endPoint.y);
                  return bounds;
                }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

                const smallestArea = (smallestBounds.maxX - smallestBounds.minX) * (smallestBounds.maxY - smallestBounds.minY);
                const currentArea = (currentBounds.maxX - currentBounds.minX) * (currentBounds.maxY - currentBounds.minY);

                return currentArea < smallestArea ? current : smallest;
              });

              // Вычисляем centroid самой маленькой комнаты
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              smallestRoom.forEach(w => {
                minX = Math.min(minX, w.startPoint.x, w.endPoint.x);
                minY = Math.min(minY, w.startPoint.y, w.endPoint.y);
                maxX = Math.max(maxX, w.startPoint.x, w.endPoint.x);
                maxY = Math.max(maxY, w.startPoint.y, w.endPoint.y);
              });
              centroid = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
            } else {
              // Если стена не входит ни в одну комнату (внешняя стена), используем centroid всего компонента
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              component.forEach(w => {
                minX = Math.min(minX, w.startPoint.x, w.endPoint.x);
                minY = Math.min(minY, w.startPoint.y, w.endPoint.y);
                maxX = Math.max(maxX, w.startPoint.x, w.endPoint.x);
                maxY = Math.max(maxY, w.startPoint.y, w.endPoint.y);
              });
              centroid = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
            }

            const wallMidPoint = {
              x: (wall.startPoint.x + wall.endPoint.x) / 2,
              y: (wall.startPoint.y + wall.endPoint.y) / 2,
            };

            const wallVector = {
              x: wall.endPoint.x - wall.startPoint.x,
              y: wall.endPoint.y - wall.startPoint.y
            };
            const wallAngle = Math.atan2(wallVector.y, wallVector.x) * (180 / Math.PI);
            const localYDir = {
              x: -Math.sin(wallAngle * Math.PI / 180),
              y: Math.cos(wallAngle * Math.PI / 180)
            };
            const centroidVector = {
              x: centroid.x - wallMidPoint.x,
              y: centroid.y - wallMidPoint.y
            };

            const dot = centroidVector.x * localYDir.x + centroidVector.y * localYDir.y;

            // Устанавливаем placementSide так, чтобы элемент смотрел внутрь комнаты
            newElement.placementSide = dot >= 0 ? 1 : -1;
          }
        }

        if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          ensureFramelessPartitionSegments(wall);
        }

        const segment = wall.segments?.find((seg) => seg.id === wallSegmentId);
        if (!segment) {
          return false;
        }

        // Вычисляем правильную позицию элемента на сегменте
        if (!newElement.positionOnSegment) {
          // Если позиция не указана, размещаем по центру сегмента
          newElement.positionOnSegment = 0.5;
        }

        // Для перегородок нужно учесть индекс сегмента
        if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          const segmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
          if (segmentIndex !== -1) {
            // Сохраняем информацию о сегменте для правильного размещения
            const numSegments = wall.segments.length;
            const segmentStart = segmentIndex / numSegments;
            const segmentEnd = (segmentIndex + 1) / numSegments;

            // Позиция элемента внутри всей перегородки
            const globalPosition = segmentStart + (newElement.positionOnSegment * (segmentEnd - segmentStart));

            // Обновляем позицию для корректного отображения
            newElement.globalPositionOnWall = globalPosition;
            newElement.segmentIndex = segmentIndex;
          }
        }

        if (!segment.elements) {
          segment.elements = [];
        }

        const elementsOnSegment = segment.elements;

        if (process.env.NODE_ENV !== "production") {
          console.log("[Reducer][ADD_ELEMENT_TO_WALL] Target frameless segment", {
            wallId: wall.id,
            segmentId: segment.id,
            segmentStart: segment.start,
            segmentLength: segment.length,
            existingElementCount: elementsOnSegment.length,
          });
        }

        // --- NEW CHECK: Door on occupied sandwich ---
        if (elementType === OBJECT_TYPES.DOOR && elementsOnSegment.length > 0) {
          const errorMessage = t("toasts.doorPlacementOnOccupiedSandwichError");
          setTimeout(() => toast.warn(errorMessage), 0);
          restrictionFailed = true;
          return false;
        }
        // --------------------------------------------

        // Проверки лимитов элементов (как в модульном режиме)
        if (ELECTRICAL_ELEMENT_TYPES.includes(elementType)) {
          const existingElectricalElements = elementsOnSegment.filter(el =>
            ELECTRICAL_ELEMENT_TYPES.includes(el.type)
          );

          if (existingElectricalElements.length >= ELEMENT_GROUP_LIMITS.ELECTRICAL) {
            const errorMessage = `Максимум ${ELEMENT_GROUP_LIMITS.ELECTRICAL} електричних елементів в сумі на сегмент стіни`;
            setTimeout(() => toast.warn(errorMessage), 0);
            restrictionFailed = true;
            return false;
          }
        }

        if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
          const existingSanitaryElements = elementsOnSegment.filter(el =>
            SANITARY_ELEMENT_TYPES.includes(el.type)
          );

          if (existingSanitaryElements.length >= ELEMENT_GROUP_LIMITS.SANITARY) {
            const errorMessage = t("toasts.sanitaryElementOnePerSandwichError");
            setTimeout(() => toast.warn(errorMessage), 0);
            restrictionFailed = true;
            return false;
          }
        } else if (!CLIMATE_ELEMENT_TYPES.includes(elementType)) {
          const elementLimit = ELEMENT_LIMITS_PER_WALL_SEGMENT[elementType];
          if (elementLimit && !ELECTRICAL_ELEMENT_TYPES.includes(elementType)) {
            const existingElementsOfSameType = elementsOnSegment.filter(el =>
              el.type === elementType
            );

            if (existingElementsOfSameType.length >= elementLimit) {
              let errorMessage;
              if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
                errorMessage = t("toasts.sanitaryElementOnePerWallError");
              } else if (elementLimit === 1) {
                errorMessage = t("toasts.elementLimitOnePerWallError");
              } else {
                errorMessage = `Максимум ${elementLimit} елементів типу "${elementType}" на сегмент стіни`;
              }
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }
          }
        }

        if (CLIMATE_ELEMENT_TYPES.includes(elementType)) {
          // Для ролеты проверяем не только placementSide, но и позицию - ролета должна быть на том же месте, что и окно
          if (elementType === OBJECT_TYPES.WINDOW_BLINDS) {
            // Проверяем, есть ли уже ролета на той же позиции (с небольшой погрешностью)
            const positionTolerance = 0.1; // 10% от длины сегмента
            const existingBlindsAtSamePosition = elementsOnSegment.filter(el =>
              el.type === OBJECT_TYPES.WINDOW_BLINDS &&
              Math.abs((el.positionOnSegment || 0.5) - (newElement.positionOnSegment || 0.5)) < positionTolerance
            );

            if (existingBlindsAtSamePosition.length >= 1) {
              setTimeout(() => toast.warn("На цьому місці вже є ролета"), 0);
              restrictionFailed = true;
              return false;
            }
          } else {
            // Для других климатических элементов (кондиционер) проверяем только placementSide
            const existingClimateOnSameSide = elementsOnSegment.filter(el =>
              el.type === elementType && el.placementSide === newElement.placementSide
            );

            if (existingClimateOnSameSide.length >= 1) {
              setTimeout(() => toast.warn("На одній стороні стіни вже є кондиціонер"), 0);
              restrictionFailed = true;
              return false;
            }
          }
        }

        if (isLargeOpeningElement(elementType)) {
          const existingLargeOpenings = elementsOnSegment.filter((el) =>
            isLargeOpeningElement(el.type),
          );

          if (existingLargeOpenings.length > 0) {
            const existingTypes = existingLargeOpenings.map((el) => el.type);
            const hasDoor =
              existingTypes.includes(OBJECT_TYPES.DOOR) ||
              elementType === OBJECT_TYPES.DOOR;
            const hasWindow =
              existingTypes.includes(OBJECT_TYPES.WINDOW) ||
              existingTypes.includes(OBJECT_TYPES.PANORAMIC_WINDOW) ||
              elementType === OBJECT_TYPES.WINDOW ||
              elementType === OBJECT_TYPES.PANORAMIC_WINDOW;

            if (hasDoor && hasWindow) {
              const errorMessage =
                "Не можна розміщувати двері та вікна на одному сегменті стіни";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }
          }

          // Проверка: только одно окно на сегмент
          if (elementType === OBJECT_TYPES.WINDOW || elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
            const existingWindows = elementsOnSegment.filter((el) =>
              el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
            );

            if (existingWindows.length > 0) {
              const errorMessage = "На одному сегменті може бути тільки одне вікно";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }
          }

          // Проверка для панорамных окон: соседний сегмент должен быть свободен
          if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
            if (!wall.segments || wall.segments.length < 2) {
              const errorMessage = "Не можна розміщувати панорамне вікно: потрібно мінімум 2 сегменти стіни";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }

            const currentSegmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
            if (currentSegmentIndex === -1) {
              restrictionFailed = true;
              return false;
            }

            // Проверяем, что есть следующий сегмент
            if (currentSegmentIndex >= wall.segments.length - 1) {
              const errorMessage = "Не можна розміщувати панорамне вікно на останньому сегменті стіни (потрібно 2 сегменти)";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }

            // Проверяем электрику на текущем сегменте
            const currentSegment = wall.segments[currentSegmentIndex];
            if (currentSegment) {
              const currentSegmentElements = currentSegment.elements || [];
              const currentSegmentElectrical = currentSegmentElements.filter(el =>
                ELECTRICAL_ELEMENT_TYPES.includes(el.type)
              );

              if (currentSegmentElectrical.length > 0) {
                const errorMessage = "Не можна розміщувати панорамне вікно: на цьому сегменті вже є електрика";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем пересечение с перегородками на текущем сегменте
              console.log('🔍 [PANORAMIC_WINDOW] Проверка пересечения с перегородками на текущем сегменте');
              if (checkPartitionIntersection(wall, currentSegment)) {
                console.log('🚫 [PANORAMIC_WINDOW] Обнаружено пересечение с перегородкой на текущем сегменте');
                const errorMessage = "Не можна розміщувати панорамне вікно: на цьому сегменті є перегородка";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
            }

            // Проверяем следующий сегмент (панорамное окно занимает текущий и следующий)
            const nextSegment = wall.segments[currentSegmentIndex + 1];
            if (nextSegment) {
              const nextSegmentElements = nextSegment.elements || [];
              const nextSegmentLargeOpenings = nextSegmentElements.filter(el =>
                isLargeOpeningElement(el.type)
              );

              if (nextSegmentLargeOpenings.length > 0) {
                const errorMessage = "Не можна розміщувати панорамне вікно: на сусідньому сегменті вже є двері або вікно";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем электрику на следующем сегменте
              const nextSegmentElectrical = nextSegmentElements.filter(el =>
                ELECTRICAL_ELEMENT_TYPES.includes(el.type)
              );

              if (nextSegmentElectrical.length > 0) {
                const errorMessage = "Не можна розміщувати панорамне вікно: на сусідньому сегменті вже є електрика";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем пересечение с перегородками на следующем сегменте
              console.log('🔍 [PANORAMIC_WINDOW] Проверка пересечения с перегородками на следующем сегменте');
              if (checkPartitionIntersection(wall, nextSegment)) {
                console.log('🚫 [PANORAMIC_WINDOW] Обнаружено пересечение с перегородкой на следующем сегменте');
                const errorMessage = "Не можна розміщувати панорамне вікно: на сусідньому сегменті є перегородка";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
            }

            // Также проверяем предыдущий сегмент, если он есть (на случай, если панорамное окно уже размещено там)
            if (currentSegmentIndex > 0) {
              const prevSegment = wall.segments[currentSegmentIndex - 1];
              if (prevSegment) {
                const prevSegmentElements = prevSegment.elements || [];
                const prevSegmentPanoramicWindows = prevSegmentElements.filter(el =>
                  el.type === OBJECT_TYPES.PANORAMIC_WINDOW
                );

                if (prevSegmentPanoramicWindows.length > 0) {
                  const errorMessage = "Не можна розміщувати панорамне вікно: попередній сегмент вже містить панорамне вікно";
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                  return false;
                }
              }
            }
          }

          // Проверка для ворот: два следующих сегмента должны быть свободны (ворота занимают 3 сегмента)
          if (elementType === OBJECT_TYPES.GATE) {
            if (!wall.segments || wall.segments.length < 3) {
              const errorMessage = "Не можна розміщувати ворота: потрібно мінімум 3 сегменти стіни";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }

            const currentSegmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
            if (currentSegmentIndex === -1) {
              restrictionFailed = true;
              return false;
            }

            // Проверяем, что есть два следующих сегмента
            if (currentSegmentIndex >= wall.segments.length - 2) {
              const errorMessage = "Не можна розміщувати ворота на останніх сегментах стіни (потрібно 3 сегменти)";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
              return false;
            }

            // Проверяем электрику на текущем сегменте
            const currentSegment = wall.segments[currentSegmentIndex];
            if (currentSegment) {
              const currentSegmentElements = currentSegment.elements || [];
              const currentSegmentElectrical = currentSegmentElements.filter(el =>
                ELECTRICAL_ELEMENT_TYPES.includes(el.type)
              );

              if (currentSegmentElectrical.length > 0) {
                const errorMessage = "Не можна розміщувати ворота: на цьому сегменті вже є електрика";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем пересечение с перегородками на текущем сегменте
              console.log('🔍 [GATE] Проверка пересечения с перегородками на текущем сегменте');
              if (checkPartitionIntersection(wall, currentSegment)) {
                console.log('🚫 [GATE] Обнаружено пересечение с перегородкой на текущем сегменте');
                const errorMessage = "Не можна розміщувати ворота: на цьому сегменті є перегородка";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
            }

            // Проверяем первый следующий сегмент
            const nextSegment1 = wall.segments[currentSegmentIndex + 1];
            if (nextSegment1) {
              const nextSegment1Elements = nextSegment1.elements || [];
              const nextSegment1LargeOpenings = nextSegment1Elements.filter(el =>
                isLargeOpeningElement(el.type)
              );

              if (nextSegment1LargeOpenings.length > 0) {
                const errorMessage = "Не можна розміщувати ворота: на сусідньому сегменті вже є двері або вікно";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем электрику на первом следующем сегменте
              const nextSegment1Electrical = nextSegment1Elements.filter(el =>
                ELECTRICAL_ELEMENT_TYPES.includes(el.type)
              );

              if (nextSegment1Electrical.length > 0) {
                const errorMessage = "Не можна розміщувати ворота: на сусідньому сегменті вже є електрика";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем пересечение с перегородками на первом следующем сегменте
              console.log('🔍 [GATE] Проверка пересечения с перегородками на первом следующем сегменте');
              if (checkPartitionIntersection(wall, nextSegment1)) {
                console.log('🚫 [GATE] Обнаружено пересечение с перегородкой на первом следующем сегменте');
                const errorMessage = "Не можна розміщувати ворота: на сусідньому сегменті є перегородка";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
            }

            // Проверяем второй следующий сегмент
            const nextSegment2 = wall.segments[currentSegmentIndex + 2];
            if (nextSegment2) {
              const nextSegment2Elements = nextSegment2.elements || [];
              const nextSegment2LargeOpenings = nextSegment2Elements.filter(el =>
                isLargeOpeningElement(el.type)
              );

              if (nextSegment2LargeOpenings.length > 0) {
                const errorMessage = "Не можна розміщувати ворота: на другому сусідньому сегменті вже є двері або вікно";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем электрику на втором следующем сегменте
              const nextSegment2Electrical = nextSegment2Elements.filter(el =>
                ELECTRICAL_ELEMENT_TYPES.includes(el.type)
              );

              if (nextSegment2Electrical.length > 0) {
                const errorMessage = "Не можна розміщувати ворота: на другому сусідньому сегменті вже є електрика";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }

              // Проверяем пересечение с перегородками на втором следующем сегменте
              console.log('🔍 [GATE] Проверка пересечения с перегородками на втором следующем сегменте');
              if (checkPartitionIntersection(wall, nextSegment2)) {
                console.log('🚫 [GATE] Обнаружено пересечение с перегородкой на втором следующем сегменте');
                const errorMessage = "Не можна розміщувати ворота: на другому сусідньому сегменті є перегородка";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
            }

            // Также проверяем предыдущие сегменты, если они есть (на случай, если ворота уже размещены там)
            if (currentSegmentIndex > 0) {
              const prevSegment = wall.segments[currentSegmentIndex - 1];
              if (prevSegment) {
                const prevSegmentElements = prevSegment.elements || [];
                const prevSegmentGates = prevSegmentElements.filter(el =>
                  el.type === OBJECT_TYPES.GATE
                );

                if (prevSegmentGates.length > 0) {
                  const errorMessage = "Не можна розміщувати ворота: попередній сегмент вже містить ворота";
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                  return false;
                }
              }
            }

            if (currentSegmentIndex > 1) {
              const prevSegment2 = wall.segments[currentSegmentIndex - 2];
              if (prevSegment2) {
                const prevSegment2Elements = prevSegment2.elements || [];
                const prevSegment2Gates = prevSegment2Elements.filter(el =>
                  el.type === OBJECT_TYPES.GATE
                );

                if (prevSegment2Gates.length > 0) {
                  const errorMessage = "Не можна розміщувати ворота: попередній сегмент вже містить ворота";
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                  return false;
                }
              }
            }
          }
        }

        // Добавляем элемент
        elementsOnSegment.push(newElement);

        // Для панорамных окон в безкаркасном режиме: добавляем копию на соседний сегмент
        if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
          const currentSegmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
          if (currentSegmentIndex !== -1 && currentSegmentIndex < wall.segments.length - 1) {
            const adjacentSegment = wall.segments[currentSegmentIndex + 1];
            if (adjacentSegment) {
              if (!adjacentSegment.elements) {
                adjacentSegment.elements = [];
              }

              const adjacentElement = {
                ...newElement,
                id: generateId("el_"),
                isAdjacentCopy: true, // Don't render this copy
              };

              // Для перегородок нужно также установить segmentIndex и globalPositionOnWall для копии
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                const numSegments = wall.segments.length;
                const adjacentSegmentIndex = currentSegmentIndex + 1;
                const adjacentSegmentStart = adjacentSegmentIndex / numSegments;
                const adjacentSegmentEnd = (adjacentSegmentIndex + 1) / numSegments;
                adjacentElement.globalPositionOnWall = adjacentSegmentStart + (adjacentElement.positionOnSegment * (adjacentSegmentEnd - adjacentSegmentStart));
                adjacentElement.segmentIndex = adjacentSegmentIndex;
              }

              adjacentSegment.elements.push(adjacentElement);

              // Для перегородок также добавляем копию в wall.elements
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                if (!Array.isArray(wall.elements)) {
                  wall.elements = [];
                }
                wall.elements.push(adjacentElement);
              }

              console.log('🪟 [PANORAMIC WINDOW] Added copy to adjacent frameless segment:', {
                originalSegmentId: wallSegmentId,
                adjacentSegmentId: adjacentSegment.id,
                currentSegmentIndex,
                elementId: newElement.id,
                adjacentElementId: adjacentElement.id,
                isPartition: wall.type === OBJECT_TYPES.FRAMELESS_PARTITION
              });
            }
          }
        }

        // Для ворот в безкаркасном режиме: добавляем копии на два соседних сегмента (ворота занимают 3 сегмента)
        if (elementType === OBJECT_TYPES.GATE) {
          const currentSegmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
          if (currentSegmentIndex !== -1 && currentSegmentIndex < wall.segments.length - 2) {
            // Первая копия на следующий сегмент
            const adjacentSegment1 = wall.segments[currentSegmentIndex + 1];
            if (adjacentSegment1) {
              if (!adjacentSegment1.elements) {
                adjacentSegment1.elements = [];
              }

              const adjacentElement1 = {
                ...newElement,
                id: generateId("el_"),
                isAdjacentCopy: true, // Don't render this copy
              };

              // Для перегородок нужно также установить segmentIndex и globalPositionOnWall для копии
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                const numSegments = wall.segments.length;
                const adjacentSegmentIndex1 = currentSegmentIndex + 1;
                const adjacentSegmentStart1 = adjacentSegmentIndex1 / numSegments;
                const adjacentSegmentEnd1 = (adjacentSegmentIndex1 + 1) / numSegments;
                adjacentElement1.globalPositionOnWall = adjacentSegmentStart1 + (adjacentElement1.positionOnSegment * (adjacentSegmentEnd1 - adjacentSegmentStart1));
                adjacentElement1.segmentIndex = adjacentSegmentIndex1;
              }

              adjacentSegment1.elements.push(adjacentElement1);

              // Для перегородок также добавляем копию в wall.elements
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                if (!Array.isArray(wall.elements)) {
                  wall.elements = [];
                }
                wall.elements.push(adjacentElement1);
              }
            }

            // Вторая копия на второй следующий сегмент
            const adjacentSegment2 = wall.segments[currentSegmentIndex + 2];
            if (adjacentSegment2) {
              if (!adjacentSegment2.elements) {
                adjacentSegment2.elements = [];
              }

              const adjacentElement2 = {
                ...newElement,
                id: generateId("el_"),
                isAdjacentCopy: true, // Don't render this copy
              };

              // Для перегородок нужно также установить segmentIndex и globalPositionOnWall для копии
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                const numSegments = wall.segments.length;
                const adjacentSegmentIndex2 = currentSegmentIndex + 2;
                const adjacentSegmentStart2 = adjacentSegmentIndex2 / numSegments;
                const adjacentSegmentEnd2 = (adjacentSegmentIndex2 + 1) / numSegments;
                adjacentElement2.globalPositionOnWall = adjacentSegmentStart2 + (adjacentElement2.positionOnSegment * (adjacentSegmentEnd2 - adjacentSegmentStart2));
                adjacentElement2.segmentIndex = adjacentSegmentIndex2;
              }

              adjacentSegment2.elements.push(adjacentElement2);

              // Для перегородок также добавляем копию в wall.elements
              if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                if (!Array.isArray(wall.elements)) {
                  wall.elements = [];
                }
                wall.elements.push(adjacentElement2);
              }
            }

            console.log('🚪 [GATE] Added copies to adjacent frameless segments:', {
              originalSegmentId: wallSegmentId,
              adjacentSegment1Id: adjacentSegment1?.id,
              adjacentSegment2Id: adjacentSegment2?.id,
              currentSegmentIndex,
              elementId: newElement.id,
              isPartition: wall.type === OBJECT_TYPES.FRAMELESS_PARTITION
            });
          }
        }

        // Пересчитываем позиции элементов (как в модульном режиме)
        if (isLargeOpeningElement(elementType)) {
          // Для больших элементов (двери/окна) позиция не меняется
        } else {
          // Для маленьких элементов пересчитываем позиции, чтобы они равномерно распределялись
          const smallElements = elementsOnSegment.filter(el =>
            !isLargeOpeningElement(el.type) && el.type !== OBJECT_TYPES.WINDOW_BLINDS
          );


          // Инициализируем позицию нового элемента, если она не задана
          if (newElement.positionOnSegment === undefined || newElement.positionOnSegment === null) {
            newElement.positionOnSegment = positionOnSegment ?? 0.5;
          }

          // Force placementSide to 1 if undefined to match default rendering and grouping behavior
          if (newElement.placementSide === undefined || newElement.placementSide === null) {
            newElement.placementSide = 1;
          }

          // Группируем элементы по стороне размещения (placementSide)
          // Это решает проблему конфликта элементов на разных сторонах (например, ролета и обогреватель)
          const elementsBySide = {};

          smallElements.forEach(el => {
            let rawSide = el.placementSide;
            // Robust normalization: handle 0, 'undefined', 'null' as default '1'
            if (rawSide === undefined || rawSide === null || rawSide === 'undefined' || rawSide === 'null' || rawSide === 0 || rawSide === '0') {
              rawSide = '1';
            }
            const side = String(rawSide);

            if (!elementsBySide[side]) {
              elementsBySide[side] = [];
            }
            elementsBySide[side].push(el);
          });

          // Перераспределяем элементы внутри каждой группы независимо
          Object.values(elementsBySide).forEach(group => {
            if (group.length > 1) {
              // Сортируем по текущей позиции для предсказуемости
              group.sort((a, b) => (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5));

              group.forEach((element, index) => {
                const spacingFactor = 1 / (group.length + 1);
                element.positionOnSegment = spacingFactor * (index + 1);
              });
            }
            // Если элемент один (group.length === 1), мы не меняем его позицию (она уже задана или сохраняется старая)
          });
        }

        if (process.env.NODE_ENV !== "production") {
          console.log("[Reducer][ADD_ELEMENT_TO_WALL] New element appended", {
            newElement,
            totalElements: elementsOnSegment.length,
          });
        }
        if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          if (!Array.isArray(wall.elements)) {
            wall.elements = [];
          }
          wall.elements.push(newElement);
        }

        // Special handling for window blinds on panoramic windows: add to adjacent segment
        // (same logic as in modular mode)
        if (elementType === OBJECT_TYPES.WINDOW_BLINDS) {
          // Check if there's a panoramic window on this segment at the same position
          const panoramicWindow = segment.elements.find(
            el => el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
              !el.isAdjacentCopy &&
              Math.abs((el.positionOnSegment || 0.5) - (newElement.positionOnSegment || 0.5)) < 0.01
          );

          if (panoramicWindow) {
            // Find adjacent segment (same logic as for panoramic window)
            const currentSegmentIndex = wall.segments.findIndex(seg => seg.id === wallSegmentId);
            if (currentSegmentIndex !== -1 && currentSegmentIndex < wall.segments.length - 1) {
              const adjacentSegment = wall.segments[currentSegmentIndex + 1];
              if (adjacentSegment) {
                if (!adjacentSegment.elements) {
                  adjacentSegment.elements = [];
                }

                // Add copy of blinds to adjacent segment
                // Note: Don't mark as isAdjacentCopy - blinds should render on both segments
                const adjacentBlindsId = generateId("el_");
                const adjacentBlinds = {
                  ...newElement,
                  id: adjacentBlindsId,
                  positionOnSegment: newElement.positionOnSegment,
                  // Note: Don't mark as isAdjacentCopy - blinds should render on both segments
                };

                // Для перегородок нужно также установить segmentIndex и globalPositionOnWall для копии
                if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                  const numSegments = wall.segments.length;
                  const adjacentSegmentIndex = currentSegmentIndex + 1;
                  const adjacentSegmentStart = adjacentSegmentIndex / numSegments;
                  const adjacentSegmentEnd = (adjacentSegmentIndex + 1) / numSegments;
                  adjacentBlinds.globalPositionOnWall = adjacentSegmentStart + (adjacentBlinds.positionOnSegment * (adjacentSegmentEnd - adjacentSegmentStart));
                  adjacentBlinds.segmentIndex = adjacentSegmentIndex;
                }

                adjacentSegment.elements.push(adjacentBlinds);

                // Для перегородок также добавляем копию в wall.elements
                if (wall.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
                  if (!Array.isArray(wall.elements)) {
                    wall.elements = [];
                  }
                  wall.elements.push(adjacentBlinds);
                }

                if (process.env.NODE_ENV !== "production") {
                  console.log('🪟 [WINDOW BLINDS] Added copy to adjacent frameless segment for panoramic window:', {
                    originalSegmentId: wallSegmentId,
                    adjacentSegmentId: adjacentSegment.id,
                    currentSegmentIndex,
                    blindsId: newElement.id,
                    adjacentBlindsId: adjacentBlindsId,
                    isPartition: wall.type === OBJECT_TYPES.FRAMELESS_PARTITION
                  });
                }
              }
            }
          }
        }

        elementAdded = true;


        return true;
      };

      const findAndMutateInObject = (obj) => {
        if (
          obj.type === OBJECT_TYPES.FRAMELESS_WALL ||
          obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
        ) {
          console.log('🔍 [findAndMutateInObject] Проверяем объект:', {
            objId: obj.id,
            objType: obj.type,
            elementType,
            wallSegmentId
          });
          const result = tryAddElementToFramelessWall(obj);
          console.log('📊 [findAndMutateInObject] Результат tryAddElementToFramelessWall:', {
            result,
            restrictionFailed
          });
          if (result) {
            return true;
          }
          // Если функция вернула false и restrictionFailed = true, нужно остановить обработку
          if (restrictionFailed) {
            console.log('🛑 [findAndMutateInObject] Остановка из-за restrictionFailed');
            return false; // Возвращаем false, чтобы остановить поиск
          }
        }

        if (obj.type === OBJECT_TYPES.MODULE && obj.wallSegments) {
          for (const segmentKey in obj.wallSegments) {
            if (obj.wallSegments[segmentKey].id === wallSegmentId) {
              const segment = obj.wallSegments[segmentKey];

              // Check for panoramic window placement on edge segments
              if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
                const [coords, orientation] = segmentKey.split('_');
                const [cellX, cellY] = coords.split(',').map(Number);
                const cWide = obj.cellsWide;
                const cLong = obj.cellsLong;

                let isEdge = false;
                if (orientation === 'h') {
                  // Horizontal: cannot place on the last column
                  if (cellX >= cWide - 1) isEdge = true;
                } else {
                  // Vertical: cannot place on the last row
                  if (cellY >= cLong - 1) isEdge = true;
                }

                if (isEdge) {
                  setTimeout(() => toast.warn("Не можна розміщувати панорамне вікно на краю модуля (потрібно 2 сегменти)"), 0);
                  restrictionFailed = true;
                  return false;
                }

                // Проверка, что соседний сегмент свободен от больших элементов
                let adjacentSegmentKey;
                if (orientation === 'h') {
                  adjacentSegmentKey = `${cellX + 1},${cellY}_h`;
                } else {
                  adjacentSegmentKey = `${cellX},${cellY + 1}_v`;
                }

                const adjacentSegment = obj.wallSegments[adjacentSegmentKey];
                if (adjacentSegment) {
                  const adjacentElements = adjacentSegment.elements || [];
                  const adjacentLargeOpenings = adjacentElements.filter(el =>
                    isLargeOpeningElement(el.type)
                  );

                  if (adjacentLargeOpenings.length > 0) {
                    const errorMessage = "Не можна розміщувати панорамне вікно: на сусідньому сегменті вже є двері або вікно";
                    setTimeout(() => toast.warn(errorMessage), 0);
                    restrictionFailed = true;
                    return false;
                  }
                }
              }

              if (obj.subtype === "shower") {
                if (!SHOWER_ALLOWED_ELEMENT_TYPES.includes(elementType)) {
                  setTimeout(() => toast.warn(t("toasts.showerModuleElementRestriction")), 0);
                  restrictionFailed = true;
                  return false;
                }
              }

              if (obj.subtype === "toilet") {
                if (!TOILET_ALLOWED_ELEMENT_TYPES.includes(elementType)) {
                  setTimeout(() => toast.warn(t("toasts.toiletModuleElementRestriction")), 0);
                  restrictionFailed = true;
                  return false;
                }
              }

              if (SANITARY_ELEMENT_TYPES.includes(elementType) && obj.subtype !== "sanitary") {
                setTimeout(() => toast.warn(t("toasts.sanitaryElementError")), 0);
                restrictionFailed = true;
                return false;
              }

              if (!segment.elements) segment.elements = [];

              // --- NEW CHECK: Door on occupied sandwich ---
              if (elementType === OBJECT_TYPES.DOOR && segment.elements.length > 0) {
                const errorMessage = t("toasts.doorPlacementOnOccupiedSandwichError");
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
                return false;
              }
              // --------------------------------------------

              if (ELECTRICAL_ELEMENT_TYPES.includes(elementType)) {
                const existingElectricalElements = segment.elements.filter(el =>
                  ELECTRICAL_ELEMENT_TYPES.includes(el.type)
                );

                if (existingElectricalElements.length >= ELEMENT_GROUP_LIMITS.ELECTRICAL) {
                  const errorMessage = `Максимум ${ELEMENT_GROUP_LIMITS.ELECTRICAL} електричних елементів в сумі на сегмент стіни`;
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                  return false;
                }
              }

              if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
                const existingSanitaryElements = segment.elements.filter(el =>
                  SANITARY_ELEMENT_TYPES.includes(el.type)
                );

                if (existingSanitaryElements.length >= ELEMENT_GROUP_LIMITS.SANITARY) {
                  const errorMessage = t("toasts.sanitaryElementOnePerSandwichError");
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                  return false;
                }
              } else if (!CLIMATE_ELEMENT_TYPES.includes(elementType)) {
                const elementLimit = ELEMENT_LIMITS_PER_WALL_SEGMENT[elementType];
                if (elementLimit && !ELECTRICAL_ELEMENT_TYPES.includes(elementType)) {
                  const existingElementsOfSameType = segment.elements.filter(el =>
                    el.type === elementType
                  );

                  if (existingElementsOfSameType.length >= elementLimit) {
                    let errorMessage;
                    if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
                      errorMessage = t("toasts.sanitaryElementOnePerWallError");
                    } else if (elementLimit === 1) {
                      errorMessage = t("toasts.elementLimitOnePerWallError");
                    } else {
                      errorMessage = `Максимум ${elementLimit} елементів типу "${elementType}" на сегмент стіни`;
                    }
                    setTimeout(() => toast.warn(errorMessage), 0);
                    restrictionFailed = true;
                    return false;
                  }
                }
              }

              if (CLIMATE_ELEMENT_TYPES.includes(elementType)) {
                // Для ролеты проверяем не только placementSide, но и позицию - ролета должна быть на том же месте, что и окно
                if (elementType === OBJECT_TYPES.WINDOW_BLINDS) {
                  // Проверяем, есть ли уже ролета на той же позиции (с небольшой погрешностью)
                  const positionTolerance = 0.1; // 10% от длины сегмента
                  const existingBlindsAtSamePosition = segment.elements.filter(el =>
                    el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                    Math.abs((el.positionOnSegment || 0.5) - (newElement.positionOnSegment || 0.5)) < positionTolerance
                  );

                  if (existingBlindsAtSamePosition.length >= 1) {
                    setTimeout(() => toast.warn("На цьому місці вже є ролета"), 0);
                    restrictionFailed = true;
                    return false;
                  }
                } else {
                  // Для других климатических элементов (кондиционер) проверяем только placementSide
                  const existingClimateOnSameSide = segment.elements.filter(el =>
                    el.type === elementType && el.placementSide === newElement.placementSide
                  );

                  if (existingClimateOnSameSide.length >= 1) {
                    setTimeout(() => toast.warn("На одній стороні стіни вже є кондиціонер"), 0);
                    restrictionFailed = true;
                    return false;
                  }
                }
              }

              const segmentKeys = Object.keys(obj.wallSegments);

              const allJoints = tempObjects.filter(o => o.type === OBJECT_TYPES.DOCKING_JOINT);
              const moduleJoints = allJoints.filter(j =>
                j.moduleA_id === obj.id || j.moduleB_id === obj.id
              );

              let conflictingElementsOnAdjacentJoints = 0;
              moduleJoints.forEach(joint => {
                if (joint.elements && joint.elements.length > 0) {
                  const jointEndpoints = [
                    { x: joint.x - joint.width / 2, y: joint.y },
                    { x: joint.x + joint.width / 2, y: joint.y }
                  ];

                  const [coords, orientation] = segmentKey.split('_');
                  const [x, y] = coords.split(',').map(Number);

                  let wallEndpoints;
                  if (orientation === 'h') {
                    wallEndpoints = [
                      { x, y },
                      { x: x + 1, y }
                    ];
                  } else {
                    wallEndpoints = [
                      { x, y },
                      { x, y: y + 1 }
                    ];
                  }

                  const formsCornerWithJoint = jointEndpoints.some(jointPoint =>
                    wallEndpoints.some(wallPoint => {
                      const distance = Math.sqrt((jointPoint.x - wallPoint.x) ** 2 + (jointPoint.y - wallPoint.y) ** 2);
                      return distance < 0.1;
                    })
                  );

                  if (formsCornerWithJoint) {
                    const hasSanitaryElement = joint.elements.some(el =>
                      SANITARY_ELEMENT_TYPES.includes(el.type)
                    );
                    if (hasSanitaryElement) {
                      conflictingElementsOnAdjacentJoints++;
                    }
                  }
                }
              });

              if (conflictingElementsOnAdjacentJoints > 0) {
                setTimeout(() => toast.warn(t("toasts.sanitaryElementCornerConflictError")), 0);
                restrictionFailed = true;
                return false;
              }

              const parseSegmentKey = (key) => {
                const [coords, orientation] = key.split('_');
                const [x, y] = coords.split(',').map(Number);

                let endX, endY;
                if (orientation === 'h') {
                  endX = x + 1;
                  endY = y;
                } else {
                  endX = x;
                  endY = y + 1;
                }

                return {
                  x, y, orientation,
                  start: { x, y },
                  end: { x: endX, y: endY }
                };
              };

              const currentSegmentData = parseSegmentKey(segmentKey);
              const adjacentWallKeys = [];

              segmentKeys.forEach(otherWallKey => {
                if (otherWallKey === segmentKey) return;

                const otherSegmentData = parseSegmentKey(otherWallKey);

                if (currentSegmentData.orientation !== otherSegmentData.orientation) {
                  const isAdjacent = (
                    (currentSegmentData.start.x === otherSegmentData.end.x &&
                      currentSegmentData.start.y === otherSegmentData.end.y) ||
                    (currentSegmentData.end.x === otherSegmentData.start.x &&
                      currentSegmentData.end.y === otherSegmentData.start.y) ||
                    (currentSegmentData.start.x === otherSegmentData.start.x &&
                      currentSegmentData.start.y === otherSegmentData.start.y) ||
                    (currentSegmentData.end.x === otherSegmentData.end.x &&
                      currentSegmentData.end.y === otherSegmentData.end.y)
                  );

                  if (isAdjacent) {
                    adjacentWallKeys.push(otherWallKey);
                  }
                }
              });

              let conflictingElementsOnAdjacentWalls = 0;
              adjacentWallKeys.forEach(adjacentWallKey => {
                const adjacentSegment = obj.wallSegments[adjacentWallKey];
                if (adjacentSegment && adjacentSegment.elements) {
                  const sanitaryElements = adjacentSegment.elements.filter(el =>
                    SANITARY_ELEMENT_TYPES.includes(el.type)
                  );
                  if (sanitaryElements.length > 0) {
                    conflictingElementsOnAdjacentWalls++;
                  }
                }
              });

              if (conflictingElementsOnAdjacentWalls > 0) {
                setTimeout(() => toast.warn(t("toasts.sanitaryElementCornerConflictError")), 0);
                restrictionFailed = true;
                return false;
              }

              if (!segment.elements) segment.elements = [];

              if (isLargeOpeningElement(elementType)) {
                const existingLargeOpenings = segment.elements.filter(el => isLargeOpeningElement(el.type));

                if (existingLargeOpenings.length > 0) {
                  const existingTypes = existingLargeOpenings.map(el => el.type);
                  const hasDoor = existingTypes.includes(OBJECT_TYPES.DOOR) ||
                    existingTypes.includes(OBJECT_TYPES.GATE) ||
                    elementType === OBJECT_TYPES.DOOR ||
                    elementType === OBJECT_TYPES.GATE;
                  const hasWindow = existingTypes.includes(OBJECT_TYPES.WINDOW) ||
                    existingTypes.includes(OBJECT_TYPES.PANORAMIC_WINDOW) ||
                    elementType === OBJECT_TYPES.WINDOW ||
                    elementType === OBJECT_TYPES.PANORAMIC_WINDOW;

                  if (hasDoor && hasWindow) {
                    setTimeout(() => toast.warn("Не можна розміщувати двері/ворота та вікна на одному сегменті стіни"), 0);
                    restrictionFailed = true;
                    return false;
                  }
                }

                // Проверка: только одно окно на сегмент
                if (elementType === OBJECT_TYPES.WINDOW || elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
                  const existingWindows = segment.elements.filter((el) =>
                    el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
                  );

                  if (existingWindows.length > 0) {
                    const errorMessage = "На одному сегменті може бути тільки одне вікно";
                    setTimeout(() => toast.warn(errorMessage), 0);
                    restrictionFailed = true;
                    return false;
                  }
                }
              }

              if (isLargeOpeningElement(elementType)) {
                segment.elements.push(newElement);

                // For panoramic windows, also add to adjacent segment (for wall removal)
                // but mark it so it won't render
                if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
                  const [coords, orientation] = segmentKey.split('_');
                  const [cellX, cellY] = coords.split(',').map(Number);

                  let adjacentSegmentKey;
                  if (orientation === 'h') {
                    adjacentSegmentKey = `${cellX + 1},${cellY}_h`;
                  } else {
                    adjacentSegmentKey = `${cellX},${cellY + 1}_v`;
                  }

                  const adjacentSegment = obj.wallSegments[adjacentSegmentKey];
                  if (adjacentSegment) {
                    const adjacentElement = {
                      ...newElement,
                      id: generateId("el_"),
                      isAdjacentCopy: true, // Don't render this copy
                    };

                    if (!adjacentSegment.elements) {
                      adjacentSegment.elements = [];
                    }

                    adjacentSegment.elements.push(adjacentElement);

                    console.log('🪟 [PANORAMIC WINDOW] Added invisible copy to adjacent segment:', {
                      originalSegment: segmentKey,
                      adjacentSegment: adjacentSegmentKey,
                      isAdjacentCopy: true
                    });
                  }
                }
              } else {
                segment.elements.push(newElement);

                const smallElements = segment.elements.filter(el =>
                  !isLargeOpeningElement(el.type) && el.type !== OBJECT_TYPES.WINDOW_BLINDS
                );

                // Group by placement side
                const elementsBySide = {};
                smallElements.forEach(el => {
                  let rawSide = el.placementSide;
                  // Robust normalization: handle 0, 'undefined', 'null' as default '1'
                  if (rawSide === undefined || rawSide === null || rawSide === 'undefined' || rawSide === 'null' || rawSide === 0 || rawSide === '0') {
                    rawSide = '1';
                  }
                  const side = String(rawSide);

                  if (!elementsBySide[side]) elementsBySide[side] = [];
                  elementsBySide[side].push(el);
                });

                Object.values(elementsBySide).forEach(group => {
                  if (group.length > 1) {
                    group.sort((a, b) => (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5));
                    group.forEach((element, index) => {
                      const spacingFactor = 1 / (group.length + 1);
                      element.positionOnSegment = spacingFactor * (index + 1);
                    });
                  }
                });

                // If newElement was alone (group length 1), set its position
                // Note: normalized grouping means we check the normalized side, not raw placementSide
                const newElRawSide = newElement.placementSide;
                const normalizedNewElSide = (newElRawSide === undefined || newElRawSide === null || newElRawSide === 'undefined' || newElRawSide === 'null' || newElRawSide === 0 || newElRawSide === '0')
                  ? '1'
                  : String(newElRawSide);

                if (smallElements.length <= 1 || elementsBySide[normalizedNewElSide]?.length <= 1) {
                  if (newElement.positionOnSegment === undefined) {
                    newElement.positionOnSegment = positionOnSegment ?? 0.5;
                  }
                }
              }

              if (elementType === OBJECT_TYPES.INTERNAL_AIR_CONDITIONER) {
                const externalElementId = generateId("el_");
                const externalElement = {
                  id: externalElementId,
                  type: OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER,
                  positionOnSegment: newElement.positionOnSegment,
                  ...defaultObjectSizes[OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER],
                  placementSide: -newElement.placementSide,
                  linkedElementId: newElementId,
                };

                newElement.linkedElementId = externalElementId;

                segment.elements.push(externalElement);

              }

              // Special handling for window blinds on panoramic windows: add to adjacent segment
              if (elementType === OBJECT_TYPES.WINDOW_BLINDS) {
                // Check if there's a panoramic window on this segment at the same position
                const panoramicWindow = segment.elements.find(
                  el => el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                    !el.isAdjacentCopy &&
                    Math.abs((el.positionOnSegment || 0.5) - (newElement.positionOnSegment || 0.5)) < 0.01
                );

                if (panoramicWindow) {
                  // Find adjacent segment (same logic as for panoramic window)
                  const [coords, orientation] = segmentKey.split('_');
                  const [cellX, cellY] = coords.split(',').map(Number);

                  let adjacentSegmentKey;
                  if (orientation === 'h') {
                    adjacentSegmentKey = `${cellX + 1},${cellY}_h`;
                  } else {
                    adjacentSegmentKey = `${cellX},${cellY + 1}_v`;
                  }

                  const adjacentSegment = obj.wallSegments[adjacentSegmentKey];
                  if (adjacentSegment) {
                    if (!adjacentSegment.elements) {
                      adjacentSegment.elements = [];
                    }

                    // Add copy of blinds to adjacent segment
                    const adjacentBlindsId = generateId("el_");
                    const adjacentBlinds = {
                      ...newElement,
                      id: adjacentBlindsId,
                      positionOnSegment: newElement.positionOnSegment,
                      // Note: Don't mark as isAdjacentCopy - blinds should render on both segments
                    };

                    adjacentSegment.elements.push(adjacentBlinds);

                    console.log('🪟 [WINDOW BLINDS] Added copy to adjacent segment for panoramic window:', {
                      originalSegment: segmentKey,
                      adjacentSegment: adjacentSegmentKey,
                      blindsId: newElement.id,
                      adjacentBlindsId: adjacentBlindsId
                    });
                  }
                }
              }

              return true;
            }
          }
        }
        return false;
      };

      for (const obj of tempObjects) {
        console.log('🔄 [ADD_ELEMENT_TO_WALL] Обрабатываем объект:', {
          objId: obj.id,
          objType: obj.type,
          restrictionFailedBefore: restrictionFailed
        });
        if (findAndMutateInObject(obj)) {
          elementAdded = true;
          console.log('✅ [ADD_ELEMENT_TO_WALL] Элемент добавлен');
          break;
        }
        console.log('📊 [ADD_ELEMENT_TO_WALL] После findAndMutateInObject:', {
          restrictionFailed,
          elementAdded
        });
        if (restrictionFailed) {
          console.log('🛑 [ADD_ELEMENT_TO_WALL] Выход из цикла из-за restrictionFailed');
          break;
        }
      }

      if (!elementAdded && !restrictionFailed) {
        const joint = tempObjects.find(
          (o) => o.id === wallSegmentId && o.type === OBJECT_TYPES.DOCKING_JOINT,
        );
        if (joint) {
          // Запрещаем размещение окон на стыках
          if (elementType === OBJECT_TYPES.WINDOW || elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
            const errorMessage = "Не можна розміщувати вікна на стыках модулів";
            setTimeout(() => toast.warn(errorMessage), 0);
            restrictionFailed = true;
          }

          const moduleA = tempObjects.find(m => m.id === joint.moduleA_id);
          const moduleB = tempObjects.find(m => m.id === joint.moduleB_id);
          const isModuleAShower = moduleA?.subtype === 'shower';
          const isModuleBShower = moduleB?.subtype === 'shower';
          const isModuleAToilet = moduleA?.subtype === 'toilet';
          const isModuleBToilet = moduleB?.subtype === 'toilet';

          if ((isModuleAShower || isModuleBShower) && !SHOWER_ALLOWED_ELEMENT_TYPES.includes(elementType)) {
            setTimeout(() => toast.warn(t("toasts.showerModuleElementRestriction")), 0);
            restrictionFailed = true;
          } else if ((isModuleAToilet || isModuleBToilet) && !TOILET_ALLOWED_ELEMENT_TYPES.includes(elementType)) {
            setTimeout(() => toast.warn(t("toasts.toiletModuleElementRestriction")), 0);
            restrictionFailed = true;
          } else if (SANITARY_ELEMENT_TYPES.includes(elementType)) {
            if (moduleA?.subtype !== 'sanitary' && moduleB?.subtype !== 'sanitary') {
              setTimeout(() => toast.warn(t("toasts.sanitaryElementError")), 0);
              restrictionFailed = true;
            } else {
              const isModuleASanitary = moduleA?.subtype === 'sanitary';
              const isModuleBSanitary = moduleB?.subtype === 'sanitary';

              if (isModuleASanitary && isModuleBSanitary) {
                // Оставляем сторону размещения без изменений, обе стороны санитарные
              } else if (isModuleASanitary) {
                newElement.placementSide = 1;
              } else if (isModuleBSanitary) {
                newElement.placementSide = -1;
              }
            }
          }

          if (!restrictionFailed) {
            if (!joint.elements) joint.elements = [];

            const numSlots = Math.max(
              1,
              Math.round(joint.width / GRID_CELL_SIZE_M),
            );

            // Панорамное окно занимает 2 соседних слота
            const isPanoramicWindow = elementType === OBJECT_TYPES.PANORAMIC_WINDOW;
            const slotsNeeded = isPanoramicWindow ? 2 : 1;

            console.log('🔍 [PANORAMIC WINDOW] Placement calculation:', {
              elementType,
              isPanoramicWindow,
              slotsNeeded,
              numSlots,
              jointWidth: joint.width,
              gridCellSize: GRID_CELL_SIZE_M,
              initialPositionOnSegment: newElement.positionOnSegment,
              segmentIndex
            });

            let targetSlotIndex =
              segmentIndex === null ||
                segmentIndex === undefined ||
                Number.isNaN(segmentIndex)
                ? Math.max(
                  0,
                  Math.min(
                    numSlots - slotsNeeded,
                    Math.floor((newElement.positionOnSegment ?? 0.5) * numSlots),
                  ),
                )
                : Math.max(
                  0,
                  Math.min(numSlots - slotsNeeded, Math.floor(segmentIndex)),
                );

            console.log('🎯 [PANORAMIC WINDOW] Target slot calculation:', {
              targetSlotIndex,
              numSlots,
              slotsNeeded,
              canPlace: targetSlotIndex + slotsNeeded <= numSlots
            });

            if (isPanoramicWindow) {
              // Центрируем панорамное окно на 2 слотах
              newElement.segmentIndex = targetSlotIndex;
              newElement.positionOnSegment = (targetSlotIndex + 1) / numSlots;
              console.log('✅ [PANORAMIC WINDOW] Final placement:', {
                segmentIndex: newElement.segmentIndex,
                positionOnSegment: newElement.positionOnSegment,
                slotsOccupied: [targetSlotIndex, targetSlotIndex + 1],
                width: newElement.width || defaultObjectSizes[OBJECT_TYPES.PANORAMIC_WINDOW].width
              });
            } else {
              newElement.segmentIndex = targetSlotIndex;
              newElement.positionOnSegment = (targetSlotIndex + 0.5) / numSlots;
            }
            const passageSegments = joint.passageSegments || [];

            // Проверяем, не пересекается ли элемент с проходами
            const slotsOccupied = isPanoramicWindow
              ? [targetSlotIndex, targetSlotIndex + 1]
              : [targetSlotIndex];

            const intersectsPassage = slotsOccupied.some(slot => passageSegments.includes(slot));
            if (intersectsPassage) {
              const errorMessage = "Неможливо розмістити елемент на проході";
              setTimeout(() => toast.warn(errorMessage), 0);
              restrictionFailed = true;
            }

            // --- NEW CHECK: Door on occupied sandwich (joint) ---
            if (!restrictionFailed && elementType === OBJECT_TYPES.DOOR) {
              const elementsInSameSegment = joint.elements.filter(el => {
                const elSlotIndex = Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );
                return elSlotIndex === targetSlotIndex;
              });

              if (elementsInSameSegment.length > 0) {
                const errorMessage = t("toasts.doorPlacementOnOccupiedSandwichError");
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
              }
            }
            // --------------------------------------------

            if (!restrictionFailed && ELECTRICAL_ELEMENT_TYPES.includes(elementType)) {

              const existingElectricalElementsInSameSegmentAndSide = joint.elements.filter(el => {
                if (!ELECTRICAL_ELEMENT_TYPES.includes(el.type)) {
                  return false;
                }

                if (el.placementSide === undefined || el.placementSide === null || el.placementSide === 0) {
                  el.placementSide = 1;
                }

                if (el.placementSide !== newElement.placementSide) {
                  return false;
                }
                const elSlotIndex = Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );
                return elSlotIndex === targetSlotIndex;
              });

              if (existingElectricalElementsInSameSegmentAndSide.length >= ELEMENT_GROUP_LIMITS.ELECTRICAL) {
                const errorMessage = `Максимум ${ELEMENT_GROUP_LIMITS.ELECTRICAL} електричних елементів на сегмент стыка з кожної сторони`;
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
              }
            }

            if (!restrictionFailed && SANITARY_ELEMENT_TYPES.includes(elementType)) {

              const existingElementsInSameSegment = joint.elements.filter(el => {
                if (!SANITARY_ELEMENT_TYPES.includes(el.type) || el.placementSide !== newElement.placementSide) {
                  return false;
                }
                const elSlotIndex = Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );
                return elSlotIndex === targetSlotIndex;
              });

              if (existingElementsInSameSegment.length >= 1) {
                const errorMessage = "На цьому сегменті стыка вже є санитарний елемент";
                setTimeout(() => toast.warn(errorMessage), 0);
                restrictionFailed = true;
              }

              if (!restrictionFailed) {
                const allJoints = tempObjects.filter(o => o.type === OBJECT_TYPES.DOCKING_JOINT);
                const allModules = tempObjects.filter(o => o.type === OBJECT_TYPES.MODULE);

                if (isSanitaryJoint(joint, allModules)) {
                  const isEdgeSegment = isEdgeSegmentInSanitaryJoint(
                    joint,
                    targetSlotIndex,
                    newElement.placementSide,
                    allJoints
                  );

                  if (isEdgeSegment) {
                    const errorMessage = "На крайні сэндвичі санитарного стыка нельзя ставить санитарні елементи";
                    setTimeout(() => toast.warn(errorMessage), 0);
                    restrictionFailed = true;
                  }
                }
              }
            } else {
              const elementLimit = ELEMENT_LIMITS_PER_WALL_SEGMENT[elementType];
              if (elementLimit && !ELECTRICAL_ELEMENT_TYPES.includes(elementType) && !CLIMATE_ELEMENT_TYPES.includes(elementType)) {
                const existingElementsOfSameType = joint.elements.filter(el =>
                  el.type === elementType
                );

                if (existingElementsOfSameType.length >= elementLimit) {
                  const errorMessage = elementLimit === 1
                    ? t("toasts.elementLimitOnePerWallError")
                    : `Максимум ${elementLimit} елементів типу "${elementType}" на стык`;
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                }
              } else if (CLIMATE_ELEMENT_TYPES.includes(elementType)) {
                const existingClimateElementsInSegment = joint.elements.filter(el => {
                  if (el.type !== elementType) return false;
                  const elSlotIndex = Math.max(
                    0,
                    Math.min(
                      numSlots - 1,
                      Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                    ),
                  );
                  return elSlotIndex === targetSlotIndex && el.placementSide === newElement.placementSide;
                });

                if (existingClimateElementsInSegment.length >= 1) {
                  setTimeout(() => toast.warn("На одному сегменті стику вже є кондиціонер"), 0);
                  restrictionFailed = true;
                }
              }
            }

            if (!restrictionFailed && SANITARY_ELEMENT_TYPES.includes(elementType)) {
              const allJoints = tempObjects.filter(o => o.type === OBJECT_TYPES.DOCKING_JOINT);
              const allModules = tempObjects.filter(o => o.type === OBJECT_TYPES.MODULE);

              const cornersMap = getAllSanitaryModuleCorners(allModules, allJoints);

              updateCornersElements(cornersMap, allJoints, allModules);

              const canPlace = canPlaceSanitaryElementInSandwich(
                cornersMap,
                joint.id,
                newElement.placementSide,
                elementType,
                allJoints,
                allModules
              );

              if (!canPlace) {
                setTimeout(() => toast.warn(t("toasts.sanitaryElementCornerConflictError")), 0);
                restrictionFailed = true;
              }
            }

            const isLargeOpeningElement = (elType) =>
              elType === OBJECT_TYPES.DOOR || elType === OBJECT_TYPES.WINDOW || elType === OBJECT_TYPES.PANORAMIC_WINDOW;

            if (!restrictionFailed && isLargeOpeningElement(elementType)) {
              const existingLargeOpenings = joint.elements.filter(el => isLargeOpeningElement(el.type));

              if (existingLargeOpenings.length > 0) {
                const existingTypes = existingLargeOpenings.map(el => el.type);
                const hasDoor = existingTypes.includes(OBJECT_TYPES.DOOR) || elementType === OBJECT_TYPES.DOOR;
                const hasWindow = existingTypes.includes(OBJECT_TYPES.WINDOW) ||
                  existingTypes.includes(OBJECT_TYPES.PANORAMIC_WINDOW) ||
                  elementType === OBJECT_TYPES.WINDOW ||
                  elementType === OBJECT_TYPES.PANORAMIC_WINDOW;

                if (hasDoor && hasWindow) {
                  setTimeout(() => toast.warn("Не можна розміщувати двері та вікна на одному стыку"), 0);
                  restrictionFailed = true;
                }
              }

              // Проверка: только одно окно на стык
              if (elementType === OBJECT_TYPES.WINDOW || elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
                const existingWindows = joint.elements.filter((el) =>
                  el.type === OBJECT_TYPES.WINDOW || el.type === OBJECT_TYPES.PANORAMIC_WINDOW
                );

                if (existingWindows.length > 0) {
                  const errorMessage = "На одному стыку може бути тільки одне вікно";
                  setTimeout(() => toast.warn(errorMessage), 0);
                  restrictionFailed = true;
                }
              }

              // Проверка для панорамных окон: оба слота должны быть свободны от больших элементов
              if (elementType === OBJECT_TYPES.PANORAMIC_WINDOW) {
                const slotsToCheck = [targetSlotIndex, targetSlotIndex + 1];

                for (const slotIndex of slotsToCheck) {
                  const existingLargeOpeningsInSlot = joint.elements.filter(el => {
                    if (!isLargeOpeningElement(el.type)) return false;

                    const elSlotIndex = Math.max(
                      0,
                      Math.min(
                        numSlots - 1,
                        Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                      ),
                    );

                    // Проверяем, занимает ли элемент этот слот
                    if (el.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                      const elSegmentIndex = el.segmentIndex ?? Math.max(
                        0,
                        Math.min(
                          numSlots - 2,
                          Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                        ),
                      );
                      return slotIndex === elSegmentIndex || slotIndex === elSegmentIndex + 1;
                    } else {
                      return slotIndex === elSlotIndex;
                    }
                  });

                  if (existingLargeOpeningsInSlot.length > 0) {
                    const errorMessage = "Не можна розміщувати панорамне вікно: один з необхідних слотів вже зайнятий дверима або вікном";
                    setTimeout(() => toast.warn(errorMessage), 0);
                    restrictionFailed = true;
                    break;
                  }
                }
              }
            }

            if (!restrictionFailed) {
              if (isLargeOpeningElement(elementType)) {
                joint.elements.push(newElement);

              } else {
                joint.elements.push(newElement);

                const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));
                const targetSlotIndex = Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((newElement.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );

                const smallElementsInSameSegmentAndSide = joint.elements.filter(el => {
                  if (isLargeOpeningElement(el.type)) return false;

                  if (el.placementSide === undefined || el.placementSide === null || el.placementSide === 0) {
                    el.placementSide = 1;
                  }

                  if (el.placementSide !== newElement.placementSide) {
                    return false;
                  }

                  const elSlotIndex = Math.max(
                    0,
                    Math.min(
                      numSlots - 1,
                      Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                    ),
                  );
                  return elSlotIndex === targetSlotIndex;
                });

                if (smallElementsInSameSegmentAndSide.length > 1) {
                  const segmentStart = targetSlotIndex / numSlots;
                  const segmentEnd = (targetSlotIndex + 1) / numSlots;
                  const segmentWidth = segmentEnd - segmentStart;

                  smallElementsInSameSegmentAndSide.forEach((element, index) => {
                    const spacingFactor = segmentWidth / (smallElementsInSameSegmentAndSide.length + 1);
                    element.positionOnSegment = segmentStart + spacingFactor * (index + 1);
                    element.segmentIndex = targetSlotIndex;
                  });
                } else {
                  if (positionOnSegment === undefined) {
                    const segmentStart = targetSlotIndex / numSlots;
                    const segmentEnd = (targetSlotIndex + 1) / numSlots;
                    newElement.positionOnSegment = (segmentStart + segmentEnd) / 2;
                  }
                  newElement.segmentIndex = targetSlotIndex;
                }

                if (elementType === OBJECT_TYPES.INTERNAL_AIR_CONDITIONER) {
                  const externalElementId = generateId("el_");

                  newElement.linkedElementId = externalElementId;

                  const targetModuleId = newElement.placementSide === 1 ? joint.moduleA_id : joint.moduleB_id;
                  const targetModule = tempObjects.find(m => m.id === targetModuleId && m.type === OBJECT_TYPES.MODULE);

                  let externalWallFound = false;

                  if (targetModule && targetModule.wallSegments) {
                    const externalWalls = [];

                    for (const [segmentKey, segment] of Object.entries(targetModule.wallSegments)) {
                      if (!segment.dockedToJointId) {
                        externalWalls.push({ segmentKey, segment });
                      }
                    }

                    if (externalWalls.length > 0) {
                      const targetWall = externalWalls[0];

                      const [coords, orientation] = targetWall.segmentKey.split('_');
                      const [wallX, wallY] = coords.split(',').map(Number);

                      let externalSide;
                      if (orientation === 'h') {
                        externalSide = (wallY <= targetModule.y) ? -1 : 1;
                      } else {
                        externalSide = (wallX <= targetModule.x) ? -1 : 1;
                      }

                      const externalElement = {
                        id: externalElementId,
                        type: OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER,
                        positionOnSegment: 0.5,
                        ...defaultObjectSizes[OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER],
                        placementSide: externalSide,
                        linkedElementId: newElementId,
                      };

                      targetWall.segment.elements.push(externalElement);
                      externalWallFound = true;

                    }
                  }

                  if (!externalWallFound) {
                    delete newElement.linkedElementId;
                  }
                }
              }

              elementAdded = true;
            }
          }
        }
      }

      console.log('🔍 [ADD_ELEMENT_TO_WALL] Финальная проверка:', {
        elementAdded,
        restrictionFailed,
        elementType,
        wallSegmentId
      });

      if (restrictionFailed) {
        console.log('🚫 [ADD_ELEMENT_TO_WALL] БЛОКИРОВКА из-за restrictionFailed');
        return state;
      }

      if (elementAdded) {
        nextStateObjects = tempObjects;
        newSelectedObjectId = newElementId;
      } else {
        return state;
      }
      break;
    }

    case "DELETE_SELECTED_OBJECT": {
      const selectedId = state.selectedObjectId;
      console.log("🗑️ [DELETE_SELECTED_OBJECT] Starting deletion, selectedId:", selectedId);

      if (!selectedId) {
        console.warn("🗑️ [DELETE_SELECTED_OBJECT] No selectedId, returning state");
        return state;
      }

      // Получаем информацию об объекте для удаления
      const deletedObjectInfo = getObjectById(
        selectedId,
        currentFloorId,
        state.projectObjects,
      );

      console.log("🗑️ [DELETE_SELECTED_OBJECT] deletedObjectInfo:", {
        id: deletedObjectInfo?.id,
        type: deletedObjectInfo?.type,
        parentJointId: deletedObjectInfo?.parentJointId,
        parentWallSegment: deletedObjectInfo?.parentWallSegment,
        parentWallId: deletedObjectInfo?.parentWallId,
      });

      if (!deletedObjectInfo) {
        console.warn("🗑️ [DELETE_SELECTED_OBJECT] Object not found:", selectedId);
        return state;
      }

      let objectsOnFloor = JSON.parse(JSON.stringify(currentFloorObjects));
      let wasMutated = false;

      // Если элемент на стыке, обрабатываем его отдельно
      if (deletedObjectInfo.parentJointId) {
        console.log("🗑️ [DELETE_SELECTED_OBJECT] Element is on joint, parentJointId:", deletedObjectInfo.parentJointId);

        const joint = objectsOnFloor.find(
          (o) => o.id === deletedObjectInfo.parentJointId && o.type === OBJECT_TYPES.DOCKING_JOINT,
        );

        console.log("🗑️ [DELETE_SELECTED_OBJECT] Found joint:", {
          found: !!joint,
          jointId: joint?.id,
          elementsCount: joint?.elements?.length,
        });

        if (joint && joint.elements && Array.isArray(joint.elements)) {
          console.log("🗑️ [DELETE_SELECTED_OBJECT] Joint elements before deletion:", joint.elements.map(el => ({ id: el?.id, type: el?.type })));

          const elIndex = joint.elements.findIndex((el) => el && el.id === selectedId);
          console.log("🗑️ [DELETE_SELECTED_OBJECT] Element index in joint.elements:", elIndex);

          if (elIndex > -1) {
            const deletedElement = joint.elements[elIndex];
            console.log("🗑️ [DELETE_SELECTED_OBJECT] Deleting element from joint:", {
              elementId: deletedElement?.id,
              elementType: deletedElement?.type,
              index: elIndex,
            });

            joint.elements.splice(elIndex, 1);
            wasMutated = true;

            console.log("🗑️ [DELETE_SELECTED_OBJECT] Joint elements after deletion:", joint.elements.map(el => ({ id: el?.id, type: el?.type })));
            console.log("🗑️ [DELETE_SELECTED_OBJECT] wasMutated set to:", wasMutated);

            // Reposition small elements if needed
            const isLargeOpeningElement = (elType) =>
              elType === OBJECT_TYPES.DOOR ||
              elType === OBJECT_TYPES.WINDOW ||
              elType === OBJECT_TYPES.PANORAMIC_WINDOW;

            if (!isLargeOpeningElement(deletedElement.type)) {
              const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));
              const targetSlotIndex = deletedElement.segmentIndex !== null && deletedElement.segmentIndex !== undefined
                ? deletedElement.segmentIndex
                : Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((deletedElement.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );

              const smallElementsInSameSlot = joint.elements.filter(el => {
                if (isLargeOpeningElement(el.type) || el.type === OBJECT_TYPES.WINDOW_BLINDS) return false;
                const elSlotIndex = el.segmentIndex !== null && el.segmentIndex !== undefined
                  ? el.segmentIndex
                  : Math.max(
                    0,
                    Math.min(
                      numSlots - 1,
                      Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                    ),
                  );
                return elSlotIndex === targetSlotIndex && el.placementSide === deletedElement.placementSide;
              });

              if (smallElementsInSameSlot.length > 0) {
                const spacingFactor = 1 / (smallElementsInSameSlot.length + 1);
                smallElementsInSameSlot.forEach((el, index) => {
                  el.positionOnSegment = (targetSlotIndex + spacingFactor * (index + 1)) / numSlots;
                });
              }
            }

            if (wasMutated) {
              console.log("✅ [DELETE_SELECTED_OBJECT] Successfully deleted element from joint, updating state");
              nextStateObjects = objectsOnFloor;
              newSelectedObjectId = null;
              // Не вызываем пересчет, так как удаление элемента не меняет геометрию стыка
              // Пересчет может восстановить элементы из старого состояния
              needsRecalculation = false;
              needsValidation = false;
              break;
            } else {
              console.warn("⚠️ [DELETE_SELECTED_OBJECT] Element not found in joint.elements");
            }
          } else {
            console.warn("⚠️ [DELETE_SELECTED_OBJECT] Joint found but element not found in joint.elements");
          }
        } else {
          console.warn("⚠️ [DELETE_SELECTED_OBJECT] Joint not found or has no elements array:", {
            jointFound: !!joint,
            hasElements: !!joint?.elements,
            isArray: Array.isArray(joint?.elements),
          });
        }
      } else {
        console.log("🗑️ [DELETE_SELECTED_OBJECT] Element is not on joint, using general deletion logic");
      }

      let roomDeletionHandled = false;

      const findAndRemoveMutating = (obj) => {
        if (wasMutated) return;

        // Специальная обработка для безкаркасных стен и перегородок
        if (obj.type === OBJECT_TYPES.FRAMELESS_WALL || obj.type === OBJECT_TYPES.FRAMELESS_PARTITION) {
          if (obj.segments && Array.isArray(obj.segments)) {
            for (const segment of obj.segments) {
              if (segment.elements && Array.isArray(segment.elements)) {
                const elIndex = segment.elements.findIndex(el => el && el.id === selectedId);
                if (elIndex > -1) {
                  const deletedElement = segment.elements[elIndex];
                  segment.elements.splice(elIndex, 1);
                  wasMutated = true;

                  // Special handling for window blinds on panoramic windows: remove copy from adjacent segment
                  if (deletedElement.type === OBJECT_TYPES.WINDOW_BLINDS) {
                    // Check if there's a panoramic window on this segment at the same position
                    const panoramicWindow = segment.elements.find(
                      el => el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                        !el.isAdjacentCopy &&
                        Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01
                    );

                    if (panoramicWindow) {
                      // Find adjacent segment and remove the copy of blinds
                      const segmentIndex = obj.segments.findIndex(seg => seg.id === segment.id);
                      if (segmentIndex !== -1 && segmentIndex < obj.segments.length - 1) {
                        const adjacentSegment = obj.segments[segmentIndex + 1];
                        if (adjacentSegment && adjacentSegment.elements && Array.isArray(adjacentSegment.elements)) {
                          const positionToMatch = deletedElement.positionOnSegment || 0.5;
                          const tolerance = 0.01;

                          // Remove blinds from adjacent segment
                          const adjacentBlindsToRemove = adjacentSegment.elements.filter(
                            el => el &&
                              el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                              Math.abs((el.positionOnSegment || 0.5) - positionToMatch) < tolerance
                          );
                          adjacentBlindsToRemove.forEach(blinds => {
                            const index = adjacentSegment.elements.indexOf(blinds);
                            if (index > -1) {
                              adjacentSegment.elements.splice(index, 1);
                              wasMutated = true;

                              // Для перегородок также удаляем из wall.elements
                              if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && obj.elements && Array.isArray(obj.elements)) {
                                const wallElementIndex = obj.elements.findIndex(el => el && el.id === blinds.id);
                                if (wallElementIndex > -1) {
                                  obj.elements.splice(wallElementIndex, 1);
                                }
                              }

                              if (process.env.NODE_ENV !== "production") {
                                console.log('🗑️ [WINDOW BLINDS] Removed copy from adjacent frameless segment:', {
                                  originalSegmentId: segment.id,
                                  adjacentSegmentId: adjacentSegment.id,
                                  blindsId: blinds.id
                                });
                              }
                            }
                          });
                        }
                      }
                    }
                  }

                  // Special handling for panoramic windows: remove adjacent copy or original
                  if (deletedElement.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                    const segmentIndex = obj.segments.findIndex(seg => seg.id === segment.id);

                    if (deletedElement.isAdjacentCopy) {
                      // If deleting the copy, find and remove the original from the previous segment
                      if (segmentIndex > 0) {
                        const prevSegment = obj.segments[segmentIndex - 1];
                        if (prevSegment && prevSegment.elements && Array.isArray(prevSegment.elements)) {
                          // Remove panoramic window with matching positionOnSegment (the original)
                          const originalIndex = prevSegment.elements.findIndex(
                            el => el &&
                              el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                              !el.isAdjacentCopy &&
                              Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01
                          );
                          if (originalIndex > -1) {
                            prevSegment.elements.splice(originalIndex, 1);
                            console.log('🗑️ [PANORAMIC WINDOW] Removed original from frameless segment when deleting copy:', {
                              copySegmentId: segment.id,
                              originalSegmentId: prevSegment.id,
                              deletedElementId: deletedElement.id
                            });
                          }
                        }
                      }
                    } else {
                      // If deleting the original, find and remove the copy from the adjacent segment
                      if (segmentIndex !== -1 && segmentIndex < obj.segments.length - 1) {
                        const nextSegment = obj.segments[segmentIndex + 1];
                        if (nextSegment && nextSegment.elements && Array.isArray(nextSegment.elements)) {
                          // Remove panoramic window with matching positionOnSegment or marked as adjacent copy
                          const adjacentCopyIndex = nextSegment.elements.findIndex(
                            el => el &&
                              el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                              (el.isAdjacentCopy || Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                          );
                          if (adjacentCopyIndex > -1) {
                            nextSegment.elements.splice(adjacentCopyIndex, 1);
                            console.log('🗑️ [PANORAMIC WINDOW] Removed adjacent copy from frameless segment:', {
                              originalSegmentId: segment.id,
                              adjacentSegmentId: nextSegment.id,
                              deletedElementId: deletedElement.id
                            });
                          }
                        }
                      }
                    }
                  }

                  // Special handling for gates: remove adjacent copies or original (gates occupy 3 segments)
                  if (deletedElement.type === OBJECT_TYPES.GATE) {
                    const segmentIndex = obj.segments.findIndex(seg => seg.id === segment.id);

                    if (deletedElement.isAdjacentCopy) {
                      // If deleting a copy, find and remove the original and all other copies
                      // Find the original (it should be on one of the previous segments)
                      for (let i = Math.max(0, segmentIndex - 2); i <= segmentIndex; i++) {
                        const checkSegment = obj.segments[i];
                        if (checkSegment && checkSegment.elements && Array.isArray(checkSegment.elements)) {
                          const originalIndex = checkSegment.elements.findIndex(
                            el => el &&
                              el.type === OBJECT_TYPES.GATE &&
                              !el.isAdjacentCopy &&
                              Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01
                          );
                          if (originalIndex > -1) {
                            // Found the original, remove it and all other copies
                            checkSegment.elements.splice(originalIndex, 1);
                            const originalSegmentIndex = i;

                            // Remove all other copies
                            for (let j = 0; j < obj.segments.length; j++) {
                              if (j === originalSegmentIndex) continue; // Skip the original segment
                              const seg = obj.segments[j];
                              if (seg && seg.elements && Array.isArray(seg.elements)) {
                                const copyIndex = seg.elements.findIndex(
                                  el => el &&
                                    el.type === OBJECT_TYPES.GATE &&
                                    el.isAdjacentCopy &&
                                    Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01
                                );
                                if (copyIndex > -1) {
                                  seg.elements.splice(copyIndex, 1);
                                }
                              }
                            }

                            // Для перегородок также удаляем из wall.elements
                            if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && Array.isArray(obj.elements)) {
                              obj.elements = obj.elements.filter(
                                el => !(el.type === OBJECT_TYPES.GATE &&
                                  Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                              );
                            }

                            console.log('🗑️ [GATE] Removed original and all copies from frameless segments when deleting copy:', {
                              copySegmentId: segment.id,
                              originalSegmentId: checkSegment.id,
                              deletedElementId: deletedElement.id
                            });
                            break;
                          }
                        }
                      }
                    } else {
                      // If deleting the original, find and remove both copies from the adjacent segments
                      if (segmentIndex !== -1 && segmentIndex < obj.segments.length - 2) {
                        // Remove first copy from next segment
                        const nextSegment1 = obj.segments[segmentIndex + 1];
                        if (nextSegment1 && nextSegment1.elements && Array.isArray(nextSegment1.elements)) {
                          const adjacentCopy1Index = nextSegment1.elements.findIndex(
                            el => el &&
                              el.type === OBJECT_TYPES.GATE &&
                              (el.isAdjacentCopy || Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                          );
                          if (adjacentCopy1Index > -1) {
                            nextSegment1.elements.splice(adjacentCopy1Index, 1);
                          }
                        }

                        // Remove second copy from second next segment
                        const nextSegment2 = obj.segments[segmentIndex + 2];
                        if (nextSegment2 && nextSegment2.elements && Array.isArray(nextSegment2.elements)) {
                          const adjacentCopy2Index = nextSegment2.elements.findIndex(
                            el => el &&
                              el.type === OBJECT_TYPES.GATE &&
                              (el.isAdjacentCopy || Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                          );
                          if (adjacentCopy2Index > -1) {
                            nextSegment2.elements.splice(adjacentCopy2Index, 1);
                          }
                        }

                        // Для перегородок также удаляем из wall.elements
                        if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && Array.isArray(obj.elements)) {
                          obj.elements = obj.elements.filter(
                            el => !(el.type === OBJECT_TYPES.GATE &&
                              Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                          );
                        }

                        console.log('🗑️ [GATE] Removed adjacent copies from frameless segments:', {
                          originalSegmentId: segment.id,
                          adjacentSegment1Id: nextSegment1?.id,
                          adjacentSegment2Id: nextSegment2?.id,
                          deletedElementId: deletedElement.id
                        });
                      }
                    }
                  }

                  // Пересчитываем позиции оставшихся элементов (как в модульном режиме)
                  const isLargeOpeningElement = (elType) =>
                    elType === OBJECT_TYPES.DOOR ||
                    elType === OBJECT_TYPES.GATE ||
                    elType === OBJECT_TYPES.WINDOW ||
                    elType === OBJECT_TYPES.PANORAMIC_WINDOW;

                  if (!isLargeOpeningElement(deletedElement.type)) {
                    const smallElements = segment.elements.filter(el => !isLargeOpeningElement(el.type) && el.type !== OBJECT_TYPES.WINDOW_BLINDS);

                    // Группируем элементы по стороне размещения
                    const elementsBySide = {};
                    smallElements.forEach(el => {
                      let rawSide = el.placementSide;
                      // Robust normalization: handle 0, 'undefined', 'null' as default '1'
                      if (rawSide === undefined || rawSide === null || rawSide === 'undefined' || rawSide === 'null' || rawSide === 0 || rawSide === '0') {
                        rawSide = '1';
                      }
                      const side = String(rawSide);

                      if (!elementsBySide[side]) elementsBySide[side] = [];
                      elementsBySide[side].push(el);
                    });

                    // Перераспределяем каждую группу отдельно
                    Object.values(elementsBySide).forEach(group => {
                      if (group.length > 0) {
                        group.sort((a, b) => (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5));
                        const spacingFactor = 1 / (group.length + 1);
                        group.forEach((el, index) => {
                          el.positionOnSegment = spacingFactor * (index + 1);
                        });
                      }
                    });
                  }

                  // Также удаляем из wall.elements для перегородок
                  if (obj.type === OBJECT_TYPES.FRAMELESS_PARTITION && obj.elements) {
                    const wallElIndex = obj.elements.findIndex(el => el && el.id === selectedId);
                    if (wallElIndex > -1) {
                      obj.elements.splice(wallElIndex, 1);
                    }
                  }

                  return;
                }
              }
            }
          }
        }

        // Special handling for joints (стыки) - fallback if not handled above
        if (obj.type === OBJECT_TYPES.DOCKING_JOINT && obj.elements && Array.isArray(obj.elements)) {
          console.log("🗑️ [DELETE_SELECTED_OBJECT] Checking joint in findAndRemoveMutating:", {
            jointId: obj.id,
            elementsCount: obj.elements.length,
          });

          const elIndex = obj.elements.findIndex(el => el && el.id === selectedId);
          if (elIndex > -1) {
            console.log("🗑️ [DELETE_SELECTED_OBJECT] Found element in joint via findAndRemoveMutating:", {
              jointId: obj.id,
              elementIndex: elIndex,
            });

            const deletedElement = obj.elements[elIndex];
            obj.elements.splice(elIndex, 1);
            wasMutated = true;

            // Reposition small elements if needed
            const isLargeOpeningElement = (elType) =>
              elType === OBJECT_TYPES.DOOR ||
              elType === OBJECT_TYPES.WINDOW ||
              elType === OBJECT_TYPES.PANORAMIC_WINDOW;

            if (!isLargeOpeningElement(deletedElement.type)) {
              const numSlots = Math.max(1, Math.round(obj.width / GRID_CELL_SIZE_M));
              const targetSlotIndex = deletedElement.segmentIndex !== null && deletedElement.segmentIndex !== undefined
                ? deletedElement.segmentIndex
                : Math.max(
                  0,
                  Math.min(
                    numSlots - 1,
                    Math.floor((deletedElement.positionOnSegment ?? 0.5) * numSlots),
                  ),
                );

              const smallElementsInSameSlot = obj.elements.filter(el => {
                if (isLargeOpeningElement(el.type)) return false;
                const elSlotIndex = el.segmentIndex !== null && el.segmentIndex !== undefined
                  ? el.segmentIndex
                  : Math.max(
                    0,
                    Math.min(
                      numSlots - 1,
                      Math.floor((el.positionOnSegment ?? 0.5) * numSlots),
                    ),
                  );
                return elSlotIndex === targetSlotIndex && el.placementSide === deletedElement.placementSide;
              });

              if (smallElementsInSameSlot.length > 0) {
                const spacingFactor = 1 / (smallElementsInSameSlot.length + 1);
                smallElementsInSameSlot.forEach((el, index) => {
                  el.positionOnSegment = (targetSlotIndex + spacingFactor * (index + 1)) / numSlots;
                });
              }
            }

            return;
          }
        }

        // Special handling for wallSegments and internalPartitions
        const collectionsToCheck = [
          { name: 'wallSegments', data: obj.wallSegments },
          { name: 'internalPartitions', data: obj.internalPartitions }
        ];

        for (const { name: collName, data: collection } of collectionsToCheck) {
          if (collection && typeof collection === 'object') {
            for (const segmentKey in collection) {
              const segment = collection[segmentKey];

              // 1. Check if we are deleting the segment itself
              if (segment.id === selectedId) {
                if (segment.elements && segment.elements.length > 0) {
                  setTimeout(() => toast.warn(t("toasts.deleteWallWithElementsError")), 0);
                  return;
                }
                delete collection[segmentKey];
                wasMutated = true;
                return;
              }

              // 2. Check if we are deleting an element inside the segment
              if (segment && segment.elements && Array.isArray(segment.elements)) {
                const elIndex = segment.elements.findIndex(el => el && el.id === selectedId);
                if (elIndex > -1) {
                  const deletedElement = segment.elements[elIndex];
                  segment.elements.splice(elIndex, 1);
                  wasMutated = true;

                  // Special handling for panoramic windows: remove adjacent copy or original
                  if (deletedElement.type === OBJECT_TYPES.PANORAMIC_WINDOW) {
                    const [coords, orientation] = segmentKey.split('_');
                    const [cellX, cellY] = coords.split(',').map(Number);

                    let segmentToCheck;
                    if (deletedElement.isAdjacentCopy) {
                      // If deleting the copy, find and remove the original from the previous segment
                      if (orientation === 'h') {
                        segmentToCheck = obj[collName][`${cellX - 1},${cellY}_h`];
                      } else {
                        segmentToCheck = obj[collName][`${cellX},${cellY - 1}_v`];
                      }

                      if (segmentToCheck && segmentToCheck.elements && Array.isArray(segmentToCheck.elements)) {
                        // Remove panoramic window with matching positionOnSegment (the original)
                        const originalIndex = segmentToCheck.elements.findIndex(
                          el => el &&
                            el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                            !el.isAdjacentCopy &&
                            Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01
                        );
                        if (originalIndex > -1) {
                          const originalWindow = segmentToCheck.elements[originalIndex];
                          segmentToCheck.elements.splice(originalIndex, 1);
                          console.log('🗑️ [PANORAMIC WINDOW] Removed original from segment when deleting copy:', {
                            copySegment: segmentKey,
                            originalSegment: orientation === 'h' ? `${cellX - 1},${cellY}_h` : `${cellX},${cellY - 1}_v`,
                            deletedElementId: deletedElement.id
                          });

                          // Remove window blinds from both segments when deleting panoramic window
                          const positionToMatch = originalWindow.positionOnSegment || 0.5;
                          const tolerance = 0.01;

                          // Remove blinds from original segment
                          if (segmentToCheck.elements && Array.isArray(segmentToCheck.elements)) {
                            const blindsToRemove = segmentToCheck.elements.filter(
                              el => el &&
                                el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                                Math.abs((el.positionOnSegment || 0.5) - positionToMatch) < tolerance
                            );
                            blindsToRemove.forEach(blinds => {
                              const index = segmentToCheck.elements.indexOf(blinds);
                              if (index > -1) {
                                segmentToCheck.elements.splice(index, 1);
                                console.log('🗑️ [PANORAMIC WINDOW] Removed window blinds from original segment:', {
                                  segment: orientation === 'h' ? `${cellX - 1},${cellY}_h` : `${cellX},${cellY - 1}_v`,
                                  blindsId: blinds.id
                                });
                              }
                            });
                          }

                          // Remove blinds from copy segment
                          if (segment.elements && Array.isArray(segment.elements)) {
                            const blindsToRemove = segment.elements.filter(
                              el => el &&
                                el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                                Math.abs((el.positionOnSegment || 0.5) - positionToMatch) < tolerance
                            );
                            blindsToRemove.forEach(blinds => {
                              const index = segment.elements.indexOf(blinds);
                              if (index > -1) {
                                segment.elements.splice(index, 1);
                                console.log('🗑️ [PANORAMIC WINDOW] Removed window blinds from copy segment:', {
                                  segment: segmentKey,
                                  blindsId: blinds.id
                                });
                              }
                            });
                          }
                        }
                      }
                    } else {
                      // If deleting the original, find and remove the copy from the adjacent segment
                      let adjacentSegmentKey;
                      if (orientation === 'h') {
                        adjacentSegmentKey = `${cellX + 1},${cellY}_h`;
                      } else {
                        adjacentSegmentKey = `${cellX},${cellY + 1}_v`;
                      }

                      const adjacentSegment = obj[collName][adjacentSegmentKey];
                      if (adjacentSegment && adjacentSegment.elements && Array.isArray(adjacentSegment.elements)) {
                        // Remove panoramic window with matching positionOnSegment or marked as adjacent copy
                        const adjacentCopyIndex = adjacentSegment.elements.findIndex(
                          el => el &&
                            el.type === OBJECT_TYPES.PANORAMIC_WINDOW &&
                            (el.isAdjacentCopy || Math.abs((el.positionOnSegment || 0.5) - (deletedElement.positionOnSegment || 0.5)) < 0.01)
                        );
                        if (adjacentCopyIndex > -1) {
                          adjacentSegment.elements.splice(adjacentCopyIndex, 1);
                          console.log('🗑️ [PANORAMIC WINDOW] Removed adjacent copy from segment:', {
                            originalSegment: segmentKey,
                            adjacentSegment: adjacentSegmentKey,
                            deletedElementId: deletedElement.id
                          });
                        }

                        // Remove window blinds from both segments when deleting panoramic window
                        const positionToMatch = deletedElement.positionOnSegment || 0.5;
                        const tolerance = 0.01;

                        // Remove blinds from original segment
                        if (segment.elements && Array.isArray(segment.elements)) {
                          const blindsToRemove = segment.elements.filter(
                            el => el &&
                              el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                              Math.abs((el.positionOnSegment || 0.5) - positionToMatch) < tolerance
                          );
                          blindsToRemove.forEach(blinds => {
                            const index = segment.elements.indexOf(blinds);
                            if (index > -1) {
                              segment.elements.splice(index, 1);
                              console.log('🗑️ [PANORAMIC WINDOW] Removed window blinds from original segment:', {
                                segment: segmentKey,
                                blindsId: blinds.id
                              });
                            }
                          });
                        }

                        // Remove blinds from adjacent segment
                        const adjacentBlindsToRemove = adjacentSegment.elements.filter(
                          el => el &&
                            el.type === OBJECT_TYPES.WINDOW_BLINDS &&
                            Math.abs((el.positionOnSegment || 0.5) - positionToMatch) < tolerance
                        );
                        adjacentBlindsToRemove.forEach(blinds => {
                          const index = adjacentSegment.elements.indexOf(blinds);
                          if (index > -1) {
                            adjacentSegment.elements.splice(index, 1);
                            console.log('🗑️ [PANORAMIC WINDOW] Removed window blinds from adjacent segment:', {
                              segment: adjacentSegmentKey,
                              blindsId: blinds.id
                            });
                          }
                        });
                      }
                    }
                  }

                  // Handle linked elements (air conditioners)
                  if (deletedElement.linkedElementId && (deletedElement.type === OBJECT_TYPES.INTERNAL_AIR_CONDITIONER || deletedElement.type === OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER)) {
                    // This part is complex. We'll just delete this one and rely on a full rescan if needed, or handle it via a separate action.
                  }

                  // Reposition small elements if needed
                  const isLargeOpeningElement = (elType) =>
                    elType === OBJECT_TYPES.DOOR ||
                    elType === OBJECT_TYPES.WINDOW ||
                    elType === OBJECT_TYPES.PANORAMIC_WINDOW;

                  if (!isLargeOpeningElement(deletedElement.type)) {
                    const smallElements = segment.elements.filter(el => !isLargeOpeningElement(el.type) && el.type !== OBJECT_TYPES.WINDOW_BLINDS);

                    // Группируем элементы по стороне размещения
                    const elementsBySide = {};
                    smallElements.forEach(el => {
                      let rawSide = el.placementSide;
                      // Robust normalization: handle 0, 'undefined', 'null' as default '1'
                      if (rawSide === undefined || rawSide === null || rawSide === 'undefined' || rawSide === 'null' || rawSide === 0 || rawSide === '0') {
                        rawSide = '1';
                      }
                      const side = String(rawSide);

                      if (!elementsBySide[side]) elementsBySide[side] = [];
                      elementsBySide[side].push(el);
                    });

                    // Перераспределяем каждую группу отдельно
                    Object.values(elementsBySide).forEach(group => {
                      if (group.length > 0) {
                        group.sort((a, b) => (a.positionOnSegment || 0.5) - (b.positionOnSegment || 0.5));
                        const spacingFactor = 1 / (group.length + 1);
                        group.forEach((el, index) => {
                          el.positionOnSegment = spacingFactor * (index + 1);
                        });
                      }
                    });
                  }

                  return;
                }
              }
            }
          }
        }

        const collections = ['elements', 'freeElements', 'perimeterWalls', 'internalWalls', 'internalPartitions', 'segments'];

        for (const collName of collections) {
          if (Array.isArray(obj[collName])) {
            const elIndex = obj[collName].findIndex(el => el && el.id === selectedId);
            if (elIndex > -1) {
              const deletedElement = obj[collName][elIndex];
              obj[collName].splice(elIndex, 1);
              wasMutated = true;
              if (deletedElement.linkedElementId && (deletedElement.type === OBJECT_TYPES.INTERNAL_AIR_CONDITIONER || deletedElement.type === OBJECT_TYPES.EXTERNAL_AIR_CONDITIONER)) {
                // This part is complex. We'll just delete this one and rely on a full rescan if needed, or handle it via a separate action.
              }
              return;
            }
            obj[collName].forEach(findAndRemoveMutating);
          } else if (typeof obj[collName] === 'object' && obj[collName] !== null) {
            for (const key in obj[collName]) {
              findAndRemoveMutating(obj[collName][key]);
            }
          }
        }
      };

      // Проверка для безкаркасных стен: нельзя удалять стены из завершенных комнат
      // Проверяем, является ли удаляемый объект частью замкнутой комнаты
      let wallIdToCheck = null;

      if (deletedObjectInfo.type === OBJECT_TYPES.FRAMELESS_WALL) {
        wallIdToCheck = selectedId;
      } else if (deletedObjectInfo.type === 'frameless_wall_segment' && deletedObjectInfo.parentWallId) {
        // Если это сегмент стены, проверяем родительскую стену
        wallIdToCheck = deletedObjectInfo.parentWallId;
      }

      if (wallIdToCheck) {
        const allWalls = objectsOnFloor.filter(obj => obj.type === OBJECT_TYPES.FRAMELESS_WALL);

        // Находим ВСЕ замкнутые комнаты ДО удаления
        const allClosedLoops = findAllClosedLoops(allWalls);

        // Фильтруем контуры, оставляя только минимальные (не содержащие другие внутри)
        // Это реальные отдельные комнаты, а не комбинации
        const minimalRooms = allClosedLoops.filter(loop => {
          // Проверяем, не содержит ли этот контур все стены другого контура
          return !allClosedLoops.some(otherLoop => {
            if (otherLoop === loop) return false;
            const otherLoopWallIds = new Set(otherLoop.map(w => w.id));
            const loopWallIds = new Set(loop.map(w => w.id));
            // Если все стены другого контура входят в этот контур - это не минимальная комната
            return [...otherLoopWallIds].every(id => loopWallIds.has(id));
          });
        });

        console.log(`🔍 [DELETE_SELECTED_OBJECT] Все минимальные комнаты (${minimalRooms.length}):`,
          minimalRooms.map((room, index) => ({
            roomIndex: index,
            wallsCount: room.length,
            wallIds: room.map(w => w.id),
            containsWall: room.some(w => w.id === wallIdToCheck),
            walls: room.map(w => ({
              id: w.id,
              start: w.startPoint,
              end: w.endPoint
            }))
          }))
        );

        // Находим все минимальные комнаты, которые содержат удаляемую стену
        const roomsWithWall = minimalRooms.filter(room => {
          const roomWallIdsSet = new Set(room.map(w => w.id));
          return roomWallIdsSet.has(wallIdToCheck);
        });

        console.log(`🔍 [DELETE_SELECTED_OBJECT] Найдено комнат с удаляемой стеной ${wallIdToCheck}: ${roomsWithWall.length}`, {
          roomsInfo: roomsWithWall.map((room, index) => ({
            roomIndex: index,
            wallsCount: room.length,
            wallIds: room.map(w => w.id)
          }))
        });

        if (roomsWithWall.length > 0) {
          // Выбираем самую маленькую минимальную комнату, которая содержит эту стену
          // Это гарантирует, что мы удалим именно ту комнату, к которой относится стена
          const deletedRoomMinimal = roomsWithWall.reduce((smallest, current) =>
            current.length < smallest.length ? current : smallest
          );

          const deletedRoomWallIds = new Set(deletedRoomMinimal.map(wall => wall.id));

          console.log(`🗑️ [DELETE_SELECTED_OBJECT] Удаляемая комната содержит стены:`, Array.from(deletedRoomWallIds));

          // Находим соседние комнаты, которые используют общие стены с удаляемой комнатой
          const deletedRoomWallIdsArray = Array.from(deletedRoomWallIds);

          console.log(`🔍 [DELETE_SELECTED_OBJECT] Всего минимальных комнат: ${minimalRooms.length}`);
          console.log(`🔍 [DELETE_SELECTED_OBJECT] Удаляемая комната: ${deletedRoomMinimal.length} стен`, {
            wallIds: Array.from(deletedRoomWallIds),
            walls: deletedRoomMinimal.map(w => ({
              id: w.id,
              start: w.startPoint,
              end: w.endPoint
            }))
          });

          // Показываем все комнаты с их стенами для визуализации
          console.log(`📐 [DELETE_SELECTED_OBJECT] Структура всех комнат:`);
          minimalRooms.forEach((room, idx) => {
            const isDeletedRoom = room.length === deletedRoomMinimal.length &&
              room.every(w => deletedRoomWallIds.has(w.id)) &&
              Array.from(deletedRoomWallIds).every(id => room.some(w => w.id === id));
            console.log(`  Комната ${idx} (${isDeletedRoom ? 'УДАЛЯЕМАЯ' : 'другая'}):`, {
              wallsCount: room.length,
              wallIds: room.map(w => w.id)
            });
          });

          const adjacentRooms = minimalRooms.filter((room, roomIndex) => {
            // Пропускаем саму удаляемую комнату (сравниваем по ID стен)
            const roomWallIds = new Set(room.map(w => w.id));
            const isSameRoom = room.length === deletedRoomMinimal.length &&
              room.every(w => deletedRoomWallIds.has(w.id)) &&
              deletedRoomWallIdsArray.every(id => roomWallIds.has(id));

            if (isSameRoom) {
              console.log(`⏭️ [DELETE_SELECTED_OBJECT] Пропускаем комнату ${roomIndex} - это удаляемая комната`);
              return false;
            }

            // Проверяем, есть ли общие стены
            const sharedWalls = deletedRoomWallIdsArray.filter(id => roomWallIds.has(id));

            // Комната считается соседней только если она делит РОВНО ОДНУ стену с удаляемой комнатой
            // Если делит больше одной - это не настоящий сосед, а часть большей структуры
            const isAdjacent = sharedWalls.length === 1;

            if (sharedWalls.length > 0) {
              if (isAdjacent) {
                console.log(`✅ [DELETE_SELECTED_OBJECT] Соседняя комната ${roomIndex} (делит 1 стену):`, {
                  wallsCount: room.length,
                  roomWallIds: Array.from(roomWallIds),
                  sharedWallsWithDeletedRoom: sharedWalls,
                  sharedWallsCount: sharedWalls.length
                });
              } else {
                console.log(`⚠️ [DELETE_SELECTED_OBJECT] Комната ${roomIndex} НЕ соседняя (делит ${sharedWalls.length} стен, не 1):`, {
                  wallsCount: room.length,
                  roomWallIds: Array.from(roomWallIds),
                  sharedWallsWithDeletedRoom: sharedWalls,
                  sharedWallsCount: sharedWalls.length,
                  reason: 'Комната делит больше одной стены - это не настоящий сосед'
                });
              }
            } else {
              console.log(`❌ [DELETE_SELECTED_OBJECT] Комната ${roomIndex} не соседняя (нет общих стен):`, {
                wallsCount: room.length,
                roomWallIds: Array.from(roomWallIds),
                deletedRoomWallIds: deletedRoomWallIdsArray
              });
            }

            return isAdjacent;
          });

          // Определяем общие стены (которые используются соседними комнатами)
          // Но проверяем: если комната делит больше половины стен удаляемой комнаты,
          // возможно, удаляемая комната является частью большей комнаты
          const sharedWallIds = new Set();
          const wallToRoomsMap = new Map(); // Для детального логирования: какая стена в каких комнатах

          adjacentRooms.forEach((adjacentRoom, roomIndex) => {
            const adjacentRoomWallIds = new Set(adjacentRoom.map(w => w.id));
            const sharedWithThisRoom = deletedRoomWallIdsArray.filter(id => adjacentRoomWallIds.has(id));
            const sharedRatio = sharedWithThisRoom.length / deletedRoomWallIds.size;

            console.log(`🔍 [DELETE_SELECTED_OBJECT] Комната ${roomIndex} делит ${sharedWithThisRoom.length} из ${deletedRoomWallIds.size} стен (${(sharedRatio * 100).toFixed(0)}%)`);

            // Если комната делит больше половины стен, это может быть родительская комната
            if (sharedRatio > 0.5) {
              console.log(`⚠️ [DELETE_SELECTED_OBJECT] Комната ${roomIndex} делит больше половины стен - возможно, удаляемая комната является частью этой комнаты`);
            }

            sharedWithThisRoom.forEach(wallId => {
              sharedWallIds.add(wallId);
              if (!wallToRoomsMap.has(wallId)) {
                wallToRoomsMap.set(wallId, []);
              }
              wallToRoomsMap.get(wallId).push(roomIndex);
            });
          });

          console.log(`🔍 [DELETE_SELECTED_OBJECT] Детальный анализ общих стен:`, {
            totalAdjacentRooms: adjacentRooms.length,
            sharedWallsDetails: Array.from(sharedWallIds).map(wallId => ({
              wallId,
              usedInRooms: wallToRoomsMap.get(wallId) || [],
              isShared: true
            })),
            nonSharedWalls: Array.from(deletedRoomWallIds).filter(id => !sharedWallIds.has(id)).map(wallId => ({
              wallId,
              isShared: false
            }))
          });

          // Удаляем только те стены удаляемой комнаты, которые НЕ являются общими
          // Общие стены всегда сохраняются для других комнат
          // НО: если все стены общие, но каждая используется только в одной соседней комнате,
          // и одна из комнат делит больше половины стен, возможно, удаляемая комната является частью этой комнаты
          let wallsToDelete = [...deletedRoomWallIds].filter(wallId => !sharedWallIds.has(wallId));

          // Если все стены общие, проверяем, не является ли удаляемая комната частью большей комнаты
          if (wallsToDelete.length === 0 && sharedWallIds.size === deletedRoomWallIds.size) {
            console.log(`⚠️ [DELETE_SELECTED_OBJECT] Все стены общие, проверяем, не является ли удаляемая комната частью большей комнаты`);

            // Находим комнату, которая делит больше всего стен с удаляемой
            let maxSharedWalls = 0;
            let roomWithMaxShared = null;
            adjacentRooms.forEach((adjacentRoom, roomIndex) => {
              const adjacentRoomWallIds = new Set(adjacentRoom.map(w => w.id));
              const sharedWithThisRoom = deletedRoomWallIdsArray.filter(id => adjacentRoomWallIds.has(id));
              if (sharedWithThisRoom.length > maxSharedWalls) {
                maxSharedWalls = sharedWithThisRoom.length;
                roomWithMaxShared = { roomIndex, sharedWalls: sharedWithThisRoom };
              }
            });

            if (roomWithMaxShared && maxSharedWalls > deletedRoomWallIds.size / 2) {
              console.log(`⚠️ [DELETE_SELECTED_OBJECT] Комната ${roomWithMaxShared.roomIndex} делит ${maxSharedWalls} из ${deletedRoomWallIds.size} стен - удаляемая комната может быть частью этой комнаты`);
              // Если комната делит больше половины стен, удаляем только те стены, которые не используются в других комнатах
              // Но так как все стены общие, удаляем только те, которые используются только в этой комнате
              wallsToDelete = deletedRoomWallIdsArray.filter(wallId => {
                const usedInRooms = wallToRoomsMap.get(wallId) || [];
                // Удаляем только те стены, которые используются только в комнате с максимальным количеством общих стен
                return usedInRooms.length === 1 && usedInRooms[0] === roomWithMaxShared.roomIndex;
              });
              console.log(`🔍 [DELETE_SELECTED_OBJECT] После проверки: удаляем ${wallsToDelete.length} стен, которые используются только в комнате ${roomWithMaxShared.roomIndex}`);
            }
          }

          // Детальный анализ каждой стены: общая или нет
          const wallAnalysis = Array.from(deletedRoomWallIds).map(wallId => {
            const isShared = sharedWallIds.has(wallId);
            const usedInRooms = wallToRoomsMap.get(wallId) || [];
            const adjacentRoomsDetails = usedInRooms.map(roomIndex => ({
              roomIndex,
              roomWalls: adjacentRooms[roomIndex].map(w => w.id),
              roomWallsCount: adjacentRooms[roomIndex].length
            }));
            return {
              wallId,
              isShared,
              usedInAdjacentRooms: usedInRooms.length,
              adjacentRoomIndices: usedInRooms,
              adjacentRoomsDetails: adjacentRoomsDetails
            };
          });

          console.log(`🔍 [DELETE_SELECTED_OBJECT] Детальный анализ каждой стены:`, wallAnalysis);

          // Еще более детальный вывод для каждой стены отдельно
          console.log(`📊 [DELETE_SELECTED_OBJECT] Постенная детализация:`);
          Array.from(deletedRoomWallIds).forEach(wallId => {
            const isShared = sharedWallIds.has(wallId);
            const usedInRooms = wallToRoomsMap.get(wallId) || [];
            console.log(`  🧱 Стена ${wallId}:`, {
              isShared,
              usedInRoomsCount: usedInRooms.length,
              usedInRoomIndices: usedInRooms,
              usedInRoomsWalls: usedInRooms.map(roomIndex => ({
                roomIndex,
                allWallsInRoom: adjacentRooms[roomIndex].map(w => w.id)
              }))
            });
          });

          // Сводная таблица: какая комната делит какие стены
          console.log(`📋 [DELETE_SELECTED_OBJECT] Сводная таблица общих стен:`);
          adjacentRooms.forEach((adjacentRoom, roomIndex) => {
            const adjacentRoomWallIds = new Set(adjacentRoom.map(w => w.id));
            const sharedWithThisRoom = Array.from(deletedRoomWallIds).filter(wallId =>
              adjacentRoomWallIds.has(wallId)
            );
            console.log(`  Комната ${roomIndex} (${adjacentRoom.length} стен): делит стены ${sharedWithThisRoom.join(', ')} (${sharedWithThisRoom.length} стен)`);
          });
          console.log(`🔍 [DELETE_SELECTED_OBJECT] Сводка анализа стен:`, {
            totalWallsInRoom: deletedRoomWallIds.size,
            sharedWalls: Array.from(sharedWallIds),
            sharedWallsCount: sharedWallIds.size,
            nonSharedWalls: wallsToDelete,
            nonSharedWallsCount: wallsToDelete.length,
            allWallsAreShared: wallsToDelete.length === 0 && sharedWallIds.size === deletedRoomWallIds.size
          });

          const wallsToDeleteSet = new Set(wallsToDelete);

          console.log(`🔗 [DELETE_SELECTED_OBJECT] Найдено соседних комнат: ${adjacentRooms.length}`);
          console.log(`🔗 [DELETE_SELECTED_OBJECT] Общие стены (будут сохранены):`, Array.from(sharedWallIds));
          console.log(`🗑️ [DELETE_SELECTED_OBJECT] Стены для удаления:`, Array.from(wallsToDeleteSet));
          console.log(`📋 [DELETE_SELECTED_OBJECT] Детализация:`, {
            всеСтеныКомнаты: Array.from(deletedRoomWallIds),
            общиеСтены: Array.from(sharedWallIds),
            необщиеСтены: wallsToDelete,
            соседниеКомнаты: adjacentRooms.map(room => ({
              wallsCount: room.length,
              wallIds: room.map(w => w.id)
            }))
          });

          // Удаляем только необщие стены (общие стены всегда сохраняются)
          if (wallsToDelete.length > 0) {
            objectsOnFloor = objectsOnFloor.filter(obj =>
              !(obj.type === OBJECT_TYPES.FRAMELESS_WALL && wallsToDeleteSet.has(obj.id))
            );
            wasMutated = true;
            setTimeout(() => {
              toast.success('Кімнату видалено.');
            }, 0);

            console.log(`✅ [DELETE_SELECTED_OBJECT] Удалена комната: ${wallsToDelete.length} необщих стен удалено, ${sharedWallIds.size} общих стен сохранено.`);

            // Комната обработана - удалили необщие стены
            // Помечаем, что комната обработана, чтобы сегмент не удалялся
            roomDeletionHandled = true;
            nextStateObjects = objectsOnFloor;
            newSelectedObjectId = null;
            needsRecalculation = true;
            needsValidation = true;
          } else if (sharedWallIds.size === deletedRoomWallIds.size && deletedRoomWallIds.size > 0) {
            // Если все стены общие, не удаляем стены - они нужны другим комнатам
            // Также не удаляем сегмент - комната не может быть удалена без удаления общих стен
            console.log(`⚠️ [DELETE_SELECTED_OBJECT] Все стены общие (${sharedWallIds.size} стен), не удаляем стены и сегмент - они нужны другим комнатам. Комната не может быть удалена.`);

            setTimeout(() => {
              toast.error('Неможливо видалити кімнату: всі стіни є спільними з іншими кімнатами.');
            }, 0);

            // Не удаляем ничего - ни стены, ни сегмент
            // Возвращаем state с снятым выделением
            return {
              ...state,
              selectedObjectId: null
            };
          }
        } else {
          // Комната не найдена - стена не входит в замкнутый контур
          // Продолжаем обычное удаление объекта
          console.log(`⚠️ [DELETE_SELECTED_OBJECT] Комната с удаляемой стеной ${wallIdToCheck} не найдена, продолжаем обычное удаление`);
        }
      }

      // Пропускаем обычное удаление, если комната уже была обработана
      if (!roomDeletionHandled) {
        let topLevelIndex = objectsOnFloor.findIndex(obj => obj.id === selectedId);
        if (topLevelIndex > -1) {
          objectsOnFloor.splice(topLevelIndex, 1);
          wasMutated = true;
        } else {
          objectsOnFloor.forEach(findAndRemoveMutating);
        }
      }

      console.log("🗑️ [DELETE_SELECTED_OBJECT] Final check - wasMutated:", wasMutated);

      if (wasMutated) {
        console.log("✅ [DELETE_SELECTED_OBJECT] Element deleted successfully, updating state");
        nextStateObjects = objectsOnFloor;
        newSelectedObjectId = null;
        needsRecalculation = true;
        needsValidation = true;
      } else {
        console.warn("⚠️ [DELETE_SELECTED_OBJECT] Element was not deleted, wasMutated is false, returning state");
        return state;
      }
      break;
    }

    case "SET_SELECTED_OBJECT_ID": {
      const objectId = action.payload;
      if (!objectId) {
        return { ...state, selectedObjectId: null };
      }

      const selectedObject = getObjectById(
        objectId,
        state.currentFloorId,
        state.projectObjects,
      );

      if (
        selectedObject &&
        selectedObject.type === OBJECT_TYPES.WALL_SEGMENT &&
        selectedObject.parentModuleId
      ) {
        if (selectedObject.jointId) {
          return { ...state, selectedObjectId: selectedObject.jointId };
        }
      }

      return { ...state, selectedObjectId: objectId };
    }

    default:
      return state;
  }
  }

  let finalState = { ...state };
  if (needsRecalculation) {
    console.log("🔄 [FINAL STATE] Calling recalculateStateAfterGeometryChange");
    nextStateObjects = recalculateStateAfterGeometryChange(
      nextStateObjects,
      currentFloorObjects,
      t,
    );
  } else {
    console.log("⏭️ [FINAL STATE] Skipping recalculateStateAfterGeometryChange (needsRecalculation = false)");
  }
  if (needsValidation) {
    nextStateObjects = validateProjectInteractions(
      nextStateObjects,
      currentFloorId,
      { ...state.projectObjects, [currentFloorId]: nextStateObjects },
      state.floors,
      t,
    );
  }

  // Логика перегородок отключена в безкаркасном режиме

  finalState.projectObjects = {
    ...state.projectObjects,
    [currentFloorId]: nextStateObjects,
  };
  finalState.selectedObjectId = newSelectedObjectId;
  finalState.framelessElementsUnlocked = framelessElementsUnlocked;

  return finalState;
};
